import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ChevronUp, CheckCircle2 } from 'lucide-react';
import type { ReconJob, ReconRow, ReconTxnPair } from '@/shared/api';
import { reconApi } from '@/shared/api';
import { StatusPill } from './StatusPill';

/**
 * Module-level cache of computed matching results, keyed by job id.
 *
 * The heavy matching engine (buildMatchBands, run per company) is expensive, so
 * we run it ONCE per job and reuse the result for the whole session. Because
 * this Map lives at module scope it:
 *   • SURVIVES navigation (list → job → back → another job) — the module stays
 *     loaded, so re-opening a job reads the cached result with NO recompute.
 *   • CLEARS on a full page refresh — a hard reload starts a fresh JS context,
 *     so the matching runs once more on the first open after refresh.
 *   • Recomputes for a NEW recon — the entry is keyed by job id AND a fingerprint
 *     of the job's data, so a re-run (which changes the data) misses the cache
 *     and recomputes for that job only.
 * Net effect: matching runs only on a new recon or a full refresh; plain
 * navigation never recomputes.
 */
const jobBandsCache = new Map<string, { fingerprint: string; bands: Map<string, MatchBand[]> }>();

/**
 * "Waiting for entries" window for a DP-only credit/debit.
 * In the equity recon the DP statement usually arrives before the matching PF
 * entries (PF comes a day or two later). So a DP row with no PF counterpart is
 * not necessarily unmatched yet — it may just be waiting for the PF side.
 *
 * Window = 2 calendar days from the DP date, extended to 4 if a Saturday or
 * Sunday falls within those first 2 days. Measured up to the reference date
 * (the latest DP date present in the uploaded statement).
 *   within window → "Waiting for entries";  window passed → "unmatched".
 */
let DP_REFERENCE_DATE = '';   // latest DP txn date in the current dataset (set per render)

function isWithinDpWaitingWindow(dpDateIso: string | null | undefined): boolean {
  if (!dpDateIso || !DP_REFERENCE_DATE) return false;
  const iso = String(dpDateIso).slice(0, 10);
  const dp = new Date(iso + 'T00:00:00Z');
  const ref = new Date(DP_REFERENCE_DATE + 'T00:00:00Z');
  if (isNaN(dp.getTime()) || isNaN(ref.getTime())) return false;

  let windowDays = 2;
  for (let d = 1; d <= 2; d++) {
    const day = new Date(dp);
    day.setUTCDate(day.getUTCDate() + d);
    const dow = day.getUTCDay(); // 0 = Sun, 6 = Sat
    if (dow === 0 || dow === 6) { windowDays = 4; break; }
  }
  const deadline = new Date(dp);
  deadline.setUTCDate(deadline.getUTCDate() + windowDays);
  return ref <= deadline;
}

/**
 * Transactions_v1 reconciliation view — NET BALANCE mode.
 *
 * Business rule (set by the user):
 *   For each ISIN, compute portfolio net = sum of signed qty across
 *   buys/sells/bonus/split/demerger, DP net = sum(credit − debit).
 *   Show portfolio_net, dp_net, difference per ISIN. No txn pairing,
 *   no AI classification.
 *
 * Layout:
 *   1. Top tiles — totals across all ISINs (net balances + status counts)
 *   2. Per-ISIN row table — columns: name/ISIN, portfolio_net, dp_net,
 *      difference, status. Mismatches sorted first.
 *   3. Drill-down on expand — every PF and DP transaction for the ISIN,
 *      sorted chronologically. The reviewer can see the underlying
 *      movements without re-uploading the file.
 */

type StatusFilter = 'all' | 'problems' | 'mismatch' | 'slb' | 'pf_only' | 'dp_only' | 'match';

export function TxnReconView({ data }: {
  data: ReconJob & { rows: ReconRow[]; txn_pairs?: ReconTxnPair[] };
}) {
  const navigate = useNavigate();
  const [holdingReconPending, setHoldingReconPending] = useState(false);
  // Pre-group audit-trail entries by row_id so each ISIN expand is O(1).
  const pairsByRow = useMemo(() => {
    const m = new Map<string, ReconTxnPair[]>();
    for (const p of data.txn_pairs ?? []) {
      const list = m.get(p.row_id) ?? [];
      list.push(p);
      m.set(p.row_id, list);
    }
    return m;
  }, [data.txn_pairs]);

  // Reference date for the "Waiting for entries" window = the latest DP txn date
  // present in the whole dataset. Stored in a module var so the deeply-nested
  // band renderers can read it without prop-drilling.
  useMemo(() => {
    let latest = '';
    for (const p of data.txn_pairs ?? []) {
      const d = (p.dp_txn_date ?? '').slice(0, 10);
      if (d && d > latest) latest = d;
    }
    DP_REFERENCE_DATE = latest;
    return latest;
  }, [data.txn_pairs]);

  // Companies are merged by display name (same name, different ISIN → ONE
  // company). EXCEPTION: rows with no usable name — blank, the literal "NULL",
  // or the literal "NaN" — are NOT merged; each such ISIN is shown as its OWN
  // company. Blank/NULL rows are labelled null1, null2, …; NaN rows are
  // labelled NaN1, NaN2, … — each sequence counted independently, in order of
  // first appearance.
  const unnamedKind = (row: ReconRow): 'null' | 'nan' | null => {
    const n = (row.display_name ?? '').trim();
    if (n === '' || n.toUpperCase() === 'NULL') return 'null';
    if (n.toUpperCase() === 'NAN') return 'nan';
    return null;
  };
  const isUnnamed = (row: ReconRow) => unnamedKind(row) !== null;
  const unnamedLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    let nNull = 0;
    let nNaN = 0;
    for (const row of data.rows) {
      const kind = unnamedKind(row);
      if (kind && !m.has(row.canonical_isin)) {
        if (kind === 'null') { nNull += 1; m.set(row.canonical_isin, `null${nNull}`); }
        else                 { nNaN  += 1; m.set(row.canonical_isin, `NaN${nNaN}`);   }
      }
    }
    return m;
  }, [data.rows]);
  // Grouping key: unnamed rows key on their own ISIN so they never merge.
  const groupKey = (row: ReconRow) => {
    const kind = unnamedKind(row);
    return kind ? `__${kind}__${row.canonical_isin}` : (row.display_name ?? row.canonical_isin);
  };
  // Display label: unnamed rows show null1…/NaN1…, named rows show their name.
  const displayLabel = (row: ReconRow) =>
    isUnnamed(row)
      ? (unnamedLabelMap.get(row.canonical_isin) ?? (unnamedKind(row) === 'nan' ? 'NaN' : 'null'))
      : (row.display_name ?? row.canonical_isin);

  // Effective status per row: run the SAME band logic the row uses, compute
  // the visible difference, and override the stale backend status when the
  // visible reconciliation is actually balanced. This makes counts, filters,
  // sort, and badges all consistent with what the user sees in each row.
  //
  // PERF: buildMatchBands is expensive and was previously run here (for the
  // status) AND again inside every IsinRow (for rendering) — i.e. twice per
  // company on every open. We now compute it ONCE per company here, cache the
  // resulting bands keyed by the company group, and hand the same bands to the
  // row so it never recomputes. Behaviour is identical — same engine, same
  // inputs — just no duplicate work.
  const bandsByGroupKey = useMemo(() => {
    // A fingerprint that changes whenever the job's underlying data changes
    // (a new recon or a re-run), so those recompute while plain navigation
    // re-opening the same unchanged job hits the cache.
    const fingerprint = `${data.rows.length}:${data.txn_pairs?.length ?? 0}:${(data as any).updated_at ?? ''}`;
    const cached = jobBandsCache.get(data.id);
    if (cached && cached.fingerprint === fingerprint) {
      return cached.bands; // reuse — NO matching recompute on navigation
    }

    const cache = new Map<string, MatchBand[]>();
    const byName = new Map<string, ReconRow[]>();
    for (const row of data.rows) {
      const key = groupKey(row);
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key)!.push(row);
    }
    for (const [key, groupRows] of byName) {
      const unifiedIsin = groupRows[0].canonical_isin;
      // Match CompanyRow's unifiedPairs EXACTLY: unify canonical_isin for
      // matching, but keep each pair's original ISIN in _displayIsin so the
      // per-row ISIN column still renders the true ISIN in combined windows.
      const combinedPairs = groupRows.flatMap(r =>
        (pairsByRow.get(r.id) ?? []).map(p => ({ ...p, _displayIsin: p.canonical_isin, canonical_isin: unifiedIsin }))
      );
      try {
        cache.set(key, buildMatchBands(combinedPairs).bands);
      } catch {
        cache.set(key, []);
      }
    }
    jobBandsCache.set(data.id, { fingerprint, bands: cache });
    return cache;
  }, [data.id, data.rows, data.txn_pairs, pairsByRow]);

  const effectiveStatusByRow = useMemo(() => {
    const m = new Map<string, string>();
    // Group rows by company name so combined-ISIN companies get ONE status
    // computed across all their pairs (matching treats them as one security).
    const byName = new Map<string, ReconRow[]>();
    for (const row of data.rows) {
      const key = groupKey(row);
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key)!.push(row);
    }
    for (const [key, groupRows] of byName) {
      let effective: string;
      try {
        const bands = bandsByGroupKey.get(key) ?? [];
        const bd = buildVisibleBreakdown(bands);
        const diff = (bd.portfolio.buys_qty + bd.portfolio.sells_qty)
                   - (bd.dp.credits_qty - bd.dp.debits_qty);
        const anyBad = groupRows.some(r => r.status === 'QTY_MISMATCH');
        effective = Math.abs(diff) < 0.001
          ? 'MATCH'
          : (anyBad ? 'QTY_MISMATCH' : groupRows[0].status);
      } catch {
        effective = groupRows[0].status;
      }
      // Apply the same window-level status to every row of the company
      for (const r of groupRows) m.set(r.id, effective);
    }
    return m;
  }, [data.rows, bandsByGroupKey]);

  const effStatus = (row: ReconRow) => effectiveStatusByRow.get(row.id) ?? row.status;

  // Auto-heal stale DB rows: whenever the frontend matching engine (buildMatchBands)
  // resolves an ISIN to MATCH but the DB still says QTY_MISMATCH (stale engine run),
  // fire a silent background recompute to update portfolio_qty / dp_qty / status.
  // This means Bajaj Finance-style cases fix themselves on first page open — no manual
  // rerun needed. Uses a ref to avoid firing multiple times per mount.
  const queryClient = useQueryClient();
  const recomputedRows = useRef(new Set<string>());
  useEffect(() => {
    const staleRows = data.rows.filter(row => {
      if (recomputedRows.current.has(row.id)) return false;
      const effective = effectiveStatusByRow.get(row.id);
      // Only fire when the frontend resolved QTY_MISMATCH → MATCH and the pairs
      // are loaded (no pairs yet = nothing to compute from).
      return effective === 'MATCH' && row.status === 'QTY_MISMATCH' &&
             (pairsByRow.get(row.id) ?? []).length > 0;
    });
    if (staleRows.length === 0) return;
    for (const row of staleRows) {
      recomputedRows.current.add(row.id);
      reconApi.recomputeRow(data.id, row.id)
        .then(result => {
          if (result.changed) {
            // Invalidate the job query so the header PF/DP/diff numbers refresh
            queryClient.invalidateQueries({ queryKey: ['recon-job', data.id] });
          }
        })
        .catch(() => { /* silent — stale display is acceptable if the call fails */ });
    }
  }, [effectiveStatusByRow, data.rows, pairsByRow, data.id, queryClient]);

  // Auto-backfill dp_client_id: when it is null but the job has a DP file,
  // silently call the backfill endpoint which re-reads the CSV/Excel header
  // and patches the summary in-place. Fires once per mount via a ref guard.
  const dpClientIdBackfilled = useRef(false);
  useEffect(() => {
    if (dpClientIdBackfilled.current) return;
    if (data.summary?.dp_client_id != null) return; // already populated
    dpClientIdBackfilled.current = true;
    reconApi.backfillDpClientId(data.id)
      .then(result => {
        if (result.dp_client_id) {
          queryClient.invalidateQueries({ queryKey: ['recon-job', data.id] });
        }
      })
      .catch(() => { /* silent */ });
  }, [data.id, data.summary?.dp_client_id, queryClient]);

  // Mismatches first, then alphabetical. Defensive sort in case the API
  // returns rows in a different order (shouldn't happen, but cheap).
  const sortedRows = useMemo(() => {
    const order: Record<string, number> = {
      QTY_MISMATCH:      0,
      SLB_OUTSTANDING:   1,
      ONLY_IN_PORTFOLIO: 2,
      ONLY_IN_DP:        3,
      MATCH:             4,
    };
    return [...data.rows].sort((a, b) => {
      const so = (order[effectiveStatusByRow.get(a.id) ?? a.status] ?? 99)
               - (order[effectiveStatusByRow.get(b.id) ?? b.status] ?? 99);
      return so !== 0 ? so : a.canonical_isin.localeCompare(b.canonical_isin);
    });
  }, [data.rows, effectiveStatusByRow]);

  const txn = data.summary?.txn_summary;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const filteredRows = sortedRows.filter(r => {
    const s = effStatus(r);
    if (statusFilter === 'all')      return true;
    if (statusFilter === 'problems') return s !== 'MATCH' && s !== 'SLB_OUTSTANDING';
    if (statusFilter === 'mismatch') return s === 'QTY_MISMATCH';
    if (statusFilter === 'slb')      return s === 'SLB_OUTSTANDING';
    if (statusFilter === 'pf_only')  return s === 'ONLY_IN_PORTFOLIO';
    if (statusFilter === 'dp_only')  return s === 'ONLY_IN_DP';
    if (statusFilter === 'match')    return s === 'MATCH';
    return true;
  });

  // Counts are per WINDOW (company), so a multi-ISIN company counts once.
  const companyStatus = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of sortedRows) {
      const key = groupKey(r);
      if (!seen.has(key)) seen.set(key, effStatus(r));
    }
    return [...seen.values()];
  }, [sortedRows, effectiveStatusByRow]);

  const counts = {
    all:      companyStatus.length,
    problems: companyStatus.filter(s => s !== 'MATCH' && s !== 'SLB_OUTSTANDING').length,
    mismatch: companyStatus.filter(s => s === 'QTY_MISMATCH').length,
    slb:      companyStatus.filter(s => s === 'SLB_OUTSTANDING').length,
    pf_only:  companyStatus.filter(s => s === 'ONLY_IN_PORTFOLIO').length,
    dp_only:  companyStatus.filter(s => s === 'ONLY_IN_DP').length,
    match:    companyStatus.filter(s => s === 'MATCH').length,
  };

  // Save the live, company-merged stats back to the job so the LIST can show
  // the exact same Match rate and Mismatches the user sees here — without the
  // list re-running the engine. Mismatches excludes SLB and unresolvable
  // (qty-mismatch + only-in-PF + only-in-DP), matching the "Problems" count.
  const savedLiveStats = useRef(false);
  useEffect(() => {
    if (savedLiveStats.current) return;
    if (!data.id || counts.all === 0) return;
    savedLiveStats.current = true;
    const mismatches = counts.mismatch + counts.pf_only + counts.dp_only;
    const matchRatePct = counts.all > 0 ? (counts.match / counts.all) * 100 : 0;
    reconApi.saveLiveStats(data.id, { matched: counts.match, mismatches, total: counts.all, matchRatePct })
      .then(() => { queryClient.invalidateQueries({ queryKey: ['recon-jobs'] }); })
      .catch(() => { /* non-critical: list falls back to stored values */ });
  }, [data.id, counts.all, counts.match, counts.mismatch, counts.pf_only, counts.dp_only, queryClient]);

  return (
    <>
      {/* Summary tiles — totals across all ISINs */}
      {txn && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 items-stretch">
          <Tile label="PF Client ID"  value={data.summary?.pf_client_id ?? '—'} tone="neutral" />
          <Tile label="DP Client ID"  value={data.summary?.dp_client_id ?? '—'} tone="neutral" />
          <Tile label="ISINs"         value={counts.all}                         tone="neutral" />
          <Tile label="Match rate"    value={`${(counts.all > 0 ? (counts.match / counts.all) * 100 : 0).toFixed(1)}%`} tone={(counts.all > 0 ? (counts.match / counts.all) * 100 : 0) >= 95 ? 'match' : (counts.all > 0 ? (counts.match / counts.all) * 100 : 0) >= 70 ? 'warn' : 'mismatch'} />
          <Tile label="Matched"       value={counts.match}                       tone="match" />
          <Tile label="Qty mismatch"  value={counts.mismatch}                    tone="warn" />
          <Tile label="Only in PF"    value={counts.pf_only}                     tone="warn" />
          <Tile label="Only in DP"    value={counts.dp_only}                     tone="mismatch" />
        </div>
      )}

      {/* Net total tiles — the headline numbers per the business rule */}
      {txn && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <NetTile label="Portfolio net holding"  value={txn.total_portfolio_net ?? 0} tone="neutral" />
          <NetTile label="DP net holding"         value={txn.total_dp_net        ?? 0} tone="neutral" />
          <NetTile label="Total difference (PF − DP)" value={txn.total_difference ?? 0}
            tone={Math.abs(txn.total_difference ?? 0) < 0.001 ? 'match' : 'mismatch'} />
        </div>
      )}

      {/* Quick filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Chip active={statusFilter === 'all'}      onClick={() => setStatusFilter('all')}      label="All ISINs"  count={counts.all} />
        <Chip active={statusFilter === 'problems'} onClick={() => setStatusFilter('problems')} label="Problems"   count={counts.problems} />
        <Chip active={statusFilter === 'mismatch'} onClick={() => setStatusFilter('mismatch')} label="Qty mismatch" count={counts.mismatch} />
        {counts.slb > 0 && (
          <Chip active={statusFilter === 'slb'}    onClick={() => setStatusFilter('slb')}      label="SLB outstanding" count={counts.slb} />
        )}
        <Chip active={statusFilter === 'pf_only'}  onClick={() => setStatusFilter('pf_only')}  label="Only in PF" count={counts.pf_only} />
        <Chip active={statusFilter === 'dp_only'}  onClick={() => setStatusFilter('dp_only')}  label="Only in DP" count={counts.dp_only} />
        <Chip active={statusFilter === 'match'}    onClick={() => setStatusFilter('match')}    label="Matched"    count={counts.match} />
        {/* ── Trans Recon / Holding Recon view switcher ── */}
        {/* Two separate buttons. 'Trans Recon' is active by default (this IS the
            transaction recon view). Clicking it is a no-op re-affirm of the current
            view. 'Holding Recon' runs the holding recon and navigates to its page.
            Later, Holding Recon's target page will link back here via Trans Recon. */}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition"
            style={{
              // Active (pressed) on the recon detail page → red pill.
              // (Inactive white state is used when this button appears on other
              //  pages, e.g. the Holding Recon page, to switch back here.)
              background: '#e11d48',
              color: '#000',
              border: '1px solid #e11d48',
            }}
            onClick={() => navigate(`/recon/${data.id}`)}
            title="Transaction Recon (current view)"
          >
            Trans Recon
          </button>
          <button
            type="button"
            disabled={holdingReconPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition disabled:opacity-60"
            style={{
              background: 'color-mix(in srgb, var(--axis-maroon) 10%, transparent)',
              color: 'var(--axis-maroon)',
              border: '1px solid color-mix(in srgb, var(--axis-maroon) 35%, transparent)',
            }}
            onClick={async () => {
              setHoldingReconPending(true);
              try {
                const result = await reconApi.triggerHoldingRecon(data.id);
                navigate(`/holding-recon/${result.holding_job_id}`);
              } catch (err: any) {
                const msg = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
                alert(`Holding Recon failed: ${msg}`);
              } finally {
                setHoldingReconPending(false);
              }
            }}
            title="Run Holding Recon from uploaded holding files"
          >
            {holdingReconPending ? 'Running…' : 'Holding Recon'}
          </button>
        </div>
      </div>

      {/* Per-ISIN table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-axis-cloud-200 flex items-center justify-between">
          <h2 className="font-semibold text-axis-ink">
            Showing {new Set(filteredRows.map(r => groupKey(r))).size} window{new Set(filteredRows.map(r => groupKey(r))).size === 1 ? '' : 's'}
          </h2>
          <span className="text-xs text-axis-ash">Net-balance reconciliation</span>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-axis-ash border-b border-axis-cloud-200 bg-axis-cloud-50">
          <div className="col-span-4">Stock / ISIN</div>
          <div className="col-span-2 text-right">Portfolio net</div>
          <div className="col-span-2 text-right">DP net</div>
          <div className="col-span-2 text-right">Difference</div>
          <div className="col-span-2 text-right">Status</div>
        </div>

        <div className="divide-y divide-axis-cloud-200">
          {filteredRows.length === 0 ? (
            <div className="px-4 py-10 text-center text-axis-ash">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              Nothing in this bucket.
            </div>
          ) : (
            // Group by EXACT company name string. Companies whose name maps to
            // more than one ISIN (e.g. after a corporate-action ISIN change)
            // render as ONE combined window; each row keeps its own ISIN.
            (() => {
              const byName = new Map<string, ReconRow[]>();
              for (const row of filteredRows) {
                const key = groupKey(row);
                if (!byName.has(key)) byName.set(key, []);
                byName.get(key)!.push(row);
              }
              // Preserve the sorted order of the first occurrence of each name
              const seen = new Set<string>();
              const groups: ReconRow[][] = [];
              for (const row of filteredRows) {
                const key = groupKey(row);
                if (seen.has(key)) continue;
                seen.add(key);
                groups.push(byName.get(key)!);
              }
              // Render one company group (single ISIN or combined company).
              const renderGroup = (groupRows: ReconRow[]) => {
                const gkey = groupKey(groupRows[0]);
                if (groupRows.length === 1) {
                  const row = groupRows[0];
                  return <IsinRow key={row.id} row={row} pairs={pairsByRow.get(row.id) ?? []} displayName={displayLabel(row)} precomputedBands={bandsByGroupKey.get(gkey)} />;
                }
                const combinedPairs = groupRows.flatMap(r => pairsByRow.get(r.id) ?? []);
                return (
                  <CompanyRow
                    key={groupRows.map(r => r.id).join('+')}
                    rows={groupRows}
                    pairs={combinedPairs}
                    precomputedBands={bandsByGroupKey.get(gkey)}
                  />
                );
              };
              return groups.map(renderGroup);
            })()
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Company row (multiple ISINs of the same company) ──────────────── */
// Synthesizes ONE combined ReconRow for a company whose name maps to more
// than one ISIN, then delegates to IsinRow. Per-row ISIN is preserved in the
// pairs; matching stays per-ISIN inside IsinRow. Window totals are summed
// across all the company's ISINs.
function CompanyRow({ rows, pairs, precomputedBands }: { rows: ReconRow[]; pairs: ReconTxnPair[]; precomputedBands?: MatchBand[] }) {
  const combinedRow: ReconRow = useMemo(() => {
    const base = rows[0];
    return {
      ...base,
      canonical_isin: rows.map(r => r.canonical_isin).join(' · '),
      display_name: base.display_name,
      portfolio_txn_count: rows.reduce((s, r) => s + (r.portfolio_txn_count ?? 0), 0),
      dp_txn_count: rows.reduce((s, r) => s + (r.dp_txn_count ?? 0), 0),
    } as ReconRow;
  }, [rows]);

  // Unify canonical_isin across all pairs so buildMatchBands treats the whole
  // company as ONE security (a PF row of one ISIN can match a DP row of the
  // other). Preserve each pair's ORIGINAL ISIN in _displayIsin so the per-row
  // ISIN column still shows the true ISIN.
  const unifiedPairs = useMemo(() => {
    const unifiedIsin = rows[0]?.canonical_isin ?? '';
    return pairs.map(p => ({
      ...p,
      _displayIsin: p.canonical_isin,
      canonical_isin: unifiedIsin,
    }));
  }, [pairs, rows]);

  return <IsinRow row={combinedRow} pairs={unifiedPairs} precomputedBands={precomputedBands} />;
}

/* ─── Per-ISIN row ──────────────────────────────────────────────────── */

export function IsinRow({ row, pairs, displayName, precomputedBands }: { row: ReconRow; pairs: ReconTxnPair[]; displayName?: string; precomputedBands?: MatchBand[] }) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Collapse and bring the company header back into view. When you collapse
  // from the BOTTOM of a long expanded section, the header is far above the
  // current scroll position — so after collapsing we scroll it back to near the
  // top of the viewport, so you land right on the company you just collapsed.
  const collapseInPlace = () => {
    const el = rootRef.current;
    const beforeTop = el?.getBoundingClientRect().top ?? 0;
    setExpanded(false);
    requestAnimationFrame(() => {
      const node = rootRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      // If the header is already comfortably in view, just keep it steady.
      if (beforeTop >= 0 && beforeTop <= window.innerHeight * 0.6) {
        const delta = rect.top - beforeTop;
        if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
        return;
      }
      // Otherwise (collapsed from far below/above) scroll the header into view,
      // near the top, so the company XYZ is what you see.
      const targetY = window.scrollY + rect.top - 90; // 90px breathing room under any sticky header
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'auto' });
    });
  };
  const [layout, setLayout] = useState<'split' | 'single'>('split');
  const [visibleCounts, setVisibleCounts] = useState<{ pf: number; dp: number } | null>(null);

  // Single source of truth: compute bands once here, share across both layouts.
  // For a combined-company window (multiple ISINs of the same company after a
  // corporate-action ISIN change), ALL rows are treated as ONE security — the
  // full matching logic runs across the entire combined pair set, so a PF row
  // under one ISIN CAN match a DP row under the other. Each row still displays
  // its own ISIN, but matching does not partition by ISIN.
  const { bands } = useMemo(() => {
    // Reuse the bands already computed once by the parent (bandsByGroupKey) to
    // avoid running the heavy engine a second time. Falls back to computing
    // locally if no precomputed set was passed (preserves original behaviour
    // for any other caller).
    if (precomputedBands) return { bands: precomputedBands };
    try {
      return buildMatchBands(pairs ?? []);
    } catch (err) {
      // A data anomaly in one company must never blank the whole page. Log the
      // exact ISIN + error so the root cause is visible, and render this window
      // empty instead of throwing.
      console.error('[buildMatchBands] failed for ISIN', row?.canonical_isin, '(', displayName, ')', err);
      return { bands: [] as MatchBand[] };
    }
  }, [precomputedBands, pairs, row?.canonical_isin, displayName]);

  // Flat list of pairs visible in the bands — used by Single view so it
  // renders exactly the same entries as Side-by-side (PF rows + DP rows,
  // chronologically sorted by band sortDate).
  const visiblePairs = useMemo((): ReconTxnPair[] => {
    const result: ReconTxnPair[] = [];
    for (const band of bands) {
      for (const p of band.pfRows) result.push(p);
      for (const d of band.dpRows) result.push(d);
      if (band.pfRows.length === 0 && band.dpRow) result.push(band.dpRow);
    }
    return result;
  }, [bands]);

  // Breakdown computed from bands — used by both layouts' BreakdownPanel.
  const visibleBreakdown = useMemo(() => buildVisibleBreakdown(bands), [bands]);

  // Visible net values — used in the ISIN row header AND the bottom panel
  const visiblePfNet = visibleBreakdown.portfolio.buys_qty + visibleBreakdown.portfolio.sells_qty;
  const visibleDpNet = visibleBreakdown.dp.credits_qty - visibleBreakdown.dp.debits_qty;
  const visibleDiff  = visiblePfNet - visibleDpNet;

  // Header counts — derived from bands
  const headerCounts = useMemo(() => {
    if (visibleCounts) return visibleCounts;
    const pfCount = bands.reduce((s, b) => s + b.pfRows.length, 0);
    const dpCount = bands.reduce((s, b) =>
      s + (b.pfRows.length === 0 && b.dpRow ? 1 : b.dpRows.length), 0);
    return { pf: pfCount, dp: dpCount };
  }, [bands, visibleCounts]);

  return (
    <div ref={rootRef}>
      <button
        type="button"
        className="w-full px-4 py-3 grid grid-cols-12 gap-2 items-center text-left hover:bg-axis-cloud-50"
        onClick={() => expanded ? collapseInPlace() : setExpanded(true)}
      >
        <div className="col-span-4 flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="w-4 h-4 text-axis-ash flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-axis-ash flex-shrink-0" />}
          <div className="min-w-0">
            <div className="text-sm font-medium text-axis-ink truncate">{displayName ?? row.display_name}</div>
            <div className="font-mono text-[11px] text-axis-ash truncate">{row.canonical_isin}</div>
          </div>
        </div>
        <div className="col-span-2 text-right tabular-nums text-axis-ink">{fmtQty(visiblePfNet)}</div>
        <div className="col-span-2 text-right tabular-nums text-axis-ink">{fmtQty(visibleDpNet)}</div>
        <div className={`col-span-2 text-right tabular-nums font-medium ${Math.abs(visibleDiff) < 0.001 ? 'text-axis-ash' : visibleDiff > 0 ? 'text-amber-700' : 'text-rose-700'}`}>
          {visibleDiff > 0 ? '+' : ''}{fmtQty(visibleDiff)}
        </div>
        <div className="col-span-2 text-right">
          <StatusPill status={Math.abs(visibleDiff) < 0.001 ? 'MATCH' : row.status} hasUnmatched={bands.some(b => b.colour === 'red')} />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <div className="flex gap-2">
            {/* Clickable left rail — click anywhere along this vertical strip to
                collapse the whole trade, without scrolling to the top or bottom. */}
            <button
              type="button"
              onClick={collapseInPlace}
              title="Collapse this trade"
              aria-label="Collapse this trade"
              className="group shrink-0 w-6 self-stretch flex flex-col items-center justify-start pt-2 rounded-md transition hover:bg-axis-cloud-100"
              style={{ border: '1px solid var(--glass-border)', background: 'color-mix(in srgb, var(--status-info) 4%, transparent)' }}
            >
              <ChevronUp className="w-4 h-4 sticky top-2" style={{ color: 'var(--text-300)' }} />
            </button>
          <div className="flex-1 min-w-0 space-y-3">
            {/* Audit trail — every PF and DP txn for this ISIN, chronologically.
                Shown FIRST so the reviewer can see the raw rows immediately
                without scrolling past the summary. */}
            <div className="rounded-md border border-axis-cloud-200 overflow-hidden">
              <div
                className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
                style={{
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-strong)',
                  borderBottom: '1px solid var(--glass-border)',
                }}
              >
                <span className="text-[11px] uppercase tracking-wider font-medium">
                  Transactions — {headerCounts.pf} portfolio txn · {headerCounts.dp} DP txn
                </span>
                {pairs.length > 0 && (
                  <div className="flex items-center gap-1 text-[11px]">
                    <button
                      type="button"
                      onClick={() => setLayout('split')}
                      className="px-2 py-0.5 rounded"
                      style={{
                        background: layout === 'split' ? 'color-mix(in srgb, var(--status-info) 18%, transparent)' : 'transparent',
                        color: layout === 'split' ? 'var(--status-info)' : 'var(--text-200)',
                        fontWeight: layout === 'split' ? 600 : 400,
                      }}
                      title="Show portfolio and DP side-by-side"
                    >
                      Side-by-side
                    </button>
                    <button
                      type="button"
                      onClick={() => setLayout('single')}
                      className="px-2 py-0.5 rounded"
                      style={{
                        background: layout === 'single' ? 'color-mix(in srgb, var(--status-info) 18%, transparent)' : 'transparent',
                        color: layout === 'single' ? 'var(--status-info)' : 'var(--text-200)',
                        fontWeight: layout === 'single' ? 600 : 400,
                      }}
                      title="Single chronological list"
                    >
                      Single
                    </button>
                  </div>
                )}
              </div>
              {pairs.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs" style={{ color: 'var(--text-200)' }}>No transactions recorded.</div>
              ) : layout === 'split' ? (
                <SplitTxnTables bands={bands} visibleBreakdown={visibleBreakdown} row={row} onCounts={(pf, dp) => setVisibleCounts({ pf, dp })} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead
                      style={{
                        background: 'var(--bg-elevated)',
                        color: 'var(--text-strong)',
                      }}
                    >
                      <tr>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Side</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Date</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Type / Description</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Qty / Credit</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Debit</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Price / Trade value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-axis-cloud-200">
                      {visiblePairs.map(p => <AuditTxnRow key={p.id} pair={p} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Compact summary rendered inside SplitTxnTables (split layout)
                or below for single layout */}
            {layout === 'single' && <AiInsightBar insights={generateInsights(bands)} />}
            {layout === 'single' && <BreakdownPanel row={row} visibleBreakdown={visibleBreakdown} hasUnmatched={bands.some(b => b.colour === 'red')} />}

            {/* Bottom collapse button — mirrors the top row toggle so the user
                doesn't have to scroll back up to collapse a long trade. */}
            <button
              type="button"
              onClick={collapseInPlace}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-medium transition hover:bg-axis-cloud-100"
              style={{ color: 'var(--text-200)', border: '1px solid var(--glass-border)' }}
              title="Collapse this trade"
            >
              <ChevronUp className="w-3.5 h-3.5" />
              Collapse {displayName ?? row.canonical_isin}
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Client-side transaction matching engine ────────────────────────
 *
 * Rules (agreed with business 2026-06-08):
 *
 *  BUY (portfolio)
 *    → looks for a DP CREDIT within 1–3 calendar days AFTER the buy date.
 *    → Multiple BUY rows on the same date are grouped; their total qty must
 *      equal the DP credit qty for a match.
 *    → The matching DP row is drawn level with the LAST row of the group.
 *
 *  SELL (portfolio)
 *    → looks for a DP DEBIT on the EXACT same date.
 *    → Multiple SELL rows on the same date are grouped; total qty must
 *      equal the DP debit qty.
 *
 *  TRANSFEROUT (portfolio)
 *    → treated same as SELL — DP DEBIT on the exact same date, qty must match.
 *    → If the same date + qty appears twice in PF, only the FIRST one matches
 *      the single DP debit; the second stays unmatched (red).
 *
 *  BUYBACK (portfolio)
 *    → treated same as SELL — DP DEBIT same day, qty must match.
 *
 *  DIVIDEND / INTERIM DIVIDEND / FINAL DIVIDEND (portfolio)
 *    → auto-GREEN, no DP counterpart required.
 *
 *  Unmatched entries (either side) → RED.
 *
 * The engine runs entirely client-side on the txn_pairs array that the
 * API already returns. No backend changes needed.
 * ─────────────────────────────────────────────────────────────────── */

/** Types that are auto-green without needing a DP counterpart. */
const DIVIDEND_TYPES = new Set([
  'DIVIDEND', 'FINAL DIVIDEND', 'INTERIM DIVIDEND',
  'FINALDIVIDEND', 'INTERIMDIVIDEND',
]);

/** SELL rows — grouped by date, total qty matched to DP DEBIT same day. */
// SELL_GROUP_TYPES removed — grouping handled via sellGroupMap
/** Individual-delivery rows — each matched separately; first in order wins. */
const SELL_INDIV_TYPES = new Set(['TRANSFEROUT', 'TRANSFER OUT', 'BUYBACK']);
/** Union — used for type-class detection. */
const SELL_LIKE_TYPES = new Set(['SELL', 'TRANSFEROUT', 'TRANSFER OUT', 'BUYBACK']);

/** Portfolio transaction types that are treated as BUY for matching.
 *  Per spec Feature 2: any label ending in "Buy" is buy-side.
 *  CA labels: "Split Buy", "Bonus Buy", "Rights Buy", "Merger Buy", "ROC Buy" */
const BUY_LIKE_TYPES = new Set([
  'BUY', 'BUYCR', 'BONUS', 'SPLIT BUY', 'DEMERGERIN', 'DEMERGERBUY',
  'Split Buy', 'Bonus Buy', 'Rights Buy', 'Merger Buy', 'ROC Buy',
  // Uppercase variants (old jobs where parser uppercased the label)
  'BONUS BUY', 'RIGHTS BUY', 'MERGER BUY', 'ROC BUY',
]);

/** Extract settlement code from a DP description.
 *  "By CM AXIS SECURITIES LIMITED,T+1 NORMAL/2023142/..." → "2023142"
 *  Returns null when no NORMAL/ pattern is found. */
/** Returns true if this DP row is an eDIS / Block Mechanism line.
 *  eDIS lines are double-booked by the depository — identical pairs
 *  must be deduplicated before summing. Non-eDIS lines (CM Clearing etc.)
 *  are never deduplicated even when quantities are identical. */
function isEdisDpLine(description: string | null | undefined): boolean {
  if (!description) return false;
  const d = description.toLowerCase();
  return d.includes('edis') || d.includes('block mechanism');
}

/** "To Early Pay-in" DP entries are a pre-settlement earmarking of shares for
 *  an upcoming exchange sale — not a real depository movement to reconcile.
 *  They are hidden from the view and excluded from the DP net entirely. */
function isEarlyPayInDescription(description: string | null | undefined): boolean {
  if (!description) return false;
  return /early\s*pay[\s-]*in/i.test(description);
}

/** "By Partly Paid to Partly Paid Shares" and "By Partly Paid to Fully Paid
 *  Shares" DP entries — partly-paid share conversions. For now NEITHER must
 *  match any PF entry; both are shown as unmatched everywhere (excluded from
 *  matching and re-added as red), pending a dedicated rule. */
function isPartlyPaidConversion(description: string | null | undefined): boolean {
  if (!description) return false;
  return /partly\s*paid\s*to\s*(?:partly|fully)\s*paid/i.test(description);
}

/** Extract the settlement code from a "…BUY-BACK/<code>" DP debit description,
 *  e.g. "To NSE CLEARING LIMITED,BUY-BACK/2026071" → "2026071". */
function extractBuyBackCode(description: string | null | undefined): string | null {
  if (!description) return null;
  const m = description.match(/buy[\s-]?back\/(\d+)/i);
  return m ? m[1].trim() : null;
}

/** True when a DP credit is the return of un-accepted buy-back shares.
 *  Matches:
 *    - "By Tender Offer Cancellation ..."  (classic pattern)
 *    - "By CM NSE CLEARING LIMITED,BUY-BACK/..."  (NSE CM buy-back return credit)
 *    - "By ... BUY-BACK/..."  (any broker buy-back credit)
 */
function isTenderCancellation(description: string | null | undefined): boolean {
  if (!description) return false;
  if (/tender\s+offer\s+cancellation/i.test(description)) return true;
  // A "By ..." credit that contains "BUY-BACK/<code>" is a buy-back return/cancellation
  if (/^by\s+/i.test(description) && /buy[\s-]?back\/\d+/i.test(description)) return true;
  return false;
}

/** Deduplicate eDIS / Block Mechanism lines within a DP group (ISIN+code).
 *
 *  The depository double-books each eDIS settlement as a matching credit+debit
 *  pair under the same settlement code:
 *    By eDIS /Block Mechanism T+1 NORMAL/2023134  cr=10  ← the "blocked" credit
 *    To eDIS /Block Mechanism T+1 NORMAL/2023134  dr=10  ← the actual debit
 *
 *  Rule: for each settlement code that has BOTH a credit side and a debit side,
 *  keep exactly half of each direction (the two sides are mirror duplicates).
 *  If a code has ONLY debits (or only credits) those are real separate deliveries
 *  — e.g. two sells of 10 each under the same settlement — and must all be kept.
 *
 *  Non-eDIS lines (CM NSE Clearing, CM Axis, etc.) are never touched. */
function dedupeEdisLines(rows: ReconTxnPair[]): ReconTxnPair[] {
  const nonEdis: ReconTxnPair[] = [];
  // Group eDIS rows by settlement code → then by direction
  const byCode = new Map<string, { cr: ReconTxnPair[]; dr: ReconTxnPair[] }>();

  for (const row of rows) {
    if (!isEdisDpLine(row.dp_description)) {
      nonEdis.push(row);
      continue;
    }
    const code = extractDpSettlementCode(row.dp_description) ?? 'nocode';
    if (!byCode.has(code)) byCode.set(code, { cr: [], dr: [] });
    const g = byCode.get(code)!;
    if ((row.dp_credit ?? 0) > 0) g.cr.push(row);
    else g.dr.push(row);
  }

  const dedupedEdis: ReconTxnPair[] = [];
  for (const { cr, dr } of byCode.values()) {
    if (cr.length > 0 && dr.length > 0) {
      // Both sides present → double-booking: keep half of each direction
      dedupedEdis.push(...cr.slice(0, Math.ceil(cr.length / 2)));
      dedupedEdis.push(...dr.slice(0, Math.ceil(dr.length / 2)));
    } else {
      // Only one direction → all rows are real separate deliveries, keep all
      dedupedEdis.push(...cr, ...dr);
    }
  }

  return [...nonEdis, ...dedupedEdis];
}

/** Extract settlement code from DP description.
 *  Supports all known DP settlement prefixes:
 *    NORMAL/          — standard T+1 exchange settlement
 *    TRADE FOR TRADE/ — trade-for-trade segment (e.g. T group stocks)
 *  Extracts the run of digits immediately after the prefix, regardless of
 *  any other text, spacing, or non-ASCII characters elsewhere in the string.
 *  Examples:
 *    "By CM AXIS,T+1 NORMAL/2023112/xyz"         → "2023112"
 *    "By CM AXIS,T+1 ¿ NORMAL/2023112"           → "2023112"
 *    "By CM AXIS,T+1 ¿ TRADE FOR TRADE/2023126"  → "2023126"
 */
function extractDpSettlementCode(description: string | null | undefined): string | null {
  if (!description) return null;
  const m = description.match(/(?:NORMAL|TRADE FOR TRADE)\/([0-9]+)/i);
  return m ? m[1].trim() : null;
}

/**
 * Returns true when a DP description is a bank-reference credit —
 * "By [BANK NAME] LIMITED/<number>" — e.g.
 *   "By ICICI BANK LIMITED/90064449"
 *   "By HDFC BANK LIMITED/26275179"
 *
 * The number after "/" is a bank reference, NOT an exchange settlement
 * code (which matches [A-Z]{3,4}\d{6,}/ or NORMAL/…). Bank-reference
 * credits are treated exactly like blank-settlement credits: eligible
 * for Tier-2 individual-quantity matching, never for Tier-1.
 *
 * Discriminator: a genuine settlement code has an exchange-prefix
 * pattern; a bank reference does not.
 *
 * True when a DP credit has NO genuine exchange settlement code and
 * should be matched via Tier-2 individual-quantity matching.
 * Covers both truly blank narrations and bank-reference credits
 * ("By HDFC BANK LIMITED/22288461", "By ICICI BANK LIMITED/90064449").
 */
function isTier2Credit(description: string | null | undefined): boolean {
  if (!description || description.trim() === '') return true;
  if (/\bSLB\b/i.test(description)) return false; // SLB never matches — anywhere
  return !extractDpSettlementCode(description) && !isEdisDpLine(description);
}

/** One "visual band" rendered as a pair of aligned rows. */
interface MatchBand {
  /** All PF pair rows belonging to this band (1 or more). Empty array
   *  for a DP-only band (unmatched DP row with no PF counterpart). */
  pfRows: ReconTxnPair[];
  /** The single DP pair row that matched, or null if unmatched. */
  dpRow: ReconTxnPair | null;
  /** All DP rows that matched (may be multiple when eDIS splits one settlement).
   *  dpRow = dpRows[0] for backward compat; use dpRows for display totals. */
  dpRows: ReconTxnPair[];
  /** GREEN = fully matched or dividend; RED = no match. */
  colour: 'green' | 'red';
  /** True when pfRows is a group of buys/sells summed to match. */
  isGroup: boolean;
  /** IDs of PF rows that are part of an intraday pair (same ISIN+date, BUY qty = SELL qty) */
  intradayIds: Set<string>;
  /** Sort key — the date that determines this band's chronological
   *  position. For PF bands this is the PF row date; for DP-only
   *  bands (no PF counterpart) this is the DP row date. Every band,
   *  matched or unmatched, sorts into ONE chronological sequence so
   *  an unmatched DP entry from 2019 never gets stranded below
   *  matched entries from 2024. */
  sortDate: string;
  /** When set, this band is the "double-matching partial intraday" BUY split.
   *  The BUY (207) is displayed as two rows:
   *   - intraday portion (splitIntraday.sellQty = 53): orange, paired with the SELL
   *   - remainder (buyTotal - sellQty = 154): green, matched to DP credit
   *  This is purely a display split; the underlying pfRows are the same. */
  splitIntraday?: { sellQty: number; dpDebit?: ReconTxnPair | null };
  /** True when this sell band is the partial intraday sell paired with a splitIntraday BUY */
  partialIntradaySell?: boolean;
  /** True when this BUY is a "partial intraday" leftover: on a day where the full
   *  SELLs matched a DP debit, a same-day BUY has no dedicated SELL counterpart
   *  left (its offsetting sell was consumed by the DP delivery). Shown with
   *  intraday styling and excluded from the PF net. */
  partialIntradayBuy?: boolean;
  /** IDs of SELL rows in this band that are the partial-intraday counterpart of
   *  a partialIntradayBuy (they still settle to the DP, but are shown with the
   *  partial-intraday colour/tag to pair them visually with the buy). */
  partialIntradayPairIds?: Set<string>;
  /** For a net-sell reconciliation band: the intraday-overlap qty (= total buys
   *  that paired intraday with these sells). The sells still settle to the DP
   *  for the NET portion (sellTotal − intradayOverlap). Display note only. */
  sellIntradayOverlap?: number;
  /** True when matched via the last-resort "bonus narration match (T+8)" rule. */
  bonusNarrationMatch?: boolean;
  /** True when this is a special Split Buy/Sell ↔ Sub Division / Corporate
   *  Actions match (qty-only, no date window). Rendered with a purple flag. */
  splitSubDivMatch?: boolean;
  /** True when this is a "sell buy-back" match: a PF SELL reconciled against a
   *  DP "To … BUY-BACK/<code>" debit and (optionally) a "By Tender Offer
   *  Cancellation" credit, where buyback debit = sell qty + cancellation credit.
   *  Rendered with a $ flag on the PF side. */
  sellBuyBack?: boolean;
  /** True when matched via BBC (Buy-Back Cancellation): debit=sell+credit where
   *  both debit and credit carry a BUY-BACK/<code>. Badged 'BBC' in UI. */
  bbcMatch?: boolean;
  /** True for the single SLB group band (all SLB DP entries clubbed together at
   *  the bottom). Informational only — never matched, excluded from the DP net
   *  and from mismatch; rendered in a distinct (non-red) colour. */
  slb?: boolean;
  /** True when this band is an offmarket / CMOTS match (qty-only, DP on/after
   *  PF). Rendered with an orange "OFM" flag on the PF side. */
  offmarketMatch?: boolean;
  /** True for the offmarket MERGER conversion case: on one date, a BUY + Merger
   *  Sell + Merger Buy of equal qty; the DP delivery matches the MERGER BUY
   *  (not the plain BUY). Rendered with an orange "OMC" flag on the PF side. */
  offmarketMergerCase?: boolean;
  /** True for the BMSMB special case: a BUY + Merger Sell + Merger Buy triple
   *  where the DP credit carries the PLAIN BUY's settlement code — the MERGER
   *  BUY is matched to that coded DP (not the plain buy). Flagged "BMSMB". */
  bmsmb?: boolean;
  /** True when matched by the SDO (Same Day OffMarket) rule: source=Offmarket/null,
   *  DP date == PF date, non-Axis-CM broker, qty exact or grouped. Badged "SDO". */
  sdoMatch?: boolean;
  /** Bonus-Issue POOL DRAWDOWN as explicit, aligned SUB-ENTRIES.
   *  The DP credit(s) and PF rows are broken into matching sub-portions so each
   *  green line pairs a PF sub-entry with a DP sub-entry of equal qty, and each
   *  red line is a PF shortfall with no DP counterpart. Every sub-entry names
   *  its parent so a reader sees where it came from.
   *
   *  Example (Time Technoplast): PF Bonus Buy 65 + 373, DP By Bonus Issue 173
   *    → allocations:
   *       { qty: 65,  pfRow: <65>,  pfParentQty: 65,  dpRow: <173>, dpParentQty: 173, matched: true  }
   *       { qty: 108, pfRow: <373>, pfParentQty: 373, dpRow: <173>, dpParentQty: 173, matched: true  }
   *       { qty: 265, pfRow: <373>, pfParentQty: 373, dpRow: null,  dpParentQty: 0,   matched: false }
   */
  bonusPool?: {
    poolTotal: number;
    allocations: Array<{
      qty:         number;          // the sub-entry quantity
      pfRow:       ReconTxnPair | null; // parent PF row (null = DP-only shortfall)
      pfParentQty: number;          // full qty of the parent PF row
      pfSplit:     boolean;         // true if the parent PF row was broken into >1 sub-entry
      dpRow:       ReconTxnPair | null; // parent DP credit (null = PF shortfall)
      dpParentQty: number;          // full qty of the parent DP credit
      dpSplit:     boolean;         // true if the parent DP credit was broken into >1 sub-entry
      matched:     boolean;         // green (matched) vs red (shortfall)
      intraday?:   boolean;         // orange: this sub-portion is an intraday offset (paired with a same-day buy)
      dpOnly?:     boolean;         // red DP entry with no PF counterpart (DP shortfall)
      intradayOverlap?: boolean;    // green (matched to DP) AND also offsets the buy intraday (shared sell)
    }>;
  };
}

/** Returns true if this DP description is a pledge/margin internal operation
 *  that should never appear in the reconciliation view. */
function isPledgeDescription(description: string | null | undefined): boolean {
  if (!description) return false;
  const d = description.toLowerCase();
  return d.includes('to pledge') ||
         d.includes('by pledge') ||
         d.includes('pledge request') ||
         d.includes('pledge closure') ||
         d.includes('margin re-pledge');
}

/**
 * Build visual bands from the raw pairs array.
 * Returns an ordered list of bands that drive the side-by-side render.
 */

/** Renders a DP description with a red "22" badge when it is a "By Demat Acceptance"
 *  entry (ACCOUNT_TYPE_NEW=22 row kept visible after filtering out type-24 rows). */
function DpDescriptionCell({ desc }: { desc: string | null | undefined }) {
  const text = desc ?? '—';
  const isDematAcceptance = /by\s+demat\s+acceptance/i.test(text);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
      {isDematAcceptance && (
        <span style={{
          flexShrink: 0,
          display: 'inline-block',
          background: '#ef4444',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 700,
          lineHeight: 1,
          padding: '2px 4px',
          borderRadius: '3px',
          letterSpacing: '0.02em',
        }}>22</span>
      )}
    </span>
  );
}

function buildMatchBands(pairs: ReconTxnPair[]): { bands: MatchBand[] } {
  const pfPairs = pairs
    .filter(p => p.pf_txn_date != null)
    .sort((a, b) => (a.pf_txn_date! > b.pf_txn_date! ? 1 : -1));

  // Filter out pledge-only internal entries from the DP display.
  // These are registrar/custodian internal operations, not settlement entries.
  // eDIS and Block Mechanism entries are kept as-is.
  // EXCEPTION: pledge entries flagged is_tier3_match were admitted by Tier-3
  // pledge-recovery — keep them so they can be shown as the matched entry.
  // "By Partly Paid to Fully Paid Shares" entries must NOT match any PF entry
  // for now — collect them here and exclude from the matchable pool, then re-add
  // them as unmatched (red) bands after all matching so they show as unmatched
  // everywhere (and the PF rows that used to match them become unmatched too).
  const partlyPaidDp = pairs.filter(p =>
    p.pf_txn_date == null && p.dp_txn_date != null && isPartlyPaidConversion(p.dp_description),
  );

  // SLB entries must NEVER match any PF entry, in ANY case. They are held out of
  // the matchable pool entirely (so no pass — offmarket, same-date, BUY/SELL
  // flush, buy-back, etc. — can ever consume them) and re-added as unmatched
  // (red) bands so they stay VISIBLE. They are also excluded from the DP net.
  const isSlbDescription = (desc: string | null | undefined) => /\bSLB\b/i.test(desc ?? '');
  const slbDp = pairs.filter(p =>
    p.pf_txn_date == null && p.dp_txn_date != null && isSlbDescription(p.dp_description),
  );

  const dpPairs = pairs
    .filter(p => p.pf_txn_date == null && p.dp_txn_date != null)
    .filter(p => p.is_tier3_match || !isPledgeDescription(p.dp_description))
    .filter(p => !isEarlyPayInDescription(p.dp_description)) // hidden + excluded from DP net
    .filter(p => !isPartlyPaidConversion(p.dp_description)) // never matched; re-added as red below
    .filter(p => !isSlbDescription(p.dp_description))       // SLB never matches; re-added as red below
    .sort((a, b) => (a.dp_txn_date! > b.dp_txn_date! ? 1 : -1));

  const usedDp = new Set<string>();

  // BUY: group same-date rows — combined qty must match one DP credit by settlement code
  //   (pf_particulars == NORMAL/<code> in DP description). Falls back to any-date qty
  //   match when pf_particulars is null (old jobs or missing Particulars column).
  // SELL / TRANSFEROUT / BUYBACK: each row matched INDIVIDUALLY in chronological order.
  //   Two TRANSFEROUT 27 on same date = two separate deliveries. Only the first gets
  //   the matching DP debit of 27 (green); the second stays unmatched (red).
  //   Grouping them (54 total) would never match a single DP debit of 27.
  // DIV: auto-green, grouped by date.
  type PfGroup = { key: string; date: string; typeClass: string; rows: ReconTxnPair[] };

  // Reusable POOL DRAWDOWN → split-match band. Draws the DP total against the PF
  // rows in order, splitting a PF row when it straddles the pool boundary. Each
  // covered chunk pairs a PF sub-portion with a DP sub-portion (green); any
  // leftover PF is a red shortfall. Used to "match the maximum" by the same
  // settlement code when quantities don't line up 1:1 (e.g. BUY 25+10=35 vs DP
  // credit 30 → 25 + 5 matched, 5 red). Marks consumed DP rows used.
  const makePoolBand = (
    pfRows: ReconTxnPair[],
    dpRowsIn: ReconTxnPair[],
    date: string,
    dpQtyOf: (dp: ReconTxnPair) => number,
    markShortfallIntraday = false,
    intradayReserveQty = 0,
  ): MatchBand => {
    const pfSubCount = new Map<string, number>();
    const dpSubCount = new Map<string, number>();
    type Alloc = { qty: number; pfRow: ReconTxnPair | null; pfParentQty: number; pfSplit: boolean;
                   dpRow: ReconTxnPair | null; dpParentQty: number; dpSplit: boolean; matched: boolean; intraday?: boolean; dpOnly?: boolean };
    const allocations: Alloc[] = [];
    let di = 0;
    let dpRemaining = di < dpRowsIn.length ? dpQtyOf(dpRowsIn[di]) : 0;
    // How much of the PF total is available for the DP match (the rest is held
    // back as intraday reserve, drawn from the LAST rows).
    const totalPf = pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    let dpMatchBudget = Math.max(0, totalPf - intradayReserveQty);
    for (const row of pfRows) {
      let pfRemaining = Math.abs(Number(row.pf_qty) ?? 0);
      const pfParentQty = pfRemaining;
      // DP-matched portion (bounded by the dpMatchBudget)
      while (pfRemaining > 0.001 && dpMatchBudget > 0.001 && di < dpRowsIn.length && dpRemaining > 0.001) {
        const take = Math.min(pfRemaining, dpRemaining, dpMatchBudget);
        allocations.push({ qty: take, pfRow: row, pfParentQty, pfSplit: false, dpRow: dpRowsIn[di], dpParentQty: dpQtyOf(dpRowsIn[di]), dpSplit: false, matched: true });
        pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
        dpSubCount.set(dpRowsIn[di].id, (dpSubCount.get(dpRowsIn[di].id) ?? 0) + 1);
        pfRemaining -= take; dpRemaining -= take; dpMatchBudget -= take;
        if (dpRemaining <= 0.001) { di++; dpRemaining = di < dpRowsIn.length ? dpQtyOf(dpRowsIn[di]) : 0; }
      }
      // Remainder of this PF row: intraday offset (orange) or red shortfall.
      if (pfRemaining > 0.001) {
        allocations.push({ qty: pfRemaining, pfRow: row, pfParentQty, pfSplit: false, dpRow: null, dpParentQty: 0, dpSplit: false, matched: markShortfallIntraday, intraday: markShortfallIntraday });
        pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
      }
    }
    // Any DP quantity left uncovered → red DP-only shortfall sub-entries.
    while (di < dpRowsIn.length) {
      if (dpRemaining > 0.001) {
        allocations.push({ qty: dpRemaining, pfRow: null, pfParentQty: 0, pfSplit: false, dpRow: dpRowsIn[di], dpParentQty: dpQtyOf(dpRowsIn[di]), dpSplit: false, matched: false, dpOnly: true });
        dpSubCount.set(dpRowsIn[di].id, (dpSubCount.get(dpRowsIn[di].id) ?? 0) + 1);
      }
      di++; dpRemaining = di < dpRowsIn.length ? dpQtyOf(dpRowsIn[di]) : 0;
    }
    for (const a of allocations) {
      a.pfSplit = a.pfRow ? (pfSubCount.get(a.pfRow.id) ?? 1) > 1 : false;
      a.dpSplit = a.dpRow ? (dpSubCount.get(a.dpRow.id) ?? 1) > 1 : false;
    }
    dpRowsIn.forEach(dp => usedDp.add(dp.id));
    const anyGreen = allocations.some(a => a.matched && !a.intraday);
    const poolTotal = dpRowsIn.reduce((s, dp) => s + dpQtyOf(dp), 0);
    return { pfRows, dpRow: dpRowsIn[0] ?? null, dpRows: dpRowsIn, colour: anyGreen ? 'green' : 'red', isGroup: true, sortDate: date, intradayIds: new Set(), bonusPool: { poolTotal, allocations } };
  };

  // Split PF rows against a target qty: the covered portion is flagged intraday
  // (orange), any remainder is red. Used to show a BUY split into an intraday
  // part (offsetting leftover sells) and a red unmatched part.
  const makeSplitIntradayBand = (pfRows: ReconTxnPair[], intradayTarget: number, date: string): MatchBand => {
    const pfSubCount = new Map<string, number>();
    type Alloc = { qty: number; pfRow: ReconTxnPair; pfParentQty: number; pfSplit: boolean;
                   dpRow: ReconTxnPair | null; dpParentQty: number; dpSplit: boolean; matched: boolean; intraday?: boolean };
    const allocations: Alloc[] = [];
    let remaining = intradayTarget;
    for (const row of pfRows) {
      let pfRemaining = Math.abs(Number(row.pf_qty) ?? 0);
      const pfParentQty = pfRemaining;
      if (remaining > 0.001) {
        const take = Math.min(pfRemaining, remaining);
        allocations.push({ qty: take, pfRow: row, pfParentQty, pfSplit: false, dpRow: null, dpParentQty: 0, dpSplit: false, matched: true, intraday: true });
        pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
        pfRemaining -= take; remaining -= take;
      }
      if (pfRemaining > 0.001) {
        allocations.push({ qty: pfRemaining, pfRow: row, pfParentQty, pfSplit: false, dpRow: null, dpParentQty: 0, dpSplit: false, matched: false });
        pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
      }
    }
    for (const a of allocations) a.pfSplit = (pfSubCount.get(a.pfRow.id) ?? 1) > 1;
    return { pfRows, dpRow: null, dpRows: [], colour: 'red', isGroup: true, sortDate: date, intradayIds: new Set(), bonusPool: { poolTotal: 0, allocations } };
  };

  const buyGroupMap  = new Map<string, PfGroup>();
  const sellGroupMap = new Map<string, PfGroup>();
  const divGroupMap  = new Map<string, PfGroup>();
  const caGroupMap   = new Map<string, PfGroup>(); // Corporate Action bonus rows
  const splitBuyRows: ReconTxnPair[] = []; // Split Buy → By Sub Division / By Corporate Actions credit (qty-only)
  const splitSellRows: ReconTxnPair[] = []; // Split Sell → To Sub Division / To Corporate Actions debit (qty-only)
  const offmarketRows: ReconTxnPair[] = []; // Offmarket BUY/Transfer → external no-code DP credit/debit (qty-only, no date window)
  const sdoSellRows: ReconTxnPair[] = []; // source=null/offmarket Transfer Out / Sell-like rows for SDO debit matching
  const buyBackSells: ReconTxnPair[] = []; // PF SELL whose code matches a DP "BUY-BACK/<code>" debit
  const mergerBuyRows:  ReconTxnPair[] = []; // "Merger Buy" rows (any source) — for OMC
  const mergerSellRows: ReconTxnPair[] = []; // "Merger Sell" rows (any source) — for OMC
  const bands: MatchBand[] = [];

  // Pre-index DP buy-back debit codes so PF SELLs that belong to a buy-back can
  // be intercepted before the normal SELL flush (a "…BUY-BACK/<code>" debit is
  // NOT parsed by extractDpSettlementCode, so it would otherwise leave the sell
  // red).
  const buyBackCodes = new Set<string>();
  for (const dp of dpPairs) {
    if (Number(dp.dp_debit ?? 0) <= 0) continue;
    const code = extractBuyBackCode(dp.dp_description);
    if (code) buyBackCodes.add(code);
  }

  for (const p of pfPairs) {
    const rawType  = (p.pf_txn_type ?? '').toUpperCase().trim(); // for SELL/DIV lookup
    const origType = (p.pf_txn_type ?? '').trim();                   // verbatim for display/CA
    let typeClass = 'OTHER';
    if (DIVIDEND_TYPES.has(rawType))                                       typeClass = 'DIV';
    else if (BUY_LIKE_TYPES.has(rawType) || BUY_LIKE_TYPES.has(origType) || origType.endsWith('Buy') || origType.endsWith('BUY')) typeClass = 'BUY';
    else if (SELL_LIKE_TYPES.has(rawType))                                 typeClass = 'SELL';

    // Merger conversion rows are collected FIRST, regardless of pf_source, so
    // the OMC pass can find them whether or not they are CMOTS-sourced. (A
    // "Merger Buy" would otherwise fall into the normal BUY flush and a "Merger
    // Sell" into OTHER, hiding them from the OMC pass.)
    if (/merger\s*buy/i.test(origType))  { mergerBuyRows.push(p);  continue; }
    if (/merger\s*sell/i.test(origType)) { mergerSellRows.push(p); continue; }

    // Sell buy-back: a PF SELL whose settlement code matches a DP
    // "…BUY-BACK/<code>" debit. Checked BEFORE the offmarket/CMOTS collection so
    // a CMOTS-sourced buy-back SELL is still routed to the dedicated buy-back
    // pass (buyback debit = sell qty + tender-offer-cancellation credit) instead
    // of being diverted into offmarket matching by its source.
    if (rawType === 'SELL') {
      const code = (p.pf_particulars ?? '').trim();
      if (code && buyBackCodes.has(code)) {
        buyBackSells.push(p);
        continue;
      }
      // No particulars — check if any BUY-BACK debit qty == sell qty + optional cancel credit.
      // e.g. SELL 91, debit 100, cancel credit 9 → 91 + 9 = 100 ✓
      if (!code) {
        const sellQty = Math.abs(Number(p.pf_qty ?? 0));
        const hasMatch = dpPairs.some(dp => {
          if (Number(dp.dp_debit ?? 0) <= 0) return false;
          const bbCode = extractBuyBackCode(dp.dp_description);
          if (!bbCode) return false;
          const debitQty = Number(dp.dp_debit ?? 0);
          if (Math.abs(debitQty - sellQty) < 0.001) return true; // full acceptance
          // Check debit == sell + cancel
          const shortfall = debitQty - sellQty;
          if (shortfall <= 0) return false;
          return dpPairs.some(c =>
            !usedDp.has(c.id) &&
            Number(c.dp_credit ?? 0) > 0 &&
            isTenderCancellation(c.dp_description) &&
            Math.abs(Number(c.dp_credit ?? 0) - shortfall) < 0.001
          );
        });
        if (hasMatch) {
          buyBackSells.push(p);
          continue;
        }
      }
    }

    // Offmarket / CMOTS trades — a BUY rule only. Offmarket/CMOTS BUYs settle
    // bilaterally against an external bank, so the DP side shows a CREDIT with
    // NO settlement code (e.g. "By ICICI BANK LIMITED/64606465"). Matched by
    // quantity (single, group-of-PF, or group-of-DP), DP dated on/after the PF.
    //
    // Only BUY-LIKE rows are routed here. SELL-like rows (SELL, Transfer Out,
    // buy-back sells) must NOT be diverted into the offmarket path — they flow
    // through normal sell handling (e.g. the sell buy-back pass), otherwise a
    // CMOTS-sourced sell would break those cases. DIVIDENDS are also excluded —
    // they always stay in their own separate dividend group, never matched as
    // part of an offmarket group (their qty is 0, which would otherwise let them
    // ride along in a subset-sum).
    // A PF source of "NULL" is treated as an offmarket entry too (same rules).
    const pfSrcRaw = (p.pf_source ?? '').trim().toLowerCase();
    // Normalise offmarket variants: 'offmarket', 'off-market', 'off market' all → 'offmarket'
    const pfSrc = pfSrcRaw.replace(/[\s_-]+/g, '') === 'offmarket' ? 'offmarket' : pfSrcRaw;
    const isOffmarketSrc = pfSrc === 'offmarket' || pfSrc === 'null';
    const isSellLikeRow = SELL_LIKE_TYPES.has(rawType) || /transfer\s*out|sell/i.test(rawType);
    if ((isOffmarketSrc || pfSrc === 'cmots') && !isSellLikeRow && typeClass !== 'DIV') {
      offmarketRows.push(p);
      continue;
    }
    // Transfer Out (and other sell-like rows) with source=null/offmarket are routed
    // to sdoSellRows for SDO debit matching. They must NOT go into offmarketRows
    // (which is BUY-only) and normal sell handling has no path for no-code external debits.
    if (isOffmarketSrc && isSellLikeRow && typeClass !== 'DIV') {
      sdoSellRows.push(p);
      continue;
    }

    // Split Sell (stock-split share cancellation of the old ISIN) matches a DP
    // "To Sub Division" DEBIT purely by quantity — NO date window, first-come-
    // first-match. Collected here before the BUY/SELL/OTHER branches because
    // "Split Sell" is not a plain SELL type. Same company scope is implicit:
    // buildMatchBands runs per-company, so pfPairs/dpPairs are one security.
    if (/split\s*sell/i.test(origType)) {
      splitSellRows.push(p);
      continue;
    }

    if (typeClass === 'DIV') {
      const key = `${p.pf_txn_date}|DIV`;
      if (!divGroupMap.has(key)) divGroupMap.set(key, { key, date: p.pf_txn_date!, typeClass: 'DIV', rows: [] });
      divGroupMap.get(key)!.rows.push(p);
      continue;
    }

    if (typeClass === 'BUY') {
      // Corporate Action rows (bonus/split from PF File Gen) are matched
      // by ISIN + date + qty — not by settlement code — because DP bonus
      // credits have no NORMAL/ code. Separate them from normal buys.
      const caTypeLabel = origType;
      const isCARow = caTypeLabel === 'Bonus Buy' || caTypeLabel === 'Rights Buy' ||
        caTypeLabel === 'BONUS BUY' || caTypeLabel === 'RIGHTS BUY';
      if (isCARow) {
        const key = `${p.pf_txn_date}|CA`;
        if (!caGroupMap.has(key)) caGroupMap.set(key, { key, date: p.pf_txn_date!, typeClass: 'CA', rows: [] });
        caGroupMap.get(key)!.rows.push(p);
        continue;
      }
      // Split Buy (stock-split share issue) matches a DP "By Sub Division"
      // CREDIT purely by quantity (grouped), with NO date window — the sub-
      // division credit can post on any date, even before the PF date.
      const isSplitBuy = /split\s*buy/i.test(caTypeLabel);
      if (isSplitBuy) {
        splitBuyRows.push(p);
        continue;
      }
      const key = `${p.pf_txn_date}|BUY`;
      if (!buyGroupMap.has(key)) buyGroupMap.set(key, { key, date: p.pf_txn_date!, typeClass: 'BUY', rows: [] });
      buyGroupMap.get(key)!.rows.push(p);
      continue;
    }

    if (typeClass === 'SELL') {
      // SELL rows: check if individual (TRANSFEROUT/BUYBACK) or grouped (SELL)
      const rawT = (p.pf_txn_type ?? '').toUpperCase().trim();
      if (SELL_INDIV_TYPES.has(rawT)) {
        // TRANSFEROUT / BUYBACK — each row matched individually; first wins
        const qty = Math.abs(Number(p.pf_qty) ?? 0);
        const date = p.pf_txn_date!;
        let matched: ReconTxnPair | null = null;
        for (const dp of dpPairs) {
          if (usedDp.has(dp.id)) continue;
          const debit = Number(dp.dp_debit ?? 0);
          if (debit <= 0 || dp.dp_txn_date !== date) continue;
          if (Math.abs(debit - qty) < 0.001) { matched = dp; break; }
        }
        if (matched) usedDp.add(matched.id);
        bands.push({ pfRows: [p], dpRow: matched, dpRows: matched ? [matched] : [], colour: matched ? 'green' : 'red', isGroup: false, sortDate: date, intradayIds: new Set() });
      } else {
        // SELL — group by date into sellGroupMap, processed after the loop
        const key = `${p.pf_txn_date}|SELL`;
        if (!sellGroupMap.has(key)) sellGroupMap.set(key, { key, date: p.pf_txn_date!, typeClass: 'SELL', rows: [] });
        sellGroupMap.get(key)!.rows.push(p);
      }
      continue;
    }

    // OTHER
    bands.push({ pfRows: [p], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: p.pf_txn_date!, intradayIds: new Set() });
  }

  // ── OMC: offmarket MERGER conversion (same day, same qty) ──────────────────
  // Pattern on one date+qty: a plain BUY + a "Merger Sell" + a "Merger Buy".
  // The plain BUY + Merger Sell are the pre-merger holding that converts (they
  // net to zero); the MERGER BUY is the delivered new-company position, so a DP
  // "By" credit delivery matches the MERGER BUY — NOT the plain BUY. Flag "OMC".
  //
  // GROUPING: several merger conversions can settle into ONE DP credit. Any
  // number (n) of valid Merger Buys whose quantities SUM to a DP credit are
  // grouped and matched to it (e.g. Merger Buy 11 + Merger Buy 35 → "By" 46).
  //
  // The DP credit may be an "Axis Securities" entry here (the merger triple is a
  // strong signal, like the same-date exception); SLB / pledge / eDIS / coded
  // settlements are still excluded. DP must be dated on/after the merger buys.
  // Runs BEFORE the offmarket/BUY flushes so it can claim the DP and pull the
  // plain BUYs out of their normal pools.
  const omcQty = (r: ReconTxnPair) => Math.abs(Number(r.pf_qty) ?? 0);
  const omcDpEligible = (d: ReconTxnPair): boolean => {
    if (usedDp.has(d.id)) return false;
    if (Number(d.dp_credit ?? 0) <= 0) return false;
    if (isPledgeDescription(d.dp_description)) return false;
    if (isEdisDpLine(d.dp_description)) return false;
    if (/\bSLB\b/i.test(d.dp_description ?? '')) return false; // SLB never matches
    if (extractDpSettlementCode(d.dp_description) !== null) return false; // no coded settlement
    return true; // Axis ALLOWED for the merger-conversion delivery
  };
  const omcConsumed = new Set<string>();

  // Step 1 — pair each Merger Buy with its triple: an unassigned same-date+qty
  // Merger Sell AND an unassigned same-date+qty plain BUY (from the offmarket
  // pool or the normal BUY groups). Only fully-tripled merger buys are valid.
  type OmcTriple = { mBuy: ReconTxnPair; mSell: ReconTxnPair; pBuy: ReconTxnPair; pBuyFrom: 'offmarket' | 'buygroup' };
  const assignedSell = new Set<string>();
  const assignedPBuy = new Set<string>();
  const triples: OmcTriple[] = [];
  const findPlainBuy = (date: string, qty: number): { row: ReconTxnPair; from: 'offmarket' | 'buygroup' } | null => {
    const inOff = offmarketRows.find(r => !assignedPBuy.has(r.id) && (r.pf_txn_date ?? '') === date && omcQty(r) === qty && /buy/i.test(r.pf_txn_type ?? ''));
    if (inOff) return { row: inOff, from: 'offmarket' };
    for (const g of buyGroupMap.values()) {
      const f = g.rows.find(r => !assignedPBuy.has(r.id) && (r.pf_txn_date ?? '') === date && omcQty(r) === qty);
      if (f) return { row: f, from: 'buygroup' };
    }
    return null;
  };
  for (const mBuy of mergerBuyRows) {
    const date = mBuy.pf_txn_date ?? '';
    const qty  = omcQty(mBuy);
    const mSell = mergerSellRows.find(r => !assignedSell.has(r.id) && (r.pf_txn_date ?? '') === date && omcQty(r) === qty);
    if (!mSell) continue;
    const pBuyInfo = findPlainBuy(date, qty);
    if (!pBuyInfo) continue;
    assignedSell.add(mSell.id);
    assignedPBuy.add(pBuyInfo.row.id);
    triples.push({ mBuy, mSell, pBuy: pBuyInfo.row, pBuyFrom: pBuyInfo.from });
  }
  const tripleByMBuy = new Map(triples.map(t => [t.mBuy.id, t]));

  // Exact subset of rows whose qty sums to target (polynomial DP, earliest-preferring).
  const omcFindSubset = (rows: ReconTxnPair[], target: number): ReconTxnPair[] | null => {
    const n = rows.length;
    const q = rows.map(omcQty);
    const t = Math.round(target);
    if (!Number.isInteger(target) || !q.every(Number.isInteger) || t <= 0 || n === 0) {
      // fall back to exact single match
      const one = rows.find(r => Math.abs(omcQty(r) - target) < 0.001);
      return one ? [one] : null;
    }
    if ((t + 1) * (n + 1) > 30_000_000) return null;
    const reach: Uint8Array[] = new Array(n + 1);
    reach[n] = new Uint8Array(t + 1); reach[n][0] = 1;
    for (let i = n - 1; i >= 0; i--) {
      const prev = reach[i + 1]; const cur = prev.slice(); const qi = q[i];
      if (qi <= t) for (let s = t - qi; s >= 0; s--) if (prev[s]) cur[s + qi] = 1;
      reach[i] = cur;
    }
    if (!reach[0][t]) return null;
    const chosen: ReconTxnPair[] = []; let rem = t;
    for (let i = 0; i < n && rem > 0; i++) { if (q[i] <= rem && reach[i + 1][rem - q[i]]) { chosen.push(rows[i]); rem -= q[i]; } }
    return rem === 0 ? chosen : null;
  };

  // Step 2a — BMSMB special case. When a triple (BUY + Merger Sell + Merger Buy,
  // same day+qty) exists AND the DP credit carries the PLAIN BUY's settlement
  // code, match the MERGER BUY to that coded DP — NOT the plain buy (which would
  // otherwise win via its settlement number in the normal BUY flush). The plain
  // BUY + Merger Sell net to zero (shown red). Flag "BMSMB". Runs before the BUY
  // flush and pulls the plain BUY from its pool so it can't claim the DP.
  //
  // GROUPING: several triples that share the same plain-BUY settlement code are
  // grouped — their Merger Buys SUM to a single coded DP credit (e.g. two
  // Merger Buys of 100 → "By …NORMAL/2026076" 200).
  //
  // Group the triples by their plain BUY's settlement code.
  const triplesByCode = new Map<string, OmcTriple[]>();
  for (const tri of triples) {
    const code = (tri.pBuy.pf_particulars ?? '').trim();
    if (!code) continue;
    if (!triplesByCode.has(code)) triplesByCode.set(code, []);
    triplesByCode.get(code)!.push(tri);
  }
  for (const [buyCode, group] of triplesByCode.entries()) {
    if (group.every(t => omcConsumed.has(t.mBuy.id))) continue;
    const live = group.filter(t => !omcConsumed.has(t.mBuy.id));
    if (live.length === 0) continue;
    const isin = live[0].mBuy.canonical_isin;
    const groupQty = live.reduce((s, t) => s + omcQty(t.mBuy), 0);
    // Find the coded DP credit(s) (same code + ISIN) whose total equals the
    // combined merger-buy qty. Usually one entry; sum in case it's split.
    const dpCands = dpPairs.filter(d =>
      !usedDp.has(d.id) &&
      Number(d.dp_credit ?? 0) > 0 &&
      d.canonical_isin === isin &&
      extractDpSettlementCode(d.dp_description) === buyCode &&
      !/\bSLB\b/i.test(d.dp_description ?? '') &&
      !isPledgeDescription(d.dp_description),
    );
    const dpTotal = dpCands.reduce((s, d) => s + Number(d.dp_credit ?? 0), 0);
    if (dpCands.length === 0 || Math.abs(dpTotal - groupQty) >= 0.001) continue;
    dpCands.forEach(d => usedDp.add(d.id));
    const mBuys: ReconTxnPair[] = [];
    const leftover: ReconTxnPair[] = [];
    for (const tri of live) {
      omcConsumed.add(tri.mBuy.id); omcConsumed.add(tri.mSell.id); omcConsumed.add(tri.pBuy.id);
      mBuys.push(tri.mBuy);
      leftover.push(tri.pBuy, tri.mSell);
      if (tri.pBuyFrom === 'offmarket') {
        const i = offmarketRows.findIndex(r => r.id === tri.pBuy.id);
        if (i >= 0) offmarketRows.splice(i, 1);
      } else {
        for (const g of buyGroupMap.values()) {
          const i = g.rows.findIndex(r => r.id === tri.pBuy.id);
          if (i >= 0) { g.rows.splice(i, 1); break; }
        }
      }
    }
    // Grouped MERGER BUYs ↔ the coded DP credit(s) (green, BMSMB).
    mBuys.sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
    bands.push({ pfRows: mBuys, dpRow: dpCands[0], dpRows: dpCands, colour: 'green', isGroup: mBuys.length > 1, sortDate: mBuys[0].pf_txn_date!, intradayIds: new Set(), bmsmb: true });
    // Plain BUYs + Merger Sells net to zero → one red BMSMB group.
    leftover.sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
    bands.push({ pfRows: leftover, dpRow: null, dpRows: [], colour: 'red', isGroup: leftover.length > 1, sortDate: leftover[0].pf_txn_date!, intradayIds: new Set(), bmsmb: true });
  }

  // Step 2 — DP-driven: for each eligible DP "By" credit (earliest first), find
  // a group of valid Merger Buys (dated on/before the DP) summing to the credit.
  let omcRemaining = triples.map(t => t.mBuy).sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
  const omcPool = dpPairs.filter(omcDpEligible).sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
  for (const dp of omcPool) {
    if (usedDp.has(dp.id)) continue;
    const dpDate = dp.dp_txn_date ?? '';
    const elig = omcRemaining.filter(r => (r.pf_txn_date ?? '') <= dpDate);
    const subset = omcFindSubset(elig, Number(dp.dp_credit ?? 0));
    if (!subset || subset.length === 0) continue;
    usedDp.add(dp.id);
    const ids = new Set(subset.map(r => r.id));
    // One OMC band for the grouped Merger Buys ↔ the DP credit.
    bands.push({ pfRows: subset, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: subset.length > 1, sortDate: subset[0].pf_txn_date!, intradayIds: new Set(), offmarketMergerCase: true });
    // Consume each matched merger buy's triple: the plain BUY + Merger Sell net
    // to zero. Collect them all into ONE collapsible red band, flagged OMC.
    const omcLeftover: ReconTxnPair[] = [];
    for (const mb of subset) {
      const tri = tripleByMBuy.get(mb.id);
      omcConsumed.add(mb.id);
      if (!tri) continue;
      omcConsumed.add(tri.mSell.id); omcConsumed.add(tri.pBuy.id);
      omcLeftover.push(tri.pBuy, tri.mSell);
      if (tri.pBuyFrom === 'offmarket') {
        const i = offmarketRows.findIndex(r => r.id === tri.pBuy.id);
        if (i >= 0) offmarketRows.splice(i, 1);
      } else {
        for (const g of buyGroupMap.values()) {
          const i = g.rows.findIndex(r => r.id === tri.pBuy.id);
          if (i >= 0) { g.rows.splice(i, 1); break; }
        }
      }
    }
    if (omcLeftover.length > 0) {
      omcLeftover.sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
      bands.push({ pfRows: omcLeftover, dpRow: null, dpRows: [], colour: 'red', isGroup: omcLeftover.length > 1, sortDate: omcLeftover[0].pf_txn_date!, intradayIds: new Set(), offmarketMergerCase: true });
    }
    omcRemaining = omcRemaining.filter(r => !ids.has(r.id));
  }

  // Step 3 — standalone Merger Buys (no triple): group by QUANTITY and match a
  // DP credit (e.g. "By Demerger") regardless of the date gap. The merger buys
  // can be on the same day or different days; the DP can post any number of days
  // later. (Merger-buy source is typically NULL/CMOTS.) Inter-depository credits
  // are excluded — those belong to the normal BUY flush.
  let standaloneMB = mergerBuyRows.filter(r => !omcConsumed.has(r.id))
    .sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
  const omcPool2 = dpPairs
    .filter(dp => omcDpEligible(dp) && !/inter\s*depository/i.test(dp.dp_description ?? ''))
    .sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
  for (const dp of omcPool2) {
    if (usedDp.has(dp.id) || standaloneMB.length === 0) continue;
    const subset = omcFindSubset(standaloneMB, Number(dp.dp_credit ?? 0)); // no date constraint
    if (!subset || subset.length === 0) continue;
    usedDp.add(dp.id);
    const ids = new Set(subset.map(r => r.id));
    subset.forEach(r => omcConsumed.add(r.id));
    bands.push({ pfRows: subset, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: subset.length > 1, sortDate: subset[0].pf_txn_date!, intradayIds: new Set(), offmarketMergerCase: true });
    standaloneMB = standaloneMB.filter(r => !ids.has(r.id));
  }

  // Leftover merger rows (no complete triple / no DP group) → shown unmatched.
  for (const r of mergerBuyRows)  if (!omcConsumed.has(r.id)) bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() });
  for (const r of mergerSellRows) if (!omcConsumed.has(r.id)) bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() });

  // Flush DIV groups (auto-green)
  for (const { date: divDate, rows } of divGroupMap.values()) {
    bands.push({ pfRows: rows, dpRow: null, dpRows: [], colour: 'green', isGroup: rows.length > 1, sortDate: divDate, intradayIds: new Set() });
  }

  // Flush CA groups — Bonus Buy / Rights Buy rows matched to DP bonus credits
  // ("By Bonus Issue", "By Corporate Actions", etc.) that have no NORMAL/ code.
  //
  // Two matching strategies, tried in order:
  //   Strategy A — exact per-row qty match (Pass 1) + group-sum (Pass 2).
  //     Handles the common case where each PF bonus row equals a DP credit.
  //   Strategy B — POOL DRAWDOWN (special Bonus-Issue scenario). When exact
  //     matching leaves rows unmatched, treat the DP bonus credits as a single
  //     quantity pool and draw down against the PF bonus rows in order
  //     (first-come-first-match). Qtys need NOT line up 1:1. When a PF row is
  //     only partially covered, SPLIT it into a green (covered) band and a red
  //     (shortfall) band via caPartialQty.
  //
  //     Worked example (Time Technoplast INE508G01029):
  //       PF Bonus Buy: 65, 373   DP "By Bonus Issue": 173
  //       Pool = 173. Draw: 65 → 173-65=108 left. 373 → covered 108, short 265.
  //       Result: 65 green, 373 split into 108 green + 265 red.
  //
  // NO date window: a DP bonus credit matches regardless of how many days from
  // the PF bonus date (all other rules — ISIN, no settlement code, credit —
  // still apply).
  for (const { date, rows } of caGroupMap.values()) {
    const isin = rows[0]?.canonical_isin ?? '';

    // CA flush pool: ONLY accept genuine CA/Bonus narrations.
    // Bank-reference credits ("By HDFC BANK.../...") and blank credits
    // are Tier-2 targets for plain BUYs — never for Bonus Buy / Rights Buy.
    const isCaNarration = (desc: string | null | undefined): boolean => {
      if (!desc) return false;
      return /^by\s+corporate\s+action/i.test(desc) ||
             /^by\s+bonus/i.test(desc) ||
             /by\s+bonus\s+issue/i.test(desc) ||
             /by\s+sub\s*division/i.test(desc);
    };
    const candidatePool = dpPairs.filter(dp => {
      if (usedDp.has(dp.id)) return false;
      const credit = Number(dp.dp_credit ?? 0);
      if (credit <= 0) return false;
      if (extractDpSettlementCode(dp.dp_description)) return false;
      if (dp.canonical_isin !== isin) return false;
      if (!isCaNarration(dp.dp_description)) return false; // only genuine CA credits
      return true; // no date-window restriction
    });

    const usedInPass = new Set<string>();

    // ── Strategy A, Pass 1: exact per-row qty match ──
    const tempMatched: Array<{ row: ReconTxnPair; dp: ReconTxnPair }> = [];
    const tempUnmatched: ReconTxnPair[] = [];
    for (const row of rows) {
      const qty = Math.abs(Number(row.pf_qty) ?? 0);
      const dpMatch = candidatePool.find(dp =>
        !usedDp.has(dp.id) && !usedInPass.has(dp.id) &&
        Math.abs(Number(dp.dp_credit ?? 0) - qty) < 0.001
      );
      if (dpMatch) { usedInPass.add(dpMatch.id); tempMatched.push({ row, dp: dpMatch }); }
      else tempUnmatched.push(row);
    }

    // ── Strategy A, Pass 2: remaining rows sum to remaining DP total → group match ──
    if (tempUnmatched.length > 0) {
      const remainingDp    = candidatePool.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id));
      const remainingTotal = tempUnmatched.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const dpTotal        = remainingDp.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);

      if (remainingDp.length > 0 && Math.abs(dpTotal - remainingTotal) < 0.001) {
        // Clean group match — commit everything green
        remainingDp.forEach(dp => usedDp.add(dp.id));
        tempMatched.forEach(({ row, dp }) => {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set() });
        });
        bands.push({ pfRows: tempUnmatched, dpRow: remainingDp[0], dpRows: remainingDp, colour: 'green', isGroup: tempUnmatched.length > 1, sortDate: date, intradayIds: new Set() });
      } else if (remainingDp.length > 0 && dpTotal > 0) {
        // ── Strategy B: POOL DRAWDOWN → explicit aligned SUB-ENTRIES ──
        // Commit the exact matches from Pass 1 first (their own bands).
        tempMatched.forEach(({ row, dp }) => {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set() });
        });
        // Order both sides first-come-first-match (earliest DP credit first).
        const poolDps = [...remainingDp].sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
        poolDps.forEach(dp => usedDp.add(dp.id));

        // Count how many sub-entries each parent row/credit gets broken into,
        // so we can tell the reader "sub-entry of ...".
        const pfSubCount = new Map<string, number>();
        const dpSubCount = new Map<string, number>();

        // Two-pointer drawdown: walk PF rows, consuming DP credits in order.
        // Each contiguous covered chunk = one matched sub-entry pairing the PF
        // portion with the DP portion it drew from. Leftover PF = red shortfall.
        type Alloc = { qty: number; pfRow: ReconTxnPair; pfParentQty: number; pfSplit: boolean;
                       dpRow: ReconTxnPair | null; dpParentQty: number; dpSplit: boolean; matched: boolean };
        const allocations: Alloc[] = [];

        let di = 0;                                   // DP pointer
        let dpRemaining = di < poolDps.length ? Number(poolDps[di].dp_credit ?? 0) : 0;

        for (const row of tempUnmatched) {
          let pfRemaining = Math.abs(Number(row.pf_qty) ?? 0);
          const pfParentQty = pfRemaining;

          // Consume DP credit(s) to cover this PF row
          while (pfRemaining > 0.001 && di < poolDps.length && dpRemaining > 0.001) {
            const take = Math.min(pfRemaining, dpRemaining);
            allocations.push({
              qty: take,
              pfRow: row, pfParentQty, pfSplit: false, // pfSplit set later
              dpRow: poolDps[di], dpParentQty: Number(poolDps[di].dp_credit ?? 0), dpSplit: false,
              matched: true,
            });
            pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
            dpSubCount.set(poolDps[di].id, (dpSubCount.get(poolDps[di].id) ?? 0) + 1);
            pfRemaining -= take;
            dpRemaining -= take;
            if (dpRemaining <= 0.001) { di++; dpRemaining = di < poolDps.length ? Number(poolDps[di].dp_credit ?? 0) : 0; }
          }

          // Any remaining PF qty is a red shortfall sub-entry
          if (pfRemaining > 0.001) {
            allocations.push({
              qty: pfRemaining,
              pfRow: row, pfParentQty, pfSplit: false,
              dpRow: null, dpParentQty: 0, dpSplit: false,
              matched: false,
            });
            pfSubCount.set(row.id, (pfSubCount.get(row.id) ?? 0) + 1);
          }
        }

        // Fill in the split flags now that we know the per-parent counts.
        for (const a of allocations) {
          a.pfSplit = (pfSubCount.get(a.pfRow.id) ?? 1) > 1;
          a.dpSplit = a.dpRow ? (dpSubCount.get(a.dpRow.id) ?? 1) > 1 : false;
        }

        const anyMatched = allocations.some(a => a.matched);
        bands.push({
          pfRows: tempUnmatched,
          dpRow: poolDps[0] ?? null,
          dpRows: poolDps,
          colour: anyMatched ? 'green' : 'red',
          isGroup: true,
          sortDate: date,
          intradayIds: new Set(),
          bonusPool: { poolTotal: dpTotal, allocations },
        });
      } else {
        // No DP credits at all — commit exact matches green, rest red
        tempMatched.forEach(({ row, dp }) => {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set() });
        });
        tempUnmatched.forEach(row => {
          bands.push({ pfRows: [row], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: date, intradayIds: new Set() });
        });
      }
    } else {
      // All individually matched
      tempMatched.forEach(({ row, dp }) => {
        usedDp.add(dp.id);
        bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set() });
      });
    }
  }

  // ── Split Buy → "By Sub Division" credit matching ─────────────────────────
  // A stock split issues new shares under a new ISIN. On the PF side these are
  // "Split Buy" rows; on the DP side they appear as "By Sub Division(...)"
  // CREDITS. Match PURELY by quantity (grouped) — NO date window. The DP credit
  // may post on any date, even before the PF Split Buy date. Grouping: the sum
  // of all Split Buy qtys is matched against the sum of By Sub Division credits.
  if (splitBuyRows.length > 0) {
    const subDivCredits = dpPairs.filter(dp => {
      if (usedDp.has(dp.id)) return false;
      if (Number(dp.dp_credit ?? 0) <= 0) return false;
      // Match either "By Sub Division(...)" or "By Corporate Actions(...)" —
      // different depositories label a stock-split share issue either way.
      const desc = dp.dp_description ?? '';
      return /by\s+sub\s*division/i.test(desc) || /by\s+corporate\s+actions?/i.test(desc);
    });
    const splitTotal = splitBuyRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    const credTotal  = subDivCredits.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);

    if (subDivCredits.length > 0 && Math.abs(credTotal - splitTotal) < 0.001) {
      // Grouped total matches — link all Split Buy rows to all By Sub Division credits
      subDivCredits.forEach(dp => usedDp.add(dp.id));
      bands.push({
        pfRows: splitBuyRows,
        dpRow: subDivCredits[0],
        dpRows: subDivCredits,
        colour: 'green',
        isGroup: splitBuyRows.length > 1 || subDivCredits.length > 1,
        sortDate: splitBuyRows[0].pf_txn_date!,
        intradayIds: new Set(),
        splitSubDivMatch: true,
      });
    } else {
      // No grouped match — try matching each Split Buy row individually to a
      // By Sub Division credit of equal qty (still qty-only, no date).
      const stillUnmatched: ReconTxnPair[] = [];
      for (const row of splitBuyRows) {
        const qty = Math.abs(Number(row.pf_qty) ?? 0);
        const dp = subDivCredits.find(d => !usedDp.has(d.id) && Math.abs(Number(d.dp_credit ?? 0) - qty) < 0.001);
        if (dp) {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set(), splitSubDivMatch: true });
        } else {
          stillUnmatched.push(row);
        }
      }
      for (const row of stillUnmatched) {
        bands.push({ pfRows: [row], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set() });
      }
    }
  }

  // ── Split Sell → "To Sub Division" debit matching ─────────────────────────
  // A stock split cancels the old ISIN's shares. On the PF side these are
  // "Split Sell" rows; on the DP side they appear as "To Sub Division(...)"
  // DEBITS. Match PURELY by quantity — NO date window (mirrors Split Buy).
  // Company scope is implicit (buildMatchBands runs per-company). Grouped total
  // first; then first-come-first-match individual matching for the remainder.
  if (splitSellRows.length > 0) {
    const subDivDebits = dpPairs.filter(dp => {
      if (usedDp.has(dp.id)) return false;
      if (Number(dp.dp_debit ?? 0) <= 0) return false;
      // Match either "To Sub Division(...)" or "To Corporate Actions(...)".
      const desc = dp.dp_description ?? '';
      return /to\s+sub\s*division/i.test(desc) || /to\s+corporate\s+actions?/i.test(desc);
    });
    const splitSellTotal = splitSellRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
    const debitTotal     = subDivDebits.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);

    if (subDivDebits.length > 0 && Math.abs(debitTotal - splitSellTotal) < 0.001) {
      // Grouped total matches — link all Split Sell rows to all To Sub Division debits
      subDivDebits.forEach(dp => usedDp.add(dp.id));
      bands.push({
        pfRows: splitSellRows,
        dpRow: subDivDebits[0],
        dpRows: subDivDebits,
        colour: 'green',
        isGroup: splitSellRows.length > 1 || subDivDebits.length > 1,
        sortDate: splitSellRows[0].pf_txn_date!,
        intradayIds: new Set(),
        splitSubDivMatch: true,
      });
    } else {
      // First-come-first-match: each Split Sell row consumes the first available
      // To Sub Division debit of equal qty (qty-only, no date).
      const sellUnmatched: ReconTxnPair[] = [];
      for (const row of splitSellRows) {
        const qty = Math.abs(Number(row.pf_qty) ?? 0);
        const dp = subDivDebits.find(d => !usedDp.has(d.id) && Math.abs(Number(d.dp_debit ?? 0) - qty) < 0.001);
        if (dp) {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set(), splitSubDivMatch: true });
        } else {
          sellUnmatched.push(row);
        }
      }
      for (const row of sellUnmatched) {
        bands.push({ pfRows: [row], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set() });
      }
    }
  }

  // ── Offmarket matching ─────────────────────────────────────────────────────
  // Offmarket trades (pf_source = "Offmarket") settle bilaterally against an
  // external bank/DP. Matching is by QUANTITY (single row or grouped-sum),
  // subject to the rules below.
  //
  //   Offmarket BUY / buy-like        → external bank DP CREDIT
  //   Offmarket Transfer Out / sell   → external bank DP DEBIT
  //
  // A candidate DP entry MUST:
  //   • have NO settlement code (extractDpSettlementCode === null) — a bank ref
  //     like "By ICICI BANK LIMITED/64606465" qualifies (646… is a bank ref,
  //     not a NORMAL/ code)
  //   • NOT be an "Axis Securities" entry (Rule 3 — must be an external bank
  //     such as ICICI / HDFC / etc.)
  //   • NOT be an SLB entry (Rule 2 — securities lending is a different
  //     mechanism and must never be an offmarket target)
  //   • NOT be an invisible internal movement (pledge, or eDIS/Block Mechanism)
  //   • post ON OR AFTER the PF trade date (Rule 1 — enforced per DP entry in
  //     matchOffmarket; delivery never predates the purchase; no upper window)
  // ── SDO: Same Day OffMarket ──────────────────────────────────────────────────
  //
  // Business scenario (screenshot: INE02PY01013):
  //   PF: source=Offmarket (or null), BUY, date=D
  //   DP: "By CM EUREKA STOCK AND SHARE BROKING SERVICES LTD.,T+1 ¿ NORMAL/2023186"
  //       credit=100, date=D  (same day, has settlement code → blocked by eligibleDp)
  //
  // Rule:
  //   1. PF source = "offmarket" or "null" (case-insensitive), any BUY-like type.
  //   2. DP credit entry whose description is NOT "By CM Axis Securities..." (any
  //      other clearing member / broker is accepted — Eureka, ICICI, NSE CM, etc.)
  //   3. PF date == DP date (strictly same day).
  //   4. Settlement code: present or absent — irrelevant.
  //   5. Qty match: single row exact OR grouped subset sum (PF rows, DP rows, or both).
  //   6. Result flagged as sdoMatch=true so the UI can badge it "SDO".
  //
  // Runs BEFORE the existing offmarket flush so same-day coded entries are claimed
  // here and not left unmatched by the no-code-only eligibleDp filter.

  const sdoClaimed = new Set<string>(); // PF row ids claimed by the SDO passes (shared scope)
  if (offmarketRows.length > 0 || sdoSellRows.length > 0) {
    // Collect still-unclaimed SDO-eligible DP credits (same-isin, credit > 0, not Axis CM).
    // Settlement code may be present — we deliberately do NOT filter on extractDpSettlementCode.
    const sdoEligibleDp = (dp: ReconTxnPair): boolean => {
      if (usedDp.has(dp.id)) return false;
      if (Number(dp.dp_credit ?? 0) <= 0) return false;
      if (isPledgeDescription(dp.dp_description)) return false;
      if (isEdisDpLine(dp.dp_description)) return false;
      if (/\bSLB\b/i.test(dp.dp_description ?? '')) return false;
      // Offmarket/null BUY rule: match against ANY credit — By CM Axis, any other
      // broker, By Inter Depository transfer — with OR without a settlement code.
      // So we deliberately do NOT exclude Axis CM entries or coded entries here.
      return true;
    };

    const qtyOf = (r: ReconTxnPair) => Math.abs(Number(r.pf_qty) ?? 0);

    // Group offmarket/null BUY rows by (isin) ONLY — NO same-day requirement.
    // Offmarket buys are entered manually; the depository credit can post on any
    // date, even BEFORE the PF entry date. So we group all offmarket buys of an
    // ISIN together and match against all its credits regardless of date.
    const sdoGroupMap = new Map<string, ReconTxnPair[]>();
    for (const r of offmarketRows) {
      const tt = (r.pf_txn_type ?? '').toUpperCase().trim();
      const isSell = SELL_LIKE_TYPES.has(tt) || /transfer\s*out|sell/i.test(tt);
      if (isSell) continue; // SDO applies to BUY-like rows only for now
      if (Math.abs(qtyOf(r)) === 0) continue;
      const key = `${r.canonical_isin}`;
      if (!sdoGroupMap.has(key)) sdoGroupMap.set(key, []);
      sdoGroupMap.get(key)!.push(r);
    }


    for (const [isin, pfGroup] of sdoGroupMap.entries()) {
      const pfDate = pfGroup[0]?.pf_txn_date ?? '';

      // Candidate DP credits: same ISIN, ANY date (before or after PF), not yet used.
      const dpCandidates = dpPairs
        .filter(dp => dp.canonical_isin === isin && sdoEligibleDp(dp))
        .sort((a, b) => Number(b.dp_credit ?? 0) - Number(a.dp_credit ?? 0)); // largest first

      if (dpCandidates.length === 0) continue;

      // Try 1: one-to-one exact match (each PF row → single same-qty DP credit).
      // COMMIT partial matches — a group of [2054, 2946, 3000] where only 3000
      // has no groupmate-blocking issue must still match its own 3000 credit.
      // Only leftover rows fall through to group/subset matching.
      const usedInSDO = new Set<string>();
      let buyRemaining: ReconTxnPair[] = [];
      for (const pf of pfGroup) {
        const pfQty = qtyOf(pf);
        const dp = dpCandidates.find(d => !usedInSDO.has(d.id) && !usedDp.has(d.id) && Math.abs(Number(d.dp_credit ?? 0) - pfQty) < 0.001);
        if (dp) {
          usedInSDO.add(dp.id);
          usedDp.add(dp.id);
          sdoClaimed.add(pf.id);
          bands.push({ pfRows: [pf], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: pf.pf_txn_date ?? pfDate, intradayIds: new Set(), sdoMatch: true } as any);
        } else {
          buyRemaining.push(pf);
        }
      }
      if (buyRemaining.length === 0) continue; // all rows matched 1-to-1

      const remTotal = buyRemaining.reduce((s, r) => s + qtyOf(r), 0);
      const remDpCandidates = dpCandidates.filter(d => !usedInSDO.has(d.id) && !usedDp.has(d.id));

      // Try 2: remaining rows total → one DP credit of equal total qty.
      const singleDpTotal = remDpCandidates.find(dp => Math.abs(Number(dp.dp_credit ?? 0) - remTotal) < 0.001);
      if (singleDpTotal) {
        usedDp.add(singleDpTotal.id);
        buyRemaining.forEach(r => sdoClaimed.add(r.id));
        bands.push({ pfRows: buyRemaining, dpRow: singleDpTotal, dpRows: [singleDpTotal], colour: 'green', isGroup: buyRemaining.length > 1, sortDate: buyRemaining[0].pf_txn_date ?? pfDate, intradayIds: new Set(), sdoMatch: true } as any);
        continue;
      }

      // Try 3: subset-sum of remaining DP credits = remTotal.
      const dpQtys = remDpCandidates.map(d => Number(d.dp_credit ?? 0));
      const n = Math.min(remDpCandidates.length, 20);
      let bestSubset: number[] | null = null;
      outer: for (let mask = 1; mask < (1 << n); mask++) {
        let sum = 0;
        const indices: number[] = [];
        for (let bit = 0; bit < n; bit++) {
          if (mask & (1 << bit)) { sum += dpQtys[bit]; indices.push(bit); }
          if (sum > remTotal + 0.001) continue outer;
        }
        if (Math.abs(sum - remTotal) < 0.001) {
          if (!bestSubset || indices.length < bestSubset.length) bestSubset = indices;
        }
      }
      if (bestSubset) {
        const matchedDps = bestSubset.map(i => remDpCandidates[i]);
        matchedDps.forEach(dp => usedDp.add(dp.id));
        buyRemaining.forEach(r => sdoClaimed.add(r.id));
        bands.push({ pfRows: buyRemaining, dpRow: matchedDps[0], dpRows: matchedDps, colour: 'green', isGroup: buyRemaining.length > 1 || matchedDps.length > 1, sortDate: buyRemaining[0].pf_txn_date ?? pfDate, intradayIds: new Set(), sdoMatch: true } as any);
      }
    }

    // ── SDO SELL side: Transfer Out / Sell-like with source=null/offmarket ──
    // Match against DP DEBIT entries (To ...) that are NOT Axis CM entries.
    // DP date: no restriction — can be before or after PF date.
    // Qty: individual exact or grouped subset sum.
    const isAxisCmDebitEntry = (desc: string | null | undefined): boolean => {
      if (!desc) return false;
      return /to\s+cm\s+axis/i.test(desc) || /axis\s+securities.*limited.*normal/i.test(desc);
    };

    const sdoSellEligibleDp = (dp: ReconTxnPair): boolean => {
      if (usedDp.has(dp.id)) return false;
      if (Number(dp.dp_debit ?? 0) <= 0) return false;
      if (isPledgeDescription(dp.dp_description)) return false;
      // isEdisDpLine blocks anything with "edis" or "block mechanism".
      // But external broker deliveries like "To EUREKA STOCK.../ eDIS /Block Mechanism NORMAL/..."
      // are real movements we MUST match for SDO. Only block AXIS internal eDIS entries.
      const isInternalEdis = (desc: string | null | undefined): boolean => {
        if (!desc) return false;
        return /to\s+axis\s+securities/i.test(desc) && isEdisDpLine(desc);
      };
      if (isInternalEdis(dp.dp_description)) return false;
      if (/SLB/i.test(dp.dp_description ?? '')) return false;
      if (isAxisCmDebitEntry(dp.dp_description)) return false;
      return true;
    };

    // Group sdoSellRows by (isin) — no same-day requirement for sell side.
    const sdoSellGroupMap = new Map<string, ReconTxnPair[]>();
    for (const r of sdoSellRows) {
      if (Math.abs(qtyOf(r)) === 0) continue;
      const isin = r.canonical_isin ?? '';
      if (!sdoSellGroupMap.has(isin)) sdoSellGroupMap.set(isin, []);
      sdoSellGroupMap.get(isin)!.push(r);
    }

    for (const [isin, pfGroup] of sdoSellGroupMap.entries()) {
      const totalPfQty = pfGroup.reduce((s, r) => s + qtyOf(r), 0);

      const dpCandidates = dpPairs
        .filter(dp => dp.canonical_isin === isin && sdoSellEligibleDp(dp))
        .sort((a, b) => Number(b.dp_debit ?? 0) - Number(a.dp_debit ?? 0));

      if (dpCandidates.length === 0) {
        // No DP match — leave unmatched
        pfGroup.forEach(r => bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() }));
        pfGroup.forEach(r => sdoClaimed.add(r.id)); // prevent double-band
        continue;
      }

      // Try 1: 1-to-1 exact match per PF row — COMMIT partial matches.
      // Previously we only committed if ALL rows matched 1-to-1; a group of
      // [3000, 4000] where only 3000 has a matching debit would discard the
      // 3000 match too. Now each row that finds its own exact debit is matched
      // immediately, and only the leftover rows fall through to group matching.
      const usedInSDOSell = new Set<string>();
      let sellRemaining: ReconTxnPair[] = [];
      for (const pf of pfGroup) {
        const pfQty = qtyOf(pf);
        const dp = dpCandidates.find(d => !usedInSDOSell.has(d.id) && !usedDp.has(d.id) && Math.abs(Number(d.dp_debit ?? 0) - pfQty) < 0.001);
        if (dp) {
          usedInSDOSell.add(dp.id);
          usedDp.add(dp.id);
          sdoClaimed.add(pf.id);
          bands.push({ pfRows: [pf], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: pf.pf_txn_date!, intradayIds: new Set(), sdoMatch: true } as any);
        } else {
          sellRemaining.push(pf);
        }
      }
      if (sellRemaining.length === 0) continue; // all rows matched 1-to-1

      // Remaining rows: recompute total + remaining eligible debits.
      const remTotal = sellRemaining.reduce((s, r) => s + qtyOf(r), 0);
      const remDpCandidates = dpCandidates.filter(d => !usedInSDOSell.has(d.id) && !usedDp.has(d.id));

      // Try 2: remaining rows total → one DP debit of equal total qty
      const singleDpTotal = remDpCandidates.find(dp => Math.abs(Number(dp.dp_debit ?? 0) - remTotal) < 0.001);
      if (singleDpTotal) {
        usedDp.add(singleDpTotal.id);
        sellRemaining.forEach(r => sdoClaimed.add(r.id));
        bands.push({ pfRows: sellRemaining, dpRow: singleDpTotal, dpRows: [singleDpTotal], colour: 'green', isGroup: sellRemaining.length > 1, sortDate: sellRemaining[0].pf_txn_date!, intradayIds: new Set(), sdoMatch: true } as any);
        continue;
      }

      // Try 3: subset-sum of remaining DP debits = remTotal
      const dpQtys = remDpCandidates.map(d => Number(d.dp_debit ?? 0));
      const n3 = Math.min(remDpCandidates.length, 20);
      let bestSubsetSell: number[] | null = null;
      outerSell: for (let mask = 1; mask < (1 << n3); mask++) {
        let sum = 0; const indices: number[] = [];
        for (let bit = 0; bit < n3; bit++) {
          if (mask & (1 << bit)) { sum += dpQtys[bit]; indices.push(bit); }
          if (sum > remTotal + 0.001) continue outerSell;
        }
        if (Math.abs(sum - remTotal) < 0.001) {
          if (!bestSubsetSell || indices.length < bestSubsetSell.length) bestSubsetSell = indices;
        }
      }
      if (bestSubsetSell) {
        const matchedDps = bestSubsetSell.map(i => remDpCandidates[i]);
        matchedDps.forEach(dp => usedDp.add(dp.id));
        sellRemaining.forEach(r => sdoClaimed.add(r.id));
        bands.push({ pfRows: sellRemaining, dpRow: matchedDps[0], dpRows: matchedDps, colour: 'green', isGroup: sellRemaining.length > 1 || matchedDps.length > 1, sortDate: sellRemaining[0].pf_txn_date!, intradayIds: new Set(), sdoMatch: true } as any);
      } else {
        // Try 4: NET match — debits minus credits across ALL unclaimed DP entries for
        // this ISIN equal the PF Transfer Out qty.
        // e.g. debit=500 (To EUREKA eDIS NORMAL/...) - credit=125 (By CM EUREKA TRADE FOR TRADE/...)
        //      net = 375 = Transfer Out qty ✓
        // Accept any DP entry (credit or debit) that is not internal Axis / pledge / SLB.
        const sdoNetEligibleDp = (dp: ReconTxnPair): boolean => {
          if (usedDp.has(dp.id)) return false;
          const hasMoney = Number(dp.dp_debit ?? 0) > 0 || Number(dp.dp_credit ?? 0) > 0;
          if (!hasMoney) return false;
          if (isPledgeDescription(dp.dp_description)) return false;
          if (/SLB/i.test(dp.dp_description ?? '')) return false;
          // Block internal Axis eDIS/Block Mechanism entries (not external broker ones)
          const isInternalAxisEdis = (desc: string | null | undefined) =>
            /to\s+axis\s+securities/i.test(desc ?? '') && isEdisDpLine(desc);
          if (isInternalAxisEdis(dp.dp_description)) return false;
          if (isAxisCmDebitEntry(dp.dp_description)) return false;
          return true;
        };

        const netPool = dpPairs.filter(dp => dp.canonical_isin === isin && sdoNetEligibleDp(dp));
        if (netPool.length > 0) {
          const netDr = netPool.reduce((s, d) => s + Number(d.dp_debit ?? 0), 0);
          const netCr = netPool.reduce((s, d) => s + Number(d.dp_credit ?? 0), 0);
          const netQty = netDr - netCr;
          if (Math.abs(netQty - totalPfQty) < 0.001) {
            netPool.forEach(dp => usedDp.add(dp.id));
            pfGroup.forEach(r => sdoClaimed.add(r.id));
            const primaryDp = netPool.find(d => Number(d.dp_debit ?? 0) > 0) ?? netPool[0];
            bands.push({ pfRows: pfGroup, dpRow: primaryDp, dpRows: netPool, colour: 'green', isGroup: true, sortDate: pfGroup[0].pf_txn_date!, intradayIds: new Set(), sdoMatch: true } as any);
          } else {
            // Still no match — show unmatched
            pfGroup.forEach(r => { sdoClaimed.add(r.id); bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() }); });
          }
        } else {
          pfGroup.forEach(r => { sdoClaimed.add(r.id); bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() }); });
        }
      }
    }
    // ── end SDO SELL ──

    // Remove SDO-claimed rows from offmarketRows so existing flush skips them.
    for (let i = offmarketRows.length - 1; i >= 0; i--) {
      if (sdoClaimed.has(offmarketRows[i].id)) offmarketRows.splice(i, 1);
    }
  }
  // ── end SDO ──────────────────────────────────────────────────────────────────

  if (offmarketRows.length > 0) {
    const omBuys:  ReconTxnPair[] = [];
    const omSells: ReconTxnPair[] = [];
    for (const r of offmarketRows) {
      // Defensive: never let a zero-qty row (e.g. a dividend) ride along in a
      // subset-sum — it would falsely join any group without changing the total.
      // Such a row (should be none after the DIV guard) is shown unmatched.
      if (Math.abs(Number(r.pf_qty) ?? 0) === 0) {
        bands.push({ pfRows: [r], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: r.pf_txn_date!, intradayIds: new Set() });
        continue;
      }
      // Skip PF rows already claimed by the relaxed SDO BUY pass above.
      if (sdoClaimed.has(r.id)) continue;
      const tt = (r.pf_txn_type ?? '').toUpperCase().trim();
      const isSell = SELL_LIKE_TYPES.has(tt) || /transfer\s*out|sell/i.test(tt);
      (isSell ? omSells : omBuys).push(r);
    }

    const isSlbEntry = (desc: string | null | undefined) =>
      /\bSLB\b/i.test(desc ?? '');

    const eligibleDp = (dp: ReconTxnPair, wantCredit: boolean): boolean => {
      if (usedDp.has(dp.id)) return false;
      const amt = wantCredit ? Number(dp.dp_credit ?? 0) : Number(dp.dp_debit ?? 0);
      if (amt <= 0) return false;
      if (isPledgeDescription(dp.dp_description)) return false;   // invisible internal op
      if (isEdisDpLine(dp.dp_description)) return false;          // invisible eDIS/block
      if (isSlbEntry(dp.dp_description)) return false;            // never match SLB
      // "Axis Securities" entries ARE allowed (grouped PF buys can match a
      // "By AXIS SECURITIES…" credit dated on/after the PF), as long as it is
      // NOT SLB. Only SLB is excluded by counterparty name now.
      if (extractDpSettlementCode(dp.dp_description) !== null) return false; // must have NO settlement code
      return true;
    };

    // DP-DRIVEN matching: walk the eligible DP entries in the order they COME
    // (earliest date first) and match each to the offmarket rows by quantity —
    // a single row or a group summing to it, always preferring the earliest
    // available rows. There is NO "singles pass then groups pass"; single and
    // group are equal candidates for the SAME DP entry, resolved DP-by-DP so
    // the DP order is never displaced.
    //
    // Rule 1 (date direction): the DP entry must post ON OR AFTER the PF trade
    // date — the depository delivery of an offmarket buy happens on/after the
    // purchase, never before. So for each DP entry only PF rows whose date is
    // <= the DP date are eligible to match it. There is otherwise NO upper date
    // window: the DP can be any number of days later.
    const matchOffmarket = (
      pfRows: ReconTxnPair[],
      wantCredit: boolean,
      dpQtyOf: (dp: ReconTxnPair) => number,
    ) => {
      const pool = dpPairs
        .filter(dp => eligibleDp(dp, wantCredit))
        .sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? '')); // as it comes
      let remaining = [...pfRows]
        .sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? '')); // earliest first
      const qtyOf = (r: ReconTxnPair) => Math.abs(Number(r.pf_qty) ?? 0);

      for (const dp of pool) {
        if (usedDp.has(dp.id)) continue;
        const dpQty = dpQtyOf(dp);
        const dpDate = dp.dp_txn_date ?? '';

        // Rule 1: eligible PF rows are those dated on/before this DP entry.
        const eligIdx: number[] = [];
        for (let i = 0; i < remaining.length; i++) {
          const pfDate = remaining[i].pf_txn_date ?? '';
          if (pfDate && dpDate && pfDate <= dpDate) eligIdx.push(i);
        }
        const elig = eligIdx.map(i => remaining[i]);
        let bestElig: number[] | null = null; // indices into `elig`

        // (1) ALL eligible rows sum to dpQty → take them all. Dominant case: a
        //     whole history of buys settling into one bank credit. Any row count.
        const totalElig = elig.reduce((s, r) => s + qtyOf(r), 0);
        if (elig.length > 0 && Math.abs(totalElig - dpQty) < 0.001) {
          bestElig = elig.map((_, k) => k);
        }

        // (2) Earliest running group (prefix) sums to dpQty → take earliest N.
        if (!bestElig) {
          let run = 0;
          for (let k = 0; k < elig.length; k++) {
            run += qtyOf(elig[k]);
            if (run > dpQty + 0.001) break;
            if (Math.abs(run - dpQty) < 0.001) { bestElig = Array.from({ length: k + 1 }, (_, j) => j); break; }
          }
        }

        // (3) General exact subset-sum with NO row-count limit — polynomial DP
        //     over quantity, preferring to INCLUDE the earliest rows.
        if (!bestElig) {
          const qtys = elig.map(qtyOf);
          const target = Math.round(dpQty);
          const n = qtys.length;
          const allInt = Number.isInteger(dpQty) && qtys.every(q => Number.isInteger(q));
          if (allInt && target > 0 && n > 0 && (target + 1) * (n + 1) <= 30_000_000) {
            const reach: Uint8Array[] = new Array(n + 1);
            reach[n] = new Uint8Array(target + 1); reach[n][0] = 1;
            for (let i = n - 1; i >= 0; i--) {
              const prev = reach[i + 1];
              const cur = prev.slice();
              const q = qtys[i];
              if (q <= target) for (let s = target - q; s >= 0; s--) if (prev[s]) cur[s + q] = 1;
              reach[i] = cur;
            }
            if (reach[0][target]) {
              const chosen: number[] = [];
              let rem = target;
              for (let i = 0; i < n && rem > 0; i++) {
                const q = qtys[i];
                if (q <= rem && reach[i + 1][rem - q]) { chosen.push(i); rem -= q; }
              }
              if (rem === 0) bestElig = chosen;
            }
          }
        }

        if (bestElig) {
          usedDp.add(dp.id);
          const chosenIdx = bestElig.map(k => eligIdx[k]);  // → indices into `remaining`
          const rows = chosenIdx.map(i => remaining[i]);
          bands.push({ pfRows: rows, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: rows.length > 1, sortDate: rows[0].pf_txn_date!, intradayIds: new Set(), offmarketMatch: true });
          const removeSet = new Set(chosenIdx);
          remaining = remaining.filter((_, i) => !removeSet.has(i));
        }
      }

      // ── Reverse direction: ONE PF row ↔ a GROUP of DP entries summing to it ──
      // e.g. PF CMOTS buy 5,200 ↔ DP "By HDFC…" 3,000 + "By KOTAK…" 2,200. The
      // DP entries must be dated ON/AFTER the PF row. Applied to PF rows still
      // unmatched by the group-of-PF-to-one-DP pass above.
      if (remaining.length > 0) {
        const stillRemaining: ReconTxnPair[] = [];
        for (const row of remaining) {
          const pfDate = row.pf_txn_date ?? '';
          const target = Math.round(qtyOf(row));
          // Eligible DP entries: unused, pass eligibleDp, dated on/after the PF.
          const dpCands = pool
            .filter(dp => !usedDp.has(dp.id) && (dp.dp_txn_date ?? '') >= pfDate)
            .sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
          const dqtys = dpCands.map(dpQtyOf);
          const n = dqtys.length;
          const allInt = Number.isInteger(qtyOf(row)) && dqtys.every(q => Number.isInteger(q));
          let chosen: number[] | null = null;
          if (allInt && target > 0 && n > 0 && (target + 1) * (n + 1) <= 30_000_000) {
            const reach: Uint8Array[] = new Array(n + 1);
            reach[n] = new Uint8Array(target + 1); reach[n][0] = 1;
            for (let i = n - 1; i >= 0; i--) {
              const prev = reach[i + 1];
              const cur = prev.slice();
              const q = dqtys[i];
              if (q <= target) for (let s = target - q; s >= 0; s--) if (prev[s]) cur[s + q] = 1;
              reach[i] = cur;
            }
            if (reach[0][target]) {
              const ch: number[] = [];
              let rem = target;
              for (let i = 0; i < n && rem > 0; i++) {
                const q = dqtys[i];
                if (q <= rem && reach[i + 1][rem - q]) { ch.push(i); rem -= q; }
              }
              if (rem === 0) chosen = ch;
            }
          }
          if (chosen && chosen.length > 0) {
            const dpGroup = chosen.map(i => dpCands[i]);
            for (const d of dpGroup) usedDp.add(d.id);
            bands.push({ pfRows: [row], dpRow: dpGroup[0], dpRows: dpGroup, colour: 'green', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set(), offmarketMatch: true });
          } else {
            stillRemaining.push(row);
          }
        }
        remaining = stillRemaining;
      }

      for (const row of remaining) {
        bands.push({ pfRows: [row], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set() });
      }
    };

    // ── Same-date + same-qty offmarket exception (allows "Axis Securities") ──
    // If an offmarket PF row has a DP entry on the SAME date with the SAME
    // quantity, match them even if the DP description is "Axis Securities" — a
    // strong same-date+same-qty signal overrides Rule 3 (external-bank-only).
    // SLB is still NEVER matched, and pledge/eDIS/coded entries are still
    // excluded. Runs BEFORE the general pass so it takes priority; matched PF
    // rows are removed from the general pool. Flagged OFM.
    const matchSameDateAxis = (pfRows: ReconTxnPair[], wantCredit: boolean, dpQtyOf: (dp: ReconTxnPair) => number): ReconTxnPair[] => {
      const leftover: ReconTxnPair[] = [];
      for (const row of pfRows) {
        const qty = Math.abs(Number(row.pf_qty) ?? 0);
        const pfDate = row.pf_txn_date ?? '';
        const dp = dpPairs.find(d => {
          if (usedDp.has(d.id)) return false;
          const amt = wantCredit ? Number(d.dp_credit ?? 0) : Number(d.dp_debit ?? 0);
          if (amt <= 0) return false;
          if (isPledgeDescription(d.dp_description)) return false;
          if (isEdisDpLine(d.dp_description)) return false;
          if (isSlbEntry(d.dp_description)) return false;                        // SLB never matches
          if (extractDpSettlementCode(d.dp_description) !== null) return false;  // no coded settlement
          if ((d.dp_txn_date ?? '') !== pfDate) return false;                    // SAME date
          return Math.abs(dpQtyOf(d) - qty) < 0.001;                             // SAME qty
        });
        if (dp) {
          usedDp.add(dp.id);
          bands.push({ pfRows: [row], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: row.pf_txn_date!, intradayIds: new Set(), offmarketMatch: true });
        } else {
          leftover.push(row);
        }
      }
      return leftover;
    };
    const omBuysLeft  = matchSameDateAxis(omBuys,  true,  dp => Number(dp.dp_credit ?? 0));
    const omSellsLeft = matchSameDateAxis(omSells, false, dp => Number(dp.dp_debit  ?? 0));

    matchOffmarket(omBuysLeft,  true,  dp => Number(dp.dp_credit ?? 0));
    matchOffmarket(omSellsLeft, false, dp => Number(dp.dp_debit  ?? 0));
  }

  // ── Sell buy-back matching ──────────────────────────────────────────────────
  // A buy-back / tender offer: shares are tendered (a DP "To … BUY-BACK/<code>"
  // debit), the company accepts some (the PF SELL) and returns the rest (a DP
  // "By Tender Offer Cancellation …" credit). So:
  //     buyback debit qty = PF sell qty + tender-cancellation credit qty
  // The PF SELL and the buy-back debit share the same settlement code. All three
  // entries reconcile to one net movement and are shown green with a $ flag.
  //
  //   Screenshot 2: To BUY-BACK/2026071 debit 700 = SELL 685 + Tender Cancel 15
  //   Screenshot 1: To BUY-BACK/2026076 debit 225 = SELL 215 + Tender Cancel 10
  for (const sell of buyBackSells) {
    const code = (sell.pf_particulars ?? '').trim();
    const sellQty = Math.abs(Number(sell.pf_qty) ?? 0);

    // The DP buy-back debit: match by code if available, otherwise by qty arithmetic.
    const debit = dpPairs.find(dp => {
      if (!usedDp.has(dp.id) && Number(dp.dp_debit ?? 0) > 0 && extractBuyBackCode(dp.dp_description) !== null) {
        if (code) return extractBuyBackCode(dp.dp_description) === code;
        // No code: accept any BUY-BACK debit where debit == sell OR debit == sell + cancel
        const debitQty = Number(dp.dp_debit ?? 0);
        if (Math.abs(debitQty - sellQty) < 0.001) return true;
        const shortfall = debitQty - sellQty;
        if (shortfall <= 0) return false;
        return dpPairs.some(c =>
          !usedDp.has(c.id) &&
          Number(c.dp_credit ?? 0) > 0 &&
          isTenderCancellation(c.dp_description) &&
          Math.abs(Number(c.dp_credit ?? 0) - shortfall) < 0.001
        );
      }
      return false;
    });

    if (!debit) {
      bands.push({ pfRows: [sell], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: sell.pf_txn_date!, intradayIds: new Set() });
      continue;
    }

    const debitQty = Number(debit.dp_debit ?? 0);
    const shortfall = debitQty - sellQty; // shares returned via tender cancellation

    // Optional tender-offer-cancellation credit that accounts for the shortfall.
    let cancel: ReconTxnPair | null = null;
    if (shortfall > 0.001) {
      cancel = dpPairs.find(dp =>
        !usedDp.has(dp.id) && Number(dp.dp_credit ?? 0) > 0 &&
        isTenderCancellation(dp.dp_description) &&
        Math.abs(Number(dp.dp_credit ?? 0) - shortfall) < 0.001) ?? null;
    }

    // Match when: debit == sell (full acceptance), OR debit == sell + cancel.
    if (shortfall <= 0.001 || cancel) {
      usedDp.add(debit.id);
      const dpRows = [debit];
      if (cancel) { usedDp.add(cancel.id); dpRows.push(cancel); }
      const isBbcCredit = cancel != null && /buy[\s-]?back\/\d+/i.test(cancel.dp_description ?? '');
      bands.push({
        pfRows: [sell], dpRow: debit, dpRows,
        colour: 'green', isGroup: false, sortDate: sell.pf_txn_date!,
        intradayIds: new Set(), sellBuyBack: true, bbcMatch: isBbcCredit,
      });
    } else {
      // Arithmetic doesn't close (no matching cancellation) — leave red.
      bands.push({ pfRows: [sell], dpRow: null, dpRows: [], colour: 'red', isGroup: false, sortDate: sell.pf_txn_date!, intradayIds: new Set() });
    }
  }

  // Accumulator for all intraday BUY row IDs (populated during SELL flush,
  // applied during BUY flush when BUY bands are actually built).
  const allIntradayBuyIds = new Set<string>();

  // Flush BUY groups — re-group by settlement code so each unique code gets
  // its own DP match. One settlement code can map to MULTIPLE DP credit entries
  // (eDIS mechanism splits a single settlement — e.g. 1600 shares becomes two
  // entries of 1496 + 104 for the same NORMAL/code). We sum ALL DP credits
  // with the same code and compare to the PF total for that code.
  const buyByCode = new Map<string, { code: string | null; date: string; rows: ReconTxnPair[] }>();
  for (const { rows } of buyGroupMap.values()) {
    for (const row of rows) {
      const code = row.pf_particulars?.trim() || null;
      // Coded buys: key = code only (settlement date can differ from trade date,
      // and one code's rows belong together). NOCODE buys: key MUST include the
      // trade date — otherwise no-code buys from unrelated dates (e.g. a 2023
      // BUY and a 2024 Split Buy) would collapse into one bogus group and their
      // summed qty would never match the right DP credit.
      const mapKey = code ?? `NOCODE|${row.pf_txn_date ?? ''}`;
      if (!buyByCode.has(mapKey)) buyByCode.set(mapKey, { code, date: row.pf_txn_date!, rows: [] });
      buyByCode.get(mapKey)!.rows.push(row);
    }
  }


  // Flush SELL groups — intraday-aware, with date+qty fallback.
  //
  // Step A: Re-group all sells by settlement code (no date in key).
  // Step B: Within each code group, detect intraday sells by finding buys
  //         on the same date with the same qty (same code). Remove them —
  //         intraday sells don't need a DP debit match.
  // Step C: Match remaining non-intraday sells:
  //   1. Primary: by settlement code (sum PF = sum DP debits with same code)
  //   2. Fallback: by date + qty (handles cases where DP code ≠ PF code,
  //      e.g. SELL 17+18=35 with PF code 2023284 matches DP debit 35 on
  //      same date even though DP uses code 2023204)
  const sellByCode = new Map<string, { code: string | null; date: string; rows: ReconTxnPair[] }>();
  for (const { rows } of sellGroupMap.values()) {
    for (const row of rows) {
      const code = row.pf_particulars?.trim() || null;
      const mapKey = code ?? `NOCODE_SELL|${row.pf_txn_date ?? ''}`;
      if (!sellByCode.has(mapKey)) sellByCode.set(mapKey, { code, date: row.pf_txn_date!, rows: [] });
      sellByCode.get(mapKey)!.rows.push(row);
    }
  }

  // Pre-index DP debits by ISIN+code.
  // eDIS/Block Mechanism lines are deduplicated per group (identical pairs
  // from double-booking are counted once). Non-eDIS lines are never deduped.
  const dpDebitByCode = new Map<string, ReconTxnPair[]>();
  for (const dp of dpPairs) {
    const debit = Number(dp.dp_debit ?? 0);
    if (debit <= 0) continue;
    const dpCode = extractDpSettlementCode(dp.dp_description);
    if (!dpCode) continue;
    const dpKey = `${dp.canonical_isin}|${dpCode}`;
    if (!dpDebitByCode.has(dpKey)) dpDebitByCode.set(dpKey, []);
    dpDebitByCode.get(dpKey)!.push(dp);
  }
  // Apply eDIS dedup per group
  for (const [k, rows] of dpDebitByCode.entries()) dpDebitByCode.set(k, dedupeEdisLines(rows));

  for (const { code: pfCode, date: sellDate, rows: allSellRows } of sellByCode.values()) {
    // Skip rows already handled by the net matching pass
    const sellRows = allSellRows.filter(r => !usedDp.has(r.id + '__pfUsed'));
    if (sellRows.length === 0) continue;
    // ── Step B: Detect and separate intraday sells ────────────────────────
    // IMPORTANT: Intraday BUY code may differ from SELL code (different settlement
    // numbers for the same day's trades). We look for BUYs on the same DATE across
    // ALL codes, not just the matching code.
    const usedBuyIdx     = new Set<number>();
    const nonIntradaySells: ReconTxnPair[] = [];
    const intradaySellIds = new Set<string>();

    // Build date → all BUY rows (across all codes) that are not yet used
    const allBuyRowsFlat: ReconTxnPair[] = [];
    for (const { rows: brows } of buyByCode.values()) {
      for (const r of brows) {
        if (!usedDp.has(r.id + '__pfUsed') && !allIntradayBuyIds.has(r.id)) {
          allBuyRowsFlat.push(r);
        }
      }
    }
    const buyRowsForCode = allBuyRowsFlat; // all available BUYs for intraday check

    const buysByDate = new Map<string, { indices: number[]; total: number }>();
    for (let i = 0; i < buyRowsForCode.length; i++) {
      const d = buyRowsForCode[i].pf_txn_date ?? '';
      if (!buysByDate.has(d)) buysByDate.set(d, { indices: [], total: 0 });
      const g = buysByDate.get(d)!;
      g.indices.push(i);
      g.total += Math.abs(Number(buyRowsForCode[i].pf_qty) ?? 0);
    }

    // Group SELLs by date too
    const sellsByDate = new Map<string, ReconTxnPair[]>();
    for (const sell of sellRows) {
      const d = sell.pf_txn_date ?? '';
      if (!sellsByDate.has(d)) sellsByDate.set(d, []);
      sellsByDate.get(d)!.push(sell);
    }

    const usedBuyDate    = new Set<string>();
    const intradayBuyIds = new Set<string>();

    for (const [date, sellsOnDate] of sellsByDate.entries()) {
      const sellTotal = sellsOnDate.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const buyGroup  = buysByDate.get(date);

      // ── Priority: match the SELLs to a DP debit BEFORE intraday pairing ─────
      // If the FULL sell total on this date equals a DP debit (same code), the
      // sells belong to the DP delivery — do NOT let intraday pairing steal a
      // sell. Keep all sells for the DP match (Step C) and mark the same-day
      // BUY(s) as PARTIAL INTRADAY. This applies even when the BUY total EQUALS
      // the sell total: if the sell has a real DP debit it was delivered (not a
      // pure intraday round-trip), so the sell matches the DP and the buy is
      // partial intraday.
      //   e.g. SELL 10 → DP "To" 10, BUY 10 → partial intraday (buy == sell).
      //   e.g. SELL 1+2+2 = 5 → DP "To" 5, and BUY 1 → partial intraday.
      if (pfCode && buyGroup && !usedBuyDate.has(date) && buyGroup.total > 0 && buyGroup.total <= sellTotal) {
        const isinForDate = sellsOnDate[0]?.canonical_isin ?? '';
        const fullDebitCands = (dpDebitByCode.get(`${isinForDate}|${pfCode}`) ?? []).filter(dp => !usedDp.has(dp.id));
        const fullDebitTotal = fullDebitCands.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);
        if (fullDebitCands.length > 0 && Math.abs(fullDebitTotal - sellTotal) < 0.001) {
          sellsOnDate.forEach(sell => nonIntradaySells.push(sell)); // all sells → DP (Step C)
          buyGroup.indices.forEach(i => {
            const buyRow = buyRowsForCode[i];
            usedBuyIdx.add(i);
            usedDp.add(buyRow.id + '__pfUsed'); // BUY flush skips it
            bands.push({ pfRows: [buyRow], dpRow: null, dpRows: [], colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set([buyRow.id]), partialIntradayBuy: true });
          });
          usedBuyDate.add(date);
          continue;
        }

        // ── Net-sell reconciliation (same code, by TOTAL) ─────────────────────
        // When SELLs > BUYs and (sellTotal − buyTotal) equals a DP debit for the
        // code: the BUYs pair intraday with an equal amount of SELLs, and the NET
        // sells settle to the DP. Individual rows need NOT line up — only totals.
        //   e.g. SELL 10+90 = 100, BUY 62+9+4 = 75, DP "To" 25 = 100 − 75.
        // Net stays balanced (buys +75, sells −100 → −25 = DP −25); this just
        // turns the rows green/intraday instead of red.
        const netSell = sellTotal - buyGroup.total;
        if (netSell > 0.001 && fullDebitCands.length > 0 && Math.abs(fullDebitTotal - netSell) < 0.001) {
          fullDebitCands.forEach(dp => usedDp.add(dp.id));
          const buyRowsArr = buyGroup.indices.map(i => buyRowsForCode[i]);
          buyGroup.indices.forEach(i => { usedBuyIdx.add(i); usedDp.add(buyRowsForCode[i].id + '__pfUsed'); });
          usedBuyDate.add(date);
          // BUYs → intraday (blue, counted +buyTotal in net)
          bands.push({ pfRows: buyRowsArr, dpRow: null, dpRows: [], colour: 'green', isGroup: buyRowsArr.length > 1, sortDate: date, intradayIds: new Set(buyRowsArr.map(r => r.id)) });
          // SELLs → matched to the DP debit for the NET; note the intraday overlap
          bands.push({ pfRows: sellsOnDate, dpRow: fullDebitCands[0], dpRows: fullDebitCands, colour: 'green', isGroup: sellsOnDate.length > 1, sortDate: date, intradayIds: new Set(), sellIntradayOverlap: buyGroup.total });
          sellsOnDate.forEach(sell => usedDp.add(sell.id + '__pfUsed'));
          continue;
        }

        // ── Case C: match the FULL same-code DP total (split a sell if needed),
        // then intraday the leftover sells with the buy; leftover buy is red ─────
        // ── Case C: match the SMALLER side fully, then intraday the remainder ──
        // Compare the same-code DP total with the sell total and fully match the
        // SMALLER of the two; the excess of the larger side goes intraday with
        // the buy (leftover buy is red). Here DP (70) < sells (80), so the DP is
        // matched in full and the extra 10 sells pair intraday with the buy.
        //   e.g. SELL 20+25+20+15 = 80, DP 25+20+25 = 70, BUY 20:
        //     70 sells → DP (split the 15), the leftover 10 sells intraday with
        //     10 of the BUY; the other 10 BUY red. (No DP left unmatched.)
        if (fullDebitCands.length > 0 && fullDebitTotal > 0 && sellTotal > fullDebitTotal + 0.001) {
          const orderedSells = [...sellsOnDate].sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));
          const orderedDp = [...fullDebitCands].sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
          // DP is the smaller side → matched in full; the excess sells (sellTotal −
          // DP) become intraday (orange).
          const sellBand = makePoolBand(orderedSells, orderedDp, date, dp => Number(dp.dp_debit ?? 0), /*markShortfallIntraday*/ true);
          // BUY does a COMPLETE intraday (its full qty) if enough sells exist. The
          // excess sells cover part of it; the rest OVERLAPS DP-matched sells (a
          // shared sell that both settles to the DP and offsets the buy).
          const intradayTargetQty = Math.min(buyGroup.total, sellTotal); // 20
          const excessIntraday = sellBand.bonusPool!.allocations.filter(a => a.intraday).reduce((s, a) => s + a.qty, 0);
          let overlapNeeded = intradayTargetQty - excessIntraday; // sells to also flag as intraday
          const withOverlap: NonNullable<typeof sellBand.bonusPool>['allocations'] = [];
          for (const a of sellBand.bonusPool!.allocations) {
            if (overlapNeeded > 0.001 && a.matched && !a.intraday && !a.dpOnly && a.pfRow) {
              if (a.qty <= overlapNeeded + 0.001) {
                withOverlap.push({ ...a, intradayOverlap: true });
                overlapNeeded -= a.qty;
              } else {
                // Split this matched alloc: part overlaps the buy, part plain DP.
                withOverlap.push({ ...a, qty: overlapNeeded, pfSplit: true, dpSplit: true, intradayOverlap: true });
                withOverlap.push({ ...a, qty: a.qty - overlapNeeded, pfSplit: true, dpSplit: true });
                overlapNeeded = 0;
              }
            } else {
              withOverlap.push(a);
            }
          }
          sellBand.bonusPool!.allocations = withOverlap;
          bands.push(sellBand);
          sellsOnDate.forEach(sell => usedDp.add(sell.id + '__pfUsed'));
          // BUY → complete intraday (full qty).
          const buyRowsArr = buyGroup.indices.map(i => buyRowsForCode[i]);
          buyGroup.indices.forEach(i => { usedBuyIdx.add(i); usedDp.add(buyRowsForCode[i].id + '__pfUsed'); });
          usedBuyDate.add(date);
          bands.push(makeSplitIntradayBand(buyRowsArr, intradayTargetQty, date));
          continue;
        }
      }

      if (buyGroup && !usedBuyDate.has(date)) {
        if (Math.abs(buyGroup.total - sellTotal) < 0.001) {
          // ALL sells on this date == ALL buys → fully intraday
          buyGroup.indices.forEach(i => {
            usedBuyIdx.add(i);
            intradayBuyIds.add(buyRowsForCode[i].id);
          });
          usedBuyDate.add(date);
          sellsOnDate.forEach(sell => intradaySellIds.add(sell.id));
        } else {
          // Partial intraday: find the subset of SELL rows that sums exactly to
          // the BUY group total. This handles cases like SELL[20,1] where
          // 20+1 = BUY total 21 (not just a single SELL row matching).
          // Bitmask subset-sum — safe up to ~20 sell rows per day.
          const target = buyGroup.total;
          const sortedSells = [...sellsOnDate].sort(
            (a, b) => Math.abs(Number(b.pf_qty) ?? 0) - Math.abs(Number(a.pf_qty) ?? 0),
          );
          let intradaySubset: ReconTxnPair[] | null = null;

          const n = Math.min(sortedSells.length, 20);
          outer:
          for (let mask = 1; mask < (1 << n); mask++) {
            let sum = 0;
            const subset: ReconTxnPair[] = [];
            for (let bit = 0; bit < n; bit++) {
              if (mask & (1 << bit)) {
                subset.push(sortedSells[bit]);
                sum += Math.abs(Number(sortedSells[bit].pf_qty) ?? 0);
                if (sum > target + 0.001) continue outer; // prune
              }
            }
            if (Math.abs(sum - target) < 0.001) {
              // Keep the subset with the most rows (maximum match)
              if (!intradaySubset || subset.length > intradaySubset.length) {
                intradaySubset = subset;
              }
            }
          }

          if (intradaySubset) {
            buyGroup.indices.forEach(i => {
              usedBuyIdx.add(i);
              intradayBuyIds.add(buyRowsForCode[i].id);
            });
            usedBuyDate.add(date);
            intradaySubset.forEach(sell => intradaySellIds.add(sell.id));
          } else {
            // Reverse partial intraday: find a subset of BUY rows that sums to
            // the SELL total (or to a subset of sells). Handles the common case
            // where ONE small buy sits inside a big same-day/same-code group and
            // must pair with an equal-qty sell — e.g. among BUYs summing to 505
            // there is a BUY 5 that pairs with the SELL 5. The intraday pass
            // previously only searched sells-summing-to-buy-total, so a lone buy
            // inside a group was never pulled out. Now we search both directions.
            const buyIdxSorted = [...buyGroup.indices].sort(
              (a, b) => Math.abs(Number(buyRowsForCode[b].pf_qty) ?? 0) - Math.abs(Number(buyRowsForCode[a].pf_qty) ?? 0),
            );
            const bn = Math.min(buyIdxSorted.length, 20);
            let buySubset: number[] | null = null;
            outerBuy:
            for (let mask = 1; mask < (1 << bn); mask++) {
              let sum = 0; const chosen: number[] = [];
              for (let bit = 0; bit < bn; bit++) {
                if (mask & (1 << bit)) {
                  const gi = buyIdxSorted[bit];
                  sum += Math.abs(Number(buyRowsForCode[gi].pf_qty) ?? 0);
                  chosen.push(gi);
                  if (sum > sellTotal + 0.001) continue outerBuy; // prune
                }
              }
              if (Math.abs(sum - sellTotal) < 0.001) {
                if (!buySubset || chosen.length < buySubset.length) buySubset = chosen; // smallest subset
              }
            }
            if (buySubset) {
              // Reverse intraday: only the matching BUY subset (== sell total)
              // is intraday. Emit it as its OWN separate band (blue) paired with
              // the sells, and keep the REST of the group out of intraday so the
              // remaining buys settle to the DP on their own.
              //   e.g. a BUY 5 inside a 505 group pairs with the SELL 5 → the 5
              //   shows as a standalone intraday entry, the other 500 stay a
              //   normal BUY group.
              const subsetRows = buySubset.map(i => buyRowsForCode[i]);
              subsetRows.forEach((r, k) => { usedBuyIdx.add(buySubset![k]); intradayBuyIds.add(r.id); });
              // Standalone intraday BUY band (does NOT go through the group flush).
              bands.push({
                pfRows: subsetRows, dpRow: null, dpRows: [], colour: 'green',
                isGroup: subsetRows.length > 1, sortDate: date,
                intradayIds: new Set(subsetRows.map(r => r.id)),
              });
              // Mark them __pfUsed so the BUY flush skips them entirely (they're
              // already emitted above as their own band).
              subsetRows.forEach(r => usedDp.add(r.id + '__pfUsed'));
              // Do NOT usedBuyDate.add(date) — the remaining buys on this date
              // must still be processed by the BUY flush.
              sellsOnDate.forEach(sell => intradaySellIds.add(sell.id));
            }
          }
          // All non-intraday sells go to nonIntradaySells
          sellsOnDate.forEach(sell => {
            if (!intradaySellIds.has(sell.id)) nonIntradaySells.push(sell);
          });
        }
      } else {
        sellsOnDate.forEach(sell => nonIntradaySells.push(sell));
      }
    }

    // Emit intraday sells as their own band (mustard yellow)
    const intradaySellRows = sellRows.filter(r => intradaySellIds.has(r.id));
    if (intradaySellRows.length > 0) {
      const ids = new Set(intradaySellRows.map(r => r.id));
      bands.push({ pfRows: intradaySellRows, dpRow: null, dpRows: [], colour: 'red', isGroup: intradaySellRows.length > 1, sortDate: sellDate, intradayIds: ids });
    }

    // Add to global accumulator — applied when BUY bands are built below
    intradayBuyIds.forEach(id => allIntradayBuyIds.add(id));

    if (nonIntradaySells.length === 0) continue;

    // ── Pre-scan: detect partial intraday (BUY > SELL same code+date) ──────
    // If for this sell code+date there's a larger BUY and a DP credit matching
    // the net qty (BUY-SELL) within 5 days, skip these sells here and let the
    // net matching pass handle the whole set (sell+buy together).
    if (pfCode && nonIntradaySells.length > 0) {
      const sellTotalForCode = nonIntradaySells.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const buyGroupForCode  = buyByCode.get(pfCode);
      if (buyGroupForCode) {
        const buyRowsForDate = buyGroupForCode.rows.filter(r => r.pf_txn_date === sellDate && !usedDp.has(r.id + '__pfUsed'));
        const buyTotalForDate = buyRowsForDate.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        if (buyTotalForDate > sellTotalForCode + 0.001) {
          const net = buyTotalForDate - sellTotalForCode;
          const isin3 = nonIntradaySells[0]?.canonical_isin ?? '';
          const hasNetDpCredit = dpPairs.some(dp =>
            !usedDp.has(dp.id) &&
            dp.canonical_isin === isin3 &&
            Number(dp.dp_credit ?? 0) > 0 &&
            Math.abs(Number(dp.dp_credit ?? 0) - net) < 0.001 &&
            dp.dp_txn_date != null &&
            Math.abs(new Date(dp.dp_txn_date).getTime() - new Date(sellDate).getTime()) <= 5 * 24 * 60 * 60 * 1000
          );
          if (hasNetDpCredit) {
            // Skip — net pass will handle these sells with the BUY together
            continue;
          }
        }
      }
    }

    // ── Step C: Match non-intraday sells ONE BY ONE ───────────────────────
    // Rule: each PF SELL row matches one DP debit of equal qty with same
    // ISIN+code. First come first served.
    // e.g. SELL 23 + SELL 23, DP has one debit of 23:
    //   SELL 23 → matches DP debit 23 → green ✓
    //   SELL 23 → no more DP debits → red ✗
    if (pfCode) {
      const sellIsin  = nonIntradaySells[0]?.canonical_isin ?? '';
      const dpSellKey = `${sellIsin}|${pfCode}`;
      const availDp   = (dpDebitByCode.get(dpSellKey) ?? []).filter(dp => !usedDp.has(dp.id));
      const usedInPass = new Set<string>();

      // First try: match each PF SELL individually against one DP debit of same qty
      // e.g. SELL 23 + SELL 23, DP has [debit=23, debit=23] → each matches one
      const tempMatched: Array<{ sell: ReconTxnPair; dp: ReconTxnPair }> = [];
      const tempUnmatched: ReconTxnPair[] = [];

      for (const sell of nonIntradaySells) {
        const sellQty = Math.abs(Number(sell.pf_qty) ?? 0);
        const dpMatch = availDp.find(dp =>
          !usedDp.has(dp.id) && !usedInPass.has(dp.id) &&
          Math.abs(Number(dp.dp_debit ?? 0) - sellQty) < 0.001
        );
        if (dpMatch) {
          usedInPass.add(dpMatch.id);
          tempMatched.push({ sell, dp: dpMatch });
        } else {
          tempUnmatched.push(sell);
        }
      }

      // Second try: if individual matching left some unmatched sells,
      // check if ALL remaining sells sum to a single DP debit total
      // e.g. SELL 241+2+24+350+1+100+97=815, DP has one debit=815
      if (tempUnmatched.length > 0) {
        const remainingDp = availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id));
        const remainingTotal = tempUnmatched.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        const dpTotal = remainingDp.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);

        if (remainingDp.length > 0 && Math.abs(dpTotal - remainingTotal) < 0.001) {
          // Group match — all remaining sells match the DP total
          remainingDp.forEach(dp => usedDp.add(dp.id));
          // Commit individual matches too
          tempMatched.forEach(({ sell, dp }) => {
            usedDp.add(dp.id);
            bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() });
          });
          bands.push({ pfRows: tempUnmatched, dpRow: remainingDp[0], dpRows: remainingDp, colour: 'green', isGroup: tempUnmatched.length > 1, sortDate: sellDate, intradayIds: new Set() });
        } else {
        // Greedy PARTIAL match: when sells and DP debits don't fully reconcile,
        // match each DP debit to a subset of the remaining sells summing to it
        // (largest debit first). Match as much as possible; leave the rest red.
        //   e.g. SELL rows totalling 60 vs DP debits [25,20,25] → match the
        //   subsets that sum to individual debits, keep the unmatchable rows red.
        const partialQty = (r: ReconTxnPair) => Math.abs(Number(r.pf_qty) ?? 0);
        const subsetForTarget = (rows: ReconTxnPair[], target: number): ReconTxnPair[] | null => {
          const n = rows.length; const q = rows.map(partialQty); const t = Math.round(target);
          if (!Number.isInteger(target) || !q.every(Number.isInteger) || t <= 0 || n === 0) {
            const one = rows.find(r => Math.abs(partialQty(r) - target) < 0.001); return one ? [one] : null;
          }
          if ((t + 1) * (n + 1) > 30_000_000) return null;
          const reach: Uint8Array[] = new Array(n + 1); reach[n] = new Uint8Array(t + 1); reach[n][0] = 1;
          for (let i = n - 1; i >= 0; i--) { const prev = reach[i + 1]; const cur = prev.slice(); const qi = q[i]; if (qi <= t) for (let s = t - qi; s >= 0; s--) if (prev[s]) cur[s + qi] = 1; reach[i] = cur; }
          if (!reach[0][t]) return null;
          const chosen: ReconTxnPair[] = []; let rem = t;
          for (let i = 0; i < n && rem > 0; i++) { if (q[i] <= rem && reach[i + 1][rem - q[i]]) { chosen.push(rows[i]); rem -= q[i]; } }
          return rem === 0 ? chosen : null;
        };
        const remDpForPartial = availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id))
          .sort((a, b) => Number(b.dp_debit ?? 0) - Number(a.dp_debit ?? 0)); // largest debit first
        let remSellsForPartial = [...tempUnmatched];
        const partialMatched: Array<{ sells: ReconTxnPair[]; dp: ReconTxnPair }> = [];
        for (const dp of remDpForPartial) {
          const subset = subsetForTarget(remSellsForPartial, Number(dp.dp_debit ?? 0));
          if (subset && subset.length > 0) {
            partialMatched.push({ sells: subset, dp });
            usedDp.add(dp.id);
            const ids = new Set(subset.map(s => s.id));
            remSellsForPartial = remSellsForPartial.filter(s => !ids.has(s.id));
          }
        }

        const remDpAfterSellExact = () => availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id));
        if (partialMatched.length > 0 || (remDpAfterSellExact().length > 0 && remSellsForPartial.length > 0)) {
          // Commit the individual matches and the partial group matches.
          tempMatched.forEach(({ sell, dp }) => { usedDp.add(dp.id); bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() }); });
          for (const { sells, dp } of partialMatched) {
            bands.push({ pfRows: sells, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: sells.length > 1, sortDate: sellDate, intradayIds: new Set() });
          }
          // Split-match remaining sells against any remaining DP debit(s) (max
          // match, split a row when needed); leftover PF = red shortfall.
          const remDp = remDpAfterSellExact();
          if (remSellsForPartial.length > 0 && remDp.length > 0) {
            bands.push(makePoolBand(remSellsForPartial, remDp, sellDate, dp => Number(dp.dp_debit ?? 0)));
          } else if (remSellsForPartial.length > 0) {
            bands.push({ pfRows: remSellsForPartial, dpRow: null, dpRows: [], colour: 'red', isGroup: remSellsForPartial.length > 1, sortDate: sellDate, intradayIds: new Set() });
          }
        } else {
        // Pass 3 (SELL): date+qty fallback for non-NORMAL/ DP debits.
        // Some brokers (HDFC Bank via CDSL, ICICI, etc.) use formats like
        // "To HDFC BANK LIMITED/..." that have no NORMAL/ settlement code,
        // so they're absent from dpDebitByCode. Fall back to date+qty.
        const sellPass3Unmatched: ReconTxnPair[] = [];
        const sellPass3Matched: Array<{ sell: ReconTxnPair; dp: ReconTxnPair }> = [...tempMatched];
        const remainingUnmatched2 = tempUnmatched;

        for (const sell of remainingUnmatched2) {
          const sellQty = Math.abs(Number(sell.pf_qty) ?? 0);
          const fallbackDp = dpPairs.find(dp => {
            if (usedDp.has(dp.id)) return false;
            const debit = Number(dp.dp_debit ?? 0);
            if (Math.abs(debit - sellQty) >= 0.001) return false;
            if (extractDpSettlementCode(dp.dp_description)) return false; // skip coded DPs
            return dp.dp_txn_date === sellDate;
          });
          if (fallbackDp) {
            usedDp.add(fallbackDp.id);
            sellPass3Matched.push({ sell, dp: fallbackDp });
          } else {
            sellPass3Unmatched.push(sell);
          }
        }

        if (sellPass3Unmatched.length === 0) {
          // All resolved via fallback
          sellPass3Matched.forEach(({ sell, dp }) => {
            usedDp.add(dp.id);
            bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() });
          });
        } else if (sellPass3Matched.length === 0) {
          bands.push({ pfRows: nonIntradaySells, dpRow: null, dpRows: [], colour: 'red', isGroup: nonIntradaySells.length > 1, sortDate: sellDate, intradayIds: new Set() });
        } else {
          sellPass3Matched.forEach(({ sell, dp }) => {
            usedDp.add(dp.id);
            bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() });
          });
          const unmatchedSells2 = sellPass3Unmatched;
          bands.push({ pfRows: unmatchedSells2, dpRow: null, dpRows: [], colour: 'red', isGroup: unmatchedSells2.length > 1, sortDate: sellDate, intradayIds: new Set() });
        }
        }
        }
      } else {
        // All individually matched — commit them
        tempMatched.forEach(({ sell, dp }) => {
          usedDp.add(dp.id);
          bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() });
        });
      }
    } else {
      // No code — FIRST COME, FIRST SERVED: each SELL matches the first still-
      // unused DP debit of the SAME qty on the SAME date, regardless of the DP's
      // settlement code. (Previously the whole group was summed and matched
      // against a single DP debit of that total, so N sells of qty X vs N
      // separate debits of qty X never matched — identical rows showed red.)
      //
      // Guard: never take a DP debit whose settlement code belongs to a *coded*
      // PF sell — that debit is reserved for the coded match, so a no-code sell
      // can't steal it.
      const sellIsin = nonIntradaySells[0]?.canonical_isin ?? '';
      const reservedCodes = new Set<string>();
      for (const g of sellByCode.values()) if (g.code) reservedCodes.add(g.code);
      const debitFree = (d: ReconTxnPair, wantQty: number) => {
        if (usedDp.has(d.id)) return false;
        if (Number(d.dp_debit ?? 0) <= 0) return false;
        if (d.canonical_isin !== sellIsin) return false;
        if (d.dp_txn_date !== sellDate) return false;
        if (Math.abs(Number(d.dp_debit ?? 0) - wantQty) >= 0.001) return false;
        const dpCode = extractDpSettlementCode(d.dp_description);
        if (dpCode && reservedCodes.has(dpCode)) return false;
        return true;
      };

      const usedInPass = new Set<string>();
      const matchedPairs: Array<{ sell: ReconTxnPair; dp: ReconTxnPair }> = [];
      let leftover: ReconTxnPair[] = [];
      for (const sell of nonIntradaySells) {
        const sellQty = Math.abs(Number(sell.pf_qty) ?? 0);
        const dp = dpPairs.find(d => !usedInPass.has(d.id) && debitFree(d, sellQty));
        if (dp) { usedInPass.add(dp.id); matchedPairs.push({ sell, dp }); }
        else leftover.push(sell);
      }

      // Remainder: fall back to the old behaviour — a single DP debit equal to
      // the leftover total (handles one debit that represents several sells).
      if (leftover.length > 0) {
        const remTotal = leftover.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        const groupDp = dpPairs.find(d => !usedInPass.has(d.id) && debitFree(d, remTotal));
        if (groupDp) {
          usedInPass.add(groupDp.id);
          usedDp.add(groupDp.id);
          bands.push({ pfRows: leftover, dpRow: groupDp, dpRows: [groupDp], colour: 'green', isGroup: leftover.length > 1, sortDate: sellDate, intradayIds: new Set() });
          leftover = [];
        }
      }

      // ── Grouped-debit fallback: multiple DP debits summing to leftover total ──
      // e.g. PF SELL 2 (no code), DP has two "To eDIS /Block Mechanism NORMAL/2026071"
      // debit=1 entries on same date. Neither single-row pass above finds a debit=2.
      // Try any subset of same-day debits summing to the remaining leftover total.
      if (leftover.length > 0) {
        const remTotal = leftover.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        const candidateDebits = dpPairs.filter(d => !usedInPass.has(d.id) && !usedDp.has(d.id) && (() => {
          if (Number(d.dp_debit ?? 0) <= 0) return false;
          if (d.canonical_isin !== sellIsin) return false;
          if (d.dp_txn_date !== sellDate) return false;
          const dpCode = extractDpSettlementCode(d.dp_description);
          if (dpCode && reservedCodes.has(dpCode)) return false;
          return true;
        })());
        if (candidateDebits.length >= 2) {
          // Bitmask subset-sum (cap at 20 rows)
          const n = Math.min(candidateDebits.length, 20);
          const qtys = candidateDebits.slice(0, n).map(d => Number(d.dp_debit ?? 0));
          let bestSubset: ReconTxnPair[] | null = null;
          outer: for (let mask = 1; mask < (1 << n); mask++) {
            let sum = 0;
            const subset: ReconTxnPair[] = [];
            for (let bit = 0; bit < n; bit++) {
              if (mask & (1 << bit)) {
                subset.push(candidateDebits[bit]);
                sum += qtys[bit];
                if (sum > remTotal + 0.001) continue outer;
              }
            }
            if (Math.abs(sum - remTotal) < 0.001 && subset.length >= 2) {
              // Prefer smallest number of rows
              if (!bestSubset || subset.length < bestSubset.length) bestSubset = subset;
            }
          }
          if (bestSubset) {
            bestSubset.forEach(d => { usedInPass.add(d.id); usedDp.add(d.id); });
            bands.push({ pfRows: leftover, dpRow: bestSubset[0], dpRows: bestSubset, colour: 'green', isGroup: leftover.length > 1, sortDate: sellDate, intradayIds: new Set() });
            leftover = [];
          }
        }
      }
      // ── end grouped-debit fallback ──

      // Commit the individual first-come matches (green).
      for (const { sell, dp } of matchedPairs) {
        usedDp.add(dp.id);
        bands.push({ pfRows: [sell], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: sellDate, intradayIds: new Set() });
      }
      // Anything still unmatched → red.
      if (leftover.length > 0) {
        bands.push({ pfRows: leftover, dpRow: null, dpRows: [], colour: 'red', isGroup: leftover.length > 1, sortDate: sellDate, intradayIds: new Set() });
      }
    }
  }



  // Pre-index DP credits by ISIN+code.
  // eDIS lines are deduplicated per group; non-eDIS (Clearing) lines are not.
  const dpCredByCode = new Map<string, ReconTxnPair[]>();
  for (const dp of dpPairs) {
    const credit = Number(dp.dp_credit ?? 0);
    if (credit <= 0) continue;
    const dpCode = extractDpSettlementCode(dp.dp_description);
    if (!dpCode) continue;
    const dpKey = `${dp.canonical_isin}|${dpCode}`;
    if (!dpCredByCode.has(dpKey)) dpCredByCode.set(dpKey, []);
    dpCredByCode.get(dpKey)!.push(dp);
  }
  // Apply eDIS dedup per group
  for (const [k, rows] of dpCredByCode.entries()) dpCredByCode.set(k, dedupeEdisLines(rows));

  // ── NET MATCHING PASS ────────────────────────────────────────────────────
  // When the same ISIN+code has both BUYs and SELLs on the same date, the DP
  // shows only the NET delivery (BUY total - SELL total). Match the net qty
  // against the DP credit/debit before doing individual BUY/SELL matching.
  // e.g. BUY 35+60+55=150, SELL 2+54+34=90 → net=60 → DP credit 60 ✓
  for (const [code, buyGroup] of buyByCode.entries()) {
    const sellGroup = sellByCode.get(code);
    if (!sellGroup) continue;

    // Group by date — net matching is per-date
    const buyByDate  = new Map<string, ReconTxnPair[]>();
    const sellByDate2 = new Map<string, ReconTxnPair[]>();
    for (const r of buyGroup.rows) {
      const d = r.pf_txn_date ?? '';
      if (!buyByDate.has(d)) buyByDate.set(d, []);
      buyByDate.get(d)!.push(r);
    }
    for (const r of sellGroup.rows) {
      const d = r.pf_txn_date ?? '';
      if (!sellByDate2.has(d)) sellByDate2.set(d, []);
      sellByDate2.get(d)!.push(r);
    }

    for (const [date, buyRows] of buyByDate.entries()) {
      const sellRows2 = sellByDate2.get(date);
      if (!sellRows2) continue;

      const buyTotal  = buyRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const sellTotal = sellRows2.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      const net = buyTotal - sellTotal;
      if (Math.abs(net) < 0.001) continue; // fully intraday, no net delivery

      if (net > 0) {
        // Net buy — look for DP credit matching net qty
        // Primary: DP credit with same settlement code summing to net
        let candidates = (dpCredByCode.get(code) ?? []).filter(dp => !usedDp.has(dp.id));
        let dpTotal2 = candidates.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);

        // Fallback: if code-based credits don't sum to net, look for a DP credit
        // matching net qty by date proximity (within 5 days). This handles the
        // partial-intraday case: BUY 207 - SELL 53 = net 154, DP credit 154 on
        // the next day (no settlement code). The DP credit 207 (same code) is
        // left free for the separate BUY 207 with a different code.
        if (candidates.length === 0 || Math.abs(dpTotal2 - net) > 0.001) {
          const isin = buyRows[0]?.canonical_isin ?? '';
          const byDateQty = dpPairs.filter(dp =>
            !usedDp.has(dp.id) &&
            dp.canonical_isin === isin &&
            Number(dp.dp_credit ?? 0) > 0 &&
            Math.abs(Number(dp.dp_credit ?? 0) - net) < 0.001 &&
            dp.dp_txn_date != null &&
            Math.abs(new Date(dp.dp_txn_date).getTime() - new Date(date).getTime()) <= 5 * 24 * 60 * 60 * 1000
          );
          if (byDateQty.length > 0) {
            candidates = byDateQty;
            dpTotal2   = byDateQty.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);
          }
        }

        if (candidates.length > 0 && Math.abs(dpTotal2 - net) < 0.001) {
          // Double-matching partial intraday:
          //   SELL 53 → consume DP debit 53, show green with intraday highlight
          //   BUY 207 → split display: BUY 53 orange + BUY 154 green (DP credit 154)
          candidates.forEach(dp => usedDp.add(dp.id));
          const sellTotal2 = sellRows2.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
          const isin2 = buyRows[0]?.canonical_isin ?? '';
          // Find DP debit matching sell qty (same code)
          const dpDebitCands = (dpDebitByCode.get(`${isin2}|${code}`) ?? []).filter(dp => !usedDp.has(dp.id));
          const dpDebitMatch = dpDebitCands.find(dp => Math.abs(Number(dp.dp_debit ?? 0) - sellTotal2) < 0.001) ?? null;
          if (dpDebitMatch) usedDp.add(dpDebitMatch.id);
          // BUY band: green, split display via splitIntraday metadata
          bands.push({ pfRows: buyRows, dpRow: candidates[0], dpRows: candidates, colour: 'green', isGroup: false, sortDate: date, intradayIds: new Set(), splitIntraday: { sellQty: sellTotal2, dpDebit: dpDebitMatch } });
          // SELL rows: mark as __pfUsed so sell flush skips them
          // Their existing band from the sell flush will NOT be created since we consumed the DP debit here
          // We create a green band for the sells with intraday colouring
          const sellIntradayIds = new Set(sellRows2.map(r => r.id));
          if (dpDebitMatch) {
            bands.push({ pfRows: sellRows2, dpRow: dpDebitMatch, dpRows: [dpDebitMatch], colour: 'green', isGroup: sellRows2.length > 1, sortDate: date, intradayIds: sellIntradayIds, partialIntradaySell: true });
          }
          buyRows.forEach(r => usedDp.add(r.id + '__pfUsed'));
          sellRows2.forEach(r => usedDp.add(r.id + '__pfUsed'));
        }
      } else {
        // Net sell — look for DP debit matching abs(net) qty
        const absNet = Math.abs(net);
        const candidates = (dpDebitByCode.get(code) ?? []).filter(dp => !usedDp.has(dp.id));
        const dpTotal2 = candidates.reduce((s, dp) => s + Number(dp.dp_debit ?? 0), 0);
        if (candidates.length > 0 && Math.abs(dpTotal2 - absNet) < 0.001) {
          candidates.forEach(dp => usedDp.add(dp.id));
          const sortDate = date;
          bands.push({ pfRows: sellRows2, dpRow: candidates[0], dpRows: candidates, colour: 'green', isGroup: sellRows2.length > 1, sortDate, intradayIds: new Set() });
          const buyIds = new Set(buyRows.map(r => r.id));
          bands.push({ pfRows: buyRows, dpRow: null, dpRows: [], colour: 'red', isGroup: buyRows.length > 1, sortDate, intradayIds: buyIds });
          buyRows.forEach(r => usedDp.add(r.id + '__pfUsed'));
          sellRows2.forEach(r => usedDp.add(r.id + '__pfUsed'));
        }
      }
    }
  }


  for (const { code: pfCode, date, rows: allBuyRows } of buyByCode.values()) {
    const rows = allBuyRows.filter(r => !usedDp.has(r.id + '__pfUsed'));
    if (rows.length === 0) continue;

    if (pfCode) {
      const buyIsin    = rows[0]?.canonical_isin ?? '';
      const dpBuyKey   = `${buyIsin}|${pfCode}`;
      const availDp    = (dpCredByCode.get(dpBuyKey) ?? []).filter(dp => !usedDp.has(dp.id));
      const usedInPass = new Set<string>();

      // Pass 1: match each BUY individually against one DP credit of same qty
      // e.g. BUY 35 + BUY 60 + BUY 55, DP [60] → BUY 60 matches, 35+55 unmatched
      const tempMatched: Array<{ buy: ReconTxnPair; dp: ReconTxnPair }> = [];
      const tempUnmatched: ReconTxnPair[] = [];

      for (const buy of rows) {
        const buyQty = Math.abs(Number(buy.pf_qty) ?? 0);
        const dpMatch = availDp.find(dp =>
          !usedDp.has(dp.id) && !usedInPass.has(dp.id) &&
          Math.abs(Number(dp.dp_credit ?? 0) - buyQty) < 0.001
        );
        if (dpMatch) {
          usedInPass.add(dpMatch.id);
          tempMatched.push({ buy, dp: dpMatch });
        } else {
          tempUnmatched.push(buy);
        }
      }

      // Pass 2: if remaining unmatched BUYs sum to remaining DP total → group match
      // e.g. BUY 271+207=478, DP [478] → group match ✓
      if (tempUnmatched.length > 0) {
        const remainingDp    = availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id));
        const remainingTotal = tempUnmatched.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        const dpTotal        = remainingDp.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);

        if (remainingDp.length > 0 && Math.abs(dpTotal - remainingTotal) < 0.001) {
          // Group match for remaining rows
          remainingDp.forEach(dp => usedDp.add(dp.id));
          tempMatched.forEach(({ buy, dp }) => {
            usedDp.add(dp.id);
            const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []);
            bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids });
          });
          const groupIntradayIds = new Set(tempUnmatched.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
          bands.push({ pfRows: tempUnmatched, dpRow: remainingDp[0], dpRows: remainingDp, colour: 'green', isGroup: tempUnmatched.length > 1, sortDate: date, intradayIds: groupIntradayIds });
        } else {
          // ── Greedy SUBSET match: a subset of buys sums to a DP credit ──────
          // Basic rule: a group of BUYs summing to a DP credit (same code)
          // matches it, even if not ALL buys are needed. Match each DP credit
          // (largest first) to a subset of the remaining buys; leave the rest red.
          //   e.g. BUY 50+25+25 (=100) vs DP credit 75 → 50+25 match the 75,
          //   the extra 25 stays red.
          const buyQtyOf = (r: ReconTxnPair) => Math.abs(Number(r.pf_qty) ?? 0);
          const buySubsetForTarget = (rowsIn: ReconTxnPair[], target: number): ReconTxnPair[] | null => {
            const n = rowsIn.length; const q = rowsIn.map(buyQtyOf); const t = Math.round(target);
            if (!Number.isInteger(target) || !q.every(Number.isInteger) || t <= 0 || n === 0) {
              const one = rowsIn.find(r => Math.abs(buyQtyOf(r) - target) < 0.001); return one ? [one] : null;
            }
            if ((t + 1) * (n + 1) > 30_000_000) return null;
            const reach: Uint8Array[] = new Array(n + 1); reach[n] = new Uint8Array(t + 1); reach[n][0] = 1;
            for (let i = n - 1; i >= 0; i--) { const prev = reach[i + 1]; const cur = prev.slice(); const qi = q[i]; if (qi <= t) for (let s = t - qi; s >= 0; s--) if (prev[s]) cur[s + qi] = 1; reach[i] = cur; }
            if (!reach[0][t]) return null;
            const chosen: ReconTxnPair[] = []; let rem = t;
            for (let i = 0; i < n && rem > 0; i++) { if (q[i] <= rem && reach[i + 1][rem - q[i]]) { chosen.push(rowsIn[i]); rem -= q[i]; } }
            return rem === 0 ? chosen : null;
          };
          const remDpForBuy = availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id))
            .sort((a, b) => Number(b.dp_credit ?? 0) - Number(a.dp_credit ?? 0)); // largest credit first
          let remBuysForSubset = [...tempUnmatched];
          const buyPartialMatched: Array<{ buys: ReconTxnPair[]; dp: ReconTxnPair }> = [];
          for (const dp of remDpForBuy) {
            const subset = buySubsetForTarget(remBuysForSubset, Number(dp.dp_credit ?? 0));
            if (subset && subset.length > 0) {
              buyPartialMatched.push({ buys: subset, dp });
              usedDp.add(dp.id);
              const ids = new Set(subset.map(b => b.id));
              remBuysForSubset = remBuysForSubset.filter(b => !ids.has(b.id));
            }
          }

          const remDpAfterExact = () => availDp.filter(dp => !usedInPass.has(dp.id) && !usedDp.has(dp.id));

          if (buyPartialMatched.length > 0 || (remDpAfterExact().length > 0 && remBuysForSubset.length > 0)) {
            tempMatched.forEach(({ buy, dp }) => { usedDp.add(dp.id); const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []); bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids }); });
            for (const { buys, dp } of buyPartialMatched) {
              const gi = new Set(buys.filter(b => allIntradayBuyIds.has(b.id)).map(b => b.id));
              bands.push({ pfRows: buys, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: buys.length > 1, sortDate: date, intradayIds: gi });
            }
            // Split-match the remaining buys against any remaining DP credit(s)
            // (max match, split a row when needed); leftover PF = red shortfall.
            const remDp = remDpAfterExact();
            if (remBuysForSubset.length > 0 && remDp.length > 0) {
              bands.push(makePoolBand(remBuysForSubset, remDp, date, dp => Number(dp.dp_credit ?? 0)));
            } else if (remBuysForSubset.length > 0) {
              const ri = new Set(remBuysForSubset.filter(b => allIntradayBuyIds.has(b.id)).map(b => b.id));
              bands.push({ pfRows: remBuysForSubset, dpRow: null, dpRows: [], colour: 'red', isGroup: remBuysForSubset.length > 1, sortDate: date, intradayIds: ri });
            }
          } else {
          // Pass 3: NORMAL/ credits + Inter Depository transfer credits combined
          // e.g. NORMAL/2025176 [82+376=458] + Inter Depository [192] = 650 = PF total
          const buyIsin2   = rows[0]?.canonical_isin ?? '';
          const pfDate     = new Date(date);
          const windowEnd  = new Date(date);
          windowEnd.setDate(windowEnd.getDate() + 7); // DP appears up to 7 days after PF date

          const interDpCandidates = dpPairs.filter(dp => {
            if (usedDp.has(dp.id)) return false;
            const credit = Number(dp.dp_credit ?? 0);
            if (credit <= 0) return false;
            if (dp.canonical_isin !== buyIsin2) return false;
            const desc = (dp.dp_description ?? '').toLowerCase();
            if (!desc.includes('inter depository') && !desc.includes('interdepository')) return false;
            const dpDate2 = new Date(dp.dp_txn_date ?? '');
            return dpDate2 >= pfDate && dpDate2 <= windowEnd;
          });

          // Rule: a DP credit with NO description (blank or literally "0") is a
          // valid counterpart for a settlement-coded PF BUY too. The settlement-
          // coded credit is always preferred, but when the coded credit alone
          // does not cover the PF total, blank/"0" credits (and Inter Depository
          // credits above) are pulled in to make up the difference — individually
          // or grouped. Collect blank/no-description credits in the same window.
          const blankDpCandidates = dpPairs.filter(dp => {
            if (usedDp.has(dp.id)) return false;
            const credit = Number(dp.dp_credit ?? 0);
            if (credit <= 0) return false;
            if (dp.canonical_isin !== buyIsin2) return false;
            const desc = (dp.dp_description ?? '').trim();
            // "no description" = empty, or literally "0"
            if (desc !== '' && desc !== '0') return false;
            if (isPledgeDescription(dp.dp_description)) return false;
            const dpDate2 = new Date(dp.dp_txn_date ?? '');
            return dpDate2 >= pfDate && dpDate2 <= windowEnd;
          });

          // Prioritise settlement-coded credits first, then Inter Depository,
          // then blank/"0" credits — find the subset that sums to the PF total.
          const prioritised = [...remainingDp, ...interDpCandidates, ...blankDpCandidates];
          const combinedTotal = prioritised.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);

          // Exact whole-pool match (fast path)
          let chosenDp: ReconTxnPair[] | null = null;
          if (prioritised.length > 0 && Math.abs(combinedTotal - remainingTotal) < 0.001) {
            chosenDp = prioritised;
          } else if (prioritised.length > 0 && combinedTotal >= remainingTotal - 0.001) {
            // Subset-sum: smallest subset of the prioritised credits whose sum
            // equals the PF total. Coded credits are earliest in the array so
            // they are preferred when multiple subsets are equal size.
            const n = Math.min(prioritised.length, 18);
            let best: number[] | null = null;
            outerCombined: for (let mask = 1; mask < (1 << n); mask++) {
              let sum = 0; const idxs: number[] = [];
              for (let bit = 0; bit < n; bit++) {
                if (mask & (1 << bit)) { sum += Number(prioritised[bit].dp_credit ?? 0); idxs.push(bit); }
                if (sum > remainingTotal + 0.001) continue outerCombined;
              }
              if (Math.abs(sum - remainingTotal) < 0.001) {
                if (!best || idxs.length < best.length) best = idxs;
              }
            }
            if (best) chosenDp = best.map(i => prioritised[i]);
          }

          if (chosenDp && chosenDp.length > 0) {
            const combinedDp = chosenDp;
            // Combined match — coded + Inter Depository + blank/"0" credits = PF total
            combinedDp.forEach(dp => usedDp.add(dp.id));
            tempMatched.forEach(({ buy, dp }) => {
              usedDp.add(dp.id);
              const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []);
              bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids });
            });
            const groupIntradayIds2 = new Set(tempUnmatched.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
            bands.push({ pfRows: tempUnmatched, dpRow: combinedDp[0], dpRows: combinedDp, colour: 'green', isGroup: tempUnmatched.length > 1, sortDate: date, intradayIds: groupIntradayIds2 });
          } else {
          // ── Pass 4: date+qty fallback for non-NORMAL/ DP credits ──────────
          // Some brokers (HDFC Bank via CDSL, ICICI, etc.) deliver credits
          // using their own reference format ("By HDFC BANK LIMITED/22288461")
          // instead of the standard NORMAL/<code> pattern. These never appear
          // in dpCredByCode, so Passes 1-3 all fail. Fall back to qty + date
          // window matching (same as the no-code BUY path) for any rows that
          // are still unmatched after the settlement-code passes.
          const allUnmatched = [...tempUnmatched];
          // Also include individually-matched rows that might need to be re-matched
          // if their DP credit was already claimed by Pass 1 above
          const pass4Unmatched: ReconTxnPair[] = [];
          const pass4Matched: Array<{ buy: ReconTxnPair; dp: ReconTxnPair }> = [...tempMatched];

          const pfDateMs4 = date ? new Date(date).getTime() : 0;
          for (const buy of allUnmatched) {
            const buyQty = Math.abs(Number(buy.pf_qty) ?? 0);
            // Look for any unused Tier-2 DP credit (bank-reference or blank-settlement)
            // of equal qty within PF date .. +10 days. isTier2Credit correctly treats
            // "By HDFC BANK LIMITED/90064449" as Tier-2 (no exchange settlement code)
            // even though it has a number after "/". Consume-once enforced via usedDp.
            const windowMatch = dpPairs.find(dp => {
              if (usedDp.has(dp.id)) return false;
              const credit = Number(dp.dp_credit ?? 0);
              if (Math.abs(credit - buyQty) >= 0.001) return false;
              if (!isTier2Credit(dp.dp_description)) return false; // skip exchange-coded DPs
              if (!dp.dp_txn_date || !pfDateMs4) return true;
              const dm = new Date(dp.dp_txn_date).getTime();
              return dm >= pfDateMs4 && (dm - pfDateMs4) <= 10 * 24 * 60 * 60 * 1000;
            });
            if (windowMatch) {
              usedDp.add(windowMatch.id);
              pass4Matched.push({ buy, dp: windowMatch });
            } else {
              pass4Unmatched.push(buy);
            }
          }

          if (pass4Unmatched.length === 0) {
            // All resolved via Pass 4 date+qty fallback
            pass4Matched.forEach(({ buy, dp }) => {
              usedDp.add(dp.id);
              const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []);
              bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids });
            });
          } else if (pass4Matched.length === 0) {
            // All unmatched — group band
            const buyBandIntradayIds = new Set(rows.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
            bands.push({ pfRows: rows, dpRow: null, dpRows: [], colour: 'red', isGroup: rows.length > 1, sortDate: date, intradayIds: buyBandIntradayIds });
          } else {
            // Mixed — green for matched, red for unmatched
            pass4Matched.forEach(({ buy, dp }) => {
              usedDp.add(dp.id);
              const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []);
              bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids });
            });
            const unmatchedIntradayIds = new Set(pass4Unmatched.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
            bands.push({ pfRows: pass4Unmatched, dpRow: null, dpRows: [], colour: 'red', isGroup: pass4Unmatched.length > 1, sortDate: date, intradayIds: unmatchedIntradayIds });
          }
          } // end Pass 3/4 else
          } // end greedy-subset else
        } // end Pass 2 else
      } else {
        // All individually matched
        tempMatched.forEach(({ buy, dp }) => {
          usedDp.add(dp.id);
          const ids = new Set(allIntradayBuyIds.has(buy.id) ? [buy.id] : []);
          bands.push({ pfRows: [buy], dpRow: dp, dpRows: [dp], colour: 'green', isGroup: false, sortDate: date, intradayIds: ids });
        });
      }
    } else {
      // ── No-code BUY: Tier-2 individual-quantity matching ─────────────────
      // Spec (Tier-2 refinement): bank-reference credits ("By HDFC BANK LIMITED/…",
      // "By ICICI BANK LIMITED/…") and blank-settlement credits have NO exchange
      // settlement code. They are matched via ISIN + quantity, NOT by group total.
      //
      // Matching order (Caution 1 — individual before aggregate):
      //   Step 1: For each Tier-2 DP credit in the settlement window, find one PF
      //           row of equal qty → match 1:1. Preferred over any aggregate.
      //   Step 2: If no single-row match, find the smallest PF subset whose sum
      //           equals the DP credit qty.
      //   Step 3: If multiple candidates equally satisfy (ambiguity), prefer
      //           closest date; flag for review if still ambiguous.
      // Consume-once: each DP credit and PF row matched at most once.
      //
      // Only fall back to group-total matching for non-Tier-2 credits
      // (e.g. bare registrar credits whose description doesn't fit Tier-2).
      const pfDateMs = date ? new Date(date).getTime() : 0;

      // Pool of Tier-2 (bank-reference / blank) DP credits in window for this ISIN.
      // IMPORTANT: exclude "By Corporate Actions", "By Bonus Issue", "By Bonus", "By Sub Division"
      // narrations — these are reserved for CA-type PF rows (Bonus Buy, Rights Buy, Split Buy).
      // A plain BUY must never steal a CA credit from a Bonus Buy that processes later.
      const isCaReservedCredit = (desc: string | null | undefined): boolean => {
        if (!desc) return false;
        return /^by\s+corporate\s+action/i.test(desc) ||
               /^by\s+bonus/i.test(desc) ||
               /by\s+bonus\s+issue/i.test(desc) ||
               /by\s+sub\s*division/i.test(desc);
      };
      const tier2DpPool = dpPairs.filter(dp => {
        if (usedDp.has(dp.id)) return false;
        if (Number(dp.dp_credit ?? 0) <= 0) return false;
        if (isPledgeDescription(dp.dp_description)) return false;
        if (!isTier2Credit(dp.dp_description)) return false;
        if (isCaReservedCredit(dp.dp_description)) return false; // reserved for CA/Bonus Buy rows
        if (!dp.dp_txn_date || !pfDateMs) return true;
        const dm = new Date(dp.dp_txn_date).getTime();
        return dm >= pfDateMs && (dm - pfDateMs) <= 10 * 24 * 60 * 60 * 1000;
      }).sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? '')); // earliest first

      // Remaining PF rows available for matching (mutable pool)
      const remainingPf = [...rows];
      const matchedPairs: Array<{ pfRows: ReconTxnPair[]; dp: ReconTxnPair; ambiguous: boolean }> = [];

      for (const dp of tier2DpPool) {
        const dpQty = Number(dp.dp_credit ?? 0);

        // Step 1: single PF row of equal qty, in window, closest date first
        const singleCandidates = remainingPf.filter(r =>
          Math.abs(Math.abs(Number(r.pf_qty ?? 0)) - dpQty) < 0.001
        );
        // Sort by date proximity to the DP credit date
        singleCandidates.sort((a, b) => {
          const da = Math.abs(new Date(a.pf_txn_date ?? '').getTime() - new Date(dp.dp_txn_date ?? '').getTime());
          const db2 = Math.abs(new Date(b.pf_txn_date ?? '').getTime() - new Date(dp.dp_txn_date ?? '').getTime());
          return da - db2;
        });

        if (singleCandidates.length > 0) {
          const winner = singleCandidates[0];
          // Ambiguous if multiple candidates exist with equal date proximity
          const isAmbiguous = singleCandidates.length > 1 &&
            Math.abs(
              Math.abs(new Date(singleCandidates[0].pf_txn_date ?? '').getTime() - new Date(dp.dp_txn_date ?? '').getTime()) -
              Math.abs(new Date(singleCandidates[1].pf_txn_date ?? '').getTime() - new Date(dp.dp_txn_date ?? '').getTime())
            ) < 1000; // within 1s = same date
          matchedPairs.push({ pfRows: [winner], dp, ambiguous: isAmbiguous });
          remainingPf.splice(remainingPf.indexOf(winner), 1);
          continue;
        }

        // Step 2: smallest subset of remaining PF rows summing to dpQty
        // (bitmask subset-sum, safe for small sets ≤ 20)
        const n = Math.min(remainingPf.length, 20);
        let bestSubset: ReconTxnPair[] | null = null;
        for (let mask = 1; mask < (1 << n); mask++) {
          let sum = 0;
          const subset: ReconTxnPair[] = [];
          for (let bit = 0; bit < n; bit++) {
            if (mask & (1 << bit)) { subset.push(remainingPf[bit]); sum += Math.abs(Number(remainingPf[bit].pf_qty ?? 0)); }
            if (sum > dpQty + 0.001) break;
          }
          if (Math.abs(sum - dpQty) < 0.001) {
            if (!bestSubset || subset.length < bestSubset.length) bestSubset = subset;
          }
        }
        if (bestSubset) {
          matchedPairs.push({ pfRows: bestSubset, dp, ambiguous: false });
          bestSubset.forEach(r => remainingPf.splice(remainingPf.indexOf(r), 1));
        }
        // If no match found, DP credit stays unmatched (handled at end of buildMatchBands)
      }

      // Emit bands for all matched pairs
      for (const { pfRows: mPfRows, dp, ambiguous: _ambiguous } of matchedPairs) {
        usedDp.add(dp.id);
        const intradayIds2 = new Set(mPfRows.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
        bands.push({ pfRows: mPfRows, dpRow: dp, dpRows: [dp], colour: 'green', isGroup: mPfRows.length > 1, sortDate: date, intradayIds: intradayIds2 });
      }

      // Emit RED bands for any PF rows still unmatched after Tier-2
      if (remainingPf.length > 0) {
        // Last resort: try group-total match against any non-Tier-2 credit (e.g. bare registrar)
        const totalQtyRemaining = remainingPf.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        let legacyMatched: ReconTxnPair | null = null;
        for (const dp of dpPairs) {
          if (usedDp.has(dp.id)) continue;
          if (Number(dp.dp_credit ?? 0) <= 0) continue;
          if (isPledgeDescription(dp.dp_description)) continue;
          if (isTier2Credit(dp.dp_description)) continue; // already handled above
          if (Math.abs(Number(dp.dp_credit ?? 0) - totalQtyRemaining) < 0.001) { legacyMatched = dp; break; }
        }
        if (legacyMatched) usedDp.add(legacyMatched.id);
        const intradayIds3 = new Set(remainingPf.filter(r => allIntradayBuyIds.has(r.id)).map(r => r.id));
        bands.push({ pfRows: remainingPf, dpRow: legacyMatched, dpRows: legacyMatched ? [legacyMatched] : [], colour: legacyMatched ? 'green' : 'red', isGroup: remainingPf.length > 1, sortDate: date, intradayIds: intradayIds3 });
      }
    }
  }

  // ── Inter Depository Transfer + "O" entry matching ────────────────────────
  // Matches DP credit entries with no NORMAL/ code against PF BUY rows.
  // Covers:
  //   1. "By Inter Depository transfer CDS / ..." — off-market transfers
  //   2. "O" (single letter, garbled/truncated registrar credit)
  //
  // Rule (per requirement): a PF BUY matches "By Inter Depository transfer"
  // credits regardless of same/different date and same/different quantity, and
  // a GROUP of such credits can combine to equal the PF BUY total (e.g.
  // 1,003 + 24 = 1,027). We try the GROUP match first, then fall back to an
  // INDIVIDUAL one-to-one match.
  const isInterDepDesc = (dp: ReconTxnPair): boolean => {
    const desc = (dp.dp_description ?? '').trim().toLowerCase();
    return desc.includes('inter depository') || desc.includes('interdepository');
  };
  const isGarbledOCredit = (dp: ReconTxnPair): boolean => {
    const desc = (dp.dp_description ?? '').trim().toLowerCase();
    return desc === 'o' || desc === '' || desc === '0' || dp.dp_description == null
      || /^\d+(\.\d+)?$/.test(desc);
  };

  // Helper: an unmatched (red) PF BUY band for a given ISIN, with the PF
  // quantity still needing a match. Handles BOTH plain single/group bands and
  // pool bands (which may carry a dpRow even while red). For pool bands we use
  // the sum of the still-unmatched PF shortfall allocations; otherwise the
  // full PF-row total.
  const redBuyBandsForIsin = (isin: string): { idx: number; qty: number }[] => {
    const out: { idx: number; qty: number }[] = [];
    for (let i = 0; i < bands.length; i++) {
      const band = bands[i];
      if (band.pfRows.length === 0) continue;
      if (band.colour !== 'red') continue;
      if (band.pfRows[0].canonical_isin !== isin) continue;
      const rawType = (band.pfRows[0].pf_txn_type ?? '').toUpperCase();
      if (rawType === 'SELL') continue;
      // A whole red band with no DP link → match against its full PF total.
      // A red pool band → match against its unmatched PF shortfall quantity.
      let qty: number;
      if (band.bonusPool) {
        qty = band.bonusPool.allocations
          .filter(a => !a.matched && !a.intraday && !a.dpOnly && a.pfRow && !a.dpRow)
          .reduce((s, a) => s + a.qty, 0);
      } else {
        if (band.dpRow !== null) continue; // non-pool band already linked to a DP row
        qty = band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
      }
      if (qty <= 0.001) continue;
      out.push({ idx: i, qty });
    }
    return out;
  };

  // ── GROUP-FIRST: sum all unused Inter Depository credits per ISIN and match
  //    a PF BUY band whose total equals that sum. Any date.
  const interDepByIsin = new Map<string, ReconTxnPair[]>();
  for (const dp of dpPairs) {
    if (usedDp.has(dp.id)) continue;
    if (Number(dp.dp_credit ?? 0) <= 0) continue;
    if (!isInterDepDesc(dp)) continue;
    const key = dp.canonical_isin;
    if (!interDepByIsin.has(key)) interDepByIsin.set(key, []);
    interDepByIsin.get(key)!.push(dp);
  }
  for (const [isin, dps] of interDepByIsin) {
    if (dps.length < 2) continue; // group means 2+; singles handled below
    const groupCredit = dps.reduce((s, d) => s + Number(d.dp_credit ?? 0), 0);
    const candidate = redBuyBandsForIsin(isin).find(b => Math.abs(b.qty - groupCredit) < 0.001);
    if (candidate == null) continue;
    // Match: link the PF BUY band to ALL the Inter Depository credits.
    for (const d of dps) usedDp.add(d.id);
    bands[candidate.idx] = {
      ...bands[candidate.idx],
      dpRow: dps[0],
      dpRows: dps,
      colour: 'green',
    };
  }

  // ── INDIVIDUAL fallback: one DP credit ↔ one PF BUY band, same ISIN + qty,
  //    any date. Covers Inter Depository singles and garbled "O"/blank credits.
  for (const dp of dpPairs) {
    if (usedDp.has(dp.id)) continue;
    const credit = Number(dp.dp_credit ?? 0);
    if (credit <= 0) continue;
    if (!isInterDepDesc(dp) && !isGarbledOCredit(dp)) continue;

    const candidate = redBuyBandsForIsin(dp.canonical_isin).find(b => Math.abs(b.qty - credit) < 0.001);
    if (candidate == null) continue;
    usedDp.add(dp.id);
    bands[candidate.idx] = {
      ...bands[candidate.idx],
      dpRow: dp,
      dpRows: [dp],
      colour: 'green',
    };
  }

  // ── Pool shortfall recovery ───────────────────────────────────────────────
  // A split BUY can leave a red SHORTFALL sub-entry inside a pool band (e.g. a
  // BUY 72 split into 2 [matched] + 70 [unmatched]). Those 70 shares can still
  // be covered by a separate DP credit — most often a "By Inter Depository
  // transfer" of the same quantity. Match such shortfalls by QUANTITY (same
  // ISIN, any date): flip the sub-entry to matched and link the DP credit.
  // Inter Depository credits are preferred; then any other unused credit.
  const creditQtyOf = (dp: ReconTxnPair) => Number(dp.dp_credit ?? 0);
  const eligibleShortfallCredit = (dp: ReconTxnPair, isin: string, qty: number): boolean => {
    if (usedDp.has(dp.id)) return false;
    if (dp.canonical_isin !== isin) return false;
    if (creditQtyOf(dp) <= 0) return false;
    if (Math.abs(creditQtyOf(dp) - qty) >= 0.001) return false;
    if (isPledgeDescription(dp.dp_description)) return false;
    if (isEdisDpLine(dp.dp_description)) return false;
    return true;
  };
  for (const band of bands) {
    if (!band.bonusPool) continue;
    for (const alloc of band.bonusPool.allocations) {
      // Only red PF-side shortfalls (PF qty with no DP counterpart, not intraday)
      if (alloc.matched || alloc.intraday || alloc.dpOnly) continue;
      if (!alloc.pfRow || alloc.dpRow) continue;
      const isin = alloc.pfRow.canonical_isin;
      const qty = alloc.qty;

      // (a) Single credit of equal qty — prefer Inter Depository, then any.
      let dp = dpPairs.find(d => eligibleShortfallCredit(d, isin, qty) && isInterDepDesc(d))
            ?? dpPairs.find(d => eligibleShortfallCredit(d, isin, qty));
      if (dp) {
        usedDp.add(dp.id);
        alloc.matched = true;
        alloc.dpRow = dp;
        alloc.dpParentQty = creditQtyOf(dp);
        if (!band.dpRows.some(d => d.id === dp!.id)) band.dpRows = [...band.dpRows, dp];
        if (!band.dpRow) band.dpRow = dp;
        band.colour = 'green';
        continue;
      }

      // (b) GROUP of Inter Depository credits summing to the shortfall qty
      //     (e.g. 1,003 + 24 = 1,027). Any date.
      const idtCredits = dpPairs.filter(d =>
        !usedDp.has(d.id) && d.canonical_isin === isin && isInterDepDesc(d) && creditQtyOf(d) > 0);
      const idtSum = idtCredits.reduce((s, d) => s + creditQtyOf(d), 0);
      if (idtCredits.length >= 2 && Math.abs(idtSum - qty) < 0.001) {
        for (const d of idtCredits) usedDp.add(d.id);
        alloc.matched = true;
        alloc.dpRow = idtCredits[0];
        alloc.dpParentQty = creditQtyOf(idtCredits[0]);
        for (const d of idtCredits) {
          if (!band.dpRows.some(x => x.id === d.id)) band.dpRows = [...band.dpRows, d];
        }
        if (!band.dpRow) band.dpRow = idtCredits[0];
        band.colour = 'green';
        continue;
      }
    }
  }

  // ── PF-side grouping ──────────────────────────────────────────────────────
  // The reverse of the DP grouping above: multiple unmatched PF BUY pieces can
  // sum to ONE DP credit (e.g. a 14 shortfall + a standalone 9 = a single
  // "By Inter Depository transfer" credit of 23). We collect all still-red PF
  // BUY quantity for an ISIN — whole red bands AND red shortfall allocations
  // inside pool bands — and, if their combined total equals an unused credit,
  // match them all to it. Prefer Inter Depository credits, then any eligible.
  {
    // Build the set of unmatched PF "pieces" per ISIN.
    type PfPiece =
      | { kind: 'band'; bandIdx: number; qty: number }
      | { kind: 'alloc'; bandIdx: number; allocIdx: number; qty: number };
    const piecesByIsin = new Map<string, PfPiece[]>();
    const addPiece = (isin: string, p: PfPiece) => {
      if (!piecesByIsin.has(isin)) piecesByIsin.set(isin, []);
      piecesByIsin.get(isin)!.push(p);
    };
    for (let bi = 0; bi < bands.length; bi++) {
      const band = bands[bi];
      if (band.pfRows.length === 0) continue;
      const rawType = (band.pfRows[0].pf_txn_type ?? '').toUpperCase();
      if (rawType === 'SELL') continue;
      const isin = band.pfRows[0].canonical_isin;
      if (band.bonusPool) {
        band.bonusPool.allocations.forEach((a, ai) => {
          if (a.matched || a.intraday || a.dpOnly) return;
          if (!a.pfRow || a.dpRow) return;
          if (a.qty > 0.001) addPiece(isin, { kind: 'alloc', bandIdx: bi, allocIdx: ai, qty: a.qty });
        });
      } else if (band.colour === 'red' && band.dpRow === null) {
        const qty = band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
        if (qty > 0.001) addPiece(isin, { kind: 'band', bandIdx: bi, qty });
      }
    }

    for (const [isin, pieces] of piecesByIsin) {
      if (pieces.length < 2) continue; // grouping needs 2+ PF pieces
      const total = pieces.reduce((s, p) => s + p.qty, 0);
      // Find an unused credit equal to the combined PF total (Inter Dep first).
      let dp = dpPairs.find(d => eligibleShortfallCredit(d, isin, total) && isInterDepDesc(d))
            ?? dpPairs.find(d => eligibleShortfallCredit(d, isin, total));
      if (!dp) continue;
      usedDp.add(dp.id);
      // Green every PF piece and link the credit.
      for (const p of pieces) {
        const band = bands[p.bandIdx];
        if (p.kind === 'alloc' && band.bonusPool) {
          const a = band.bonusPool.allocations[p.allocIdx];
          a.matched = true;
          a.dpRow = dp;
          a.dpParentQty = creditQtyOf(dp);
        }
        if (!band.dpRows.some(x => x.id === dp!.id)) band.dpRows = [...band.dpRows, dp];
        if (!band.dpRow) band.dpRow = dp;
        band.colour = 'green';
      }
    }
  }

  // ── Tier-3 pledge-recovery pass ──────────────────────────────────────────
  // PF rows with is_tier3_match=true recover against a pledge/hidden DP entry.
  // Split rows (BUY 5 + BUY 6 same code+date) are GROUPED so their combined
  // qty (11) matches a single DP entry of that qty. Only applies to rows in a
  // RED band — never overrides a green normal match.
  const tier3DpPairs = dpPairs.filter(dp => dp.is_tier3_match && !usedDp.has(dp.id));
  if (tier3DpPairs.length > 0) {
    const tier3PfRows = pairs.filter(p => p.pf_qty !== null && p.is_tier3_match);
    // Group by code + date + direction
    const groupMap = new Map<string, ReconTxnPair[]>();
    for (const pfRow of tier3PfRows) {
      const code = (pfRow.pf_particulars ?? '').trim();
      const date = pfRow.pf_txn_date ?? '';
      const isBuy = Number(pfRow.pf_qty ?? 0) > 0;
      const key = `${code}|${date}|${isBuy ? 'B' : 'S'}`;
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(pfRow);
    }
    for (const groupRows of groupMap.values()) {
      // Only proceed if ALL rows in the group are in RED bands (unmatched).
      // If any is in a green band, a normal match took priority — skip.
      const anyGreen = groupRows.some(pr => {
        const b = bands.find(bd => bd.pfRows.some(r => r.id === pr.id));
        return b && b.colour !== 'red';
      });
      if (anyGreen) continue;

      const groupQty = groupRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty ?? 0)), 0);
      const pfDateMs = groupRows[0].pf_txn_date ? new Date(groupRows[0].pf_txn_date).getTime() : 0;
      const match = tier3DpPairs.find(dp => {
        if (usedDp.has(dp.id)) return false;
        const dpQty = Number(dp.dp_credit ?? 0) + Number(dp.dp_debit ?? 0);
        if (Math.abs(dpQty - groupQty) >= 0.001) return false;
        if (!dp.dp_txn_date || !pfDateMs) return true;
        const dpMs = new Date(dp.dp_txn_date).getTime();
        return dpMs >= pfDateMs && (dpMs - pfDateMs) <= 10 * 24 * 60 * 60 * 1000;
      });
      if (!match) continue;
      usedDp.add(match.id);
      // Remove existing RED band(s) containing any of the group rows
      for (const pr of groupRows) {
        const idx = bands.findIndex(b => b.pfRows.some(r => r.id === pr.id));
        if (idx >= 0) bands.splice(idx, 1);
      }
      // Add ONE green band linking the whole group to the single DP entry
      bands.push({ pfRows: groupRows, dpRow: match, dpRows: [match], colour: 'green',
        isGroup: groupRows.length > 1, sortDate: groupRows[0].pf_txn_date!, intradayIds: new Set() });
    }
  }

  // ── Bonus narration match (T+8) — LAST RESORT for bonus entries only ──────
  // A PF Bonus Buy that is STILL unmatched after every other rule/tier can be
  // matched to DP corporate-action credits by narration + aggregate quantity.
  // Conditions (ALL required):
  //   1. PF row is a Bonus Buy AND currently in a RED (unmatched) band.
  //   2. Same ISIN.
  //   3. DP narration is EXACTLY "By Corporate Action(s)" or "By Bonus".
  //   4. DP date within [PF bonus date, PF bonus date + 8 days] inclusive.
  //   5. Sum of qualifying DP credits EXACTLY equals the PF bonus qty.
  // Sub-Division rows are NEVER included. No partial match. Flag "bonus_narration".
  {
    const isBonusNarration = (desc: string | null | undefined): boolean => {
      if (!desc) return false;
      const d = desc.toLowerCase().trim();
      // Only "By Corporate Action(s)" or "By Bonus" — nothing else.
      return /^by\s+corporate\s+action/.test(d) || /^by\s+bonus\b/.test(d);
    };
    // PF bonus rows still unmatched (in a red band)
    const unmatchedBonusRows = bands
      .filter(b => b.colour === 'red' && b.pfRows.length > 0)
      .flatMap(b => b.pfRows)
      .filter(p => {
        const t = (p.pf_txn_type ?? '').trim().toLowerCase();
        return t === 'bonus buy' || t === 'bonus';
      });

    for (const pfRow of unmatchedBonusRows) {
      const isin  = pfRow.canonical_isin;
      const pfQty = Math.abs(Number(pfRow.pf_qty) ?? 0);

      // Qualifying DP rows: same ISIN, bonus/corporate-action narration only,
      // unused. NO date window — the DP credit can be any number of days from
      // the PF bonus date. (Sub-Division excluded by the narration test above.)
      const qualifying = dpPairs.filter(dp => {
        if (usedDp.has(dp.id)) return false;
        if (dp.canonical_isin !== isin) return false;
        if (Number(dp.dp_credit ?? 0) <= 0) return false;
        if (!isBonusNarration(dp.dp_description)) return false;
        return true;
      });
      if (qualifying.length === 0) continue;

      const sumQty = qualifying.reduce((s, dp) => s + Number(dp.dp_credit ?? 0), 0);
      // Exact aggregate equality only — never force a partial match.
      if (Math.abs(sumQty - pfQty) >= 0.001) continue;

      // Match: mark all DP rows + remove the PF row's red band, add a green band.
      qualifying.forEach(dp => usedDp.add(dp.id));
      const idx = bands.findIndex(b => b.pfRows.some(r => r.id === pfRow.id));
      if (idx >= 0) {
        const old = bands[idx];
        if (old.pfRows.length === 1) {
          bands.splice(idx, 1);
        } else {
          // Remove just this PF row from the multi-row red band
          bands[idx] = { ...old, pfRows: old.pfRows.filter(r => r.id !== pfRow.id) };
        }
      }
      bands.push({
        pfRows: [pfRow],
        dpRow: qualifying[0],
        dpRows: qualifying,
        colour: 'green',
        isGroup: qualifying.length > 1,
        sortDate: pfRow.pf_txn_date!,
        intradayIds: new Set(),
        bonusNarrationMatch: true,
      });
    }
  }

  // Unmatched DP rows become DP-ONLY bands (empty pfRows array) so they
  // slot into the SAME chronological sequence as every PF band below.
  // EXCLUDE pledge entries: they are internal operations and must never
  // show as standalone unmatched DP rows. Only pledge entries that got
  // LINKED to a PF row via Tier-3 (now in green bands, in usedDp) appear.
  const unmatchedDp = dpPairs.filter(dp =>
    !usedDp.has(dp.id) && !isPledgeDescription(dp.dp_description)
  );

  // ── Suppress eDIS/Block Mechanism noise pairs ─────────────────────────────
  // Criteria (ALL must be true to suppress both rows):
  //   1. Description contains "eDIS /Block Mechanism" (isEdisDpLine)
  //   2. NO settlement code in the description (extractDpSettlementCode returns null)
  //   3. One row is a credit and the other is a debit with equal qty
  //   4. Both rows are within 8 calendar days of each other
  // If a settlement code IS present the rows must remain visible.
  // EXCEPTION: eDIS/Block-Mechanism rows whose description also contains "SLB"
  // (securities lending & borrowing) are a real movement and must stay VISIBLE
  // even though they carry no settlement code — so they are excluded from the
  // suppression candidate set below.
  const edisNoiseSuppressed = new Set<string>();
  const edisNoSettlement = unmatchedDp.filter(dp =>
    isEdisDpLine(dp.dp_description) &&
    extractDpSettlementCode(dp.dp_description) === null &&
    !/\bSLB\b/i.test(dp.dp_description ?? '')   // keep SLB eDIS entries visible
  );
  // Index credit rows by qty only — we are already inside a single-ISIN
  // context (buildMatchBands receives per-ISIN pairs), so keying on ISIN
  // is redundant and can break if the two rows have subtly different ISIN
  // strings (e.g. trailing whitespace from PDF extraction).
  const edisCredits = new Map<string, ReconTxnPair[]>();
  for (const dp of edisNoSettlement) {
    const cr = Number(dp.dp_credit ?? 0);
    if (cr <= 0) continue;
    const key = `${cr}`;
    if (!edisCredits.has(key)) edisCredits.set(key, []);
    edisCredits.get(key)!.push(dp);
  }
  for (const dp of edisNoSettlement) {
    const dr = Number(dp.dp_debit ?? 0);
    if (dr <= 0) continue;
    const key = `${dr}`;
    const creditCandidates = (edisCredits.get(key) ?? []).filter(c => !edisNoiseSuppressed.has(c.id));
    const drDate = dp.dp_txn_date ? new Date(dp.dp_txn_date).getTime() : null;
    if (!drDate) continue;
    const match = creditCandidates.find(c => {
      const crDate = c.dp_txn_date ? new Date(c.dp_txn_date).getTime() : null;
      return crDate !== null && Math.abs(drDate - crDate) <= 8 * 24 * 60 * 60 * 1000;
    });
    if (match) {
      edisNoiseSuppressed.add(dp.id);
      edisNoiseSuppressed.add(match.id);
    }
  }
  // Also suppress any eDIS-no-settlement rows that have no paired counterpart.
  // A lone "By eDIS /Block Mechanism" credit = block-earmark, never a real delivery.
  // A lone "To AXIS.../eDIS /Block Mechanism /" debit = the unwind of that block;
  // its credit pair was consumed by SELL matching, so suppress the orphaned debit too.
  for (const dp of edisNoSettlement) {
    if (!edisNoiseSuppressed.has(dp.id)) {
      edisNoiseSuppressed.add(dp.id);
    }
  }

  for (const dp of unmatchedDp) {
    if (edisNoiseSuppressed.has(dp.id)) continue;
    bands.push({ pfRows: [], dpRow: dp, dpRows: (dp ? [dp] : []), colour: 'red', isGroup: false, sortDate: dp.dp_txn_date!, intradayIds: new Set() });
  }

  // "By Partly Paid to Fully Paid Shares" — always shown as unmatched (red),
  // never matched to any PF entry (held out of the matchable pool above).
  for (const dp of partlyPaidDp) {
    bands.push({ pfRows: [], dpRow: dp, dpRows: [dp], colour: 'red', isGroup: false, sortDate: dp.dp_txn_date!, intradayIds: new Set() });
  }

  // SLB entries — never matched to any PF entry (held out of the matchable pool
  // above) and excluded from the DP net. Clubbed into ONE group at the bottom
  // (like dividends), rendered in a distinct non-red colour since they are
  // informational only and don't count toward matching or mismatch.
  if (slbDp.length > 0) {
    const sortedSlb = [...slbDp].sort((a, b) => (a.dp_txn_date ?? '').localeCompare(b.dp_txn_date ?? ''));
    bands.push({ pfRows: [], dpRow: sortedSlb[0], dpRows: sortedSlb, colour: 'green', isGroup: sortedSlb.length > 1, sortDate: '9999-12-30', intradayIds: new Set(), slb: true });
  }

  // Sort the ENTIRE unified list chronologically by sortDate — matched
  // bands and unmatched-DP-only bands together, one sequence.
  bands.sort((a, b) => (a.sortDate > b.sortDate ? 1 : a.sortDate < b.sortDate ? -1 : 0));

  // ── Merge individual BUY bands with same ISIN + code + date + colour ─────
  // When the two-pass logic emits BUY rows one-by-one, they end up as
  // separate bands. Merge them back into one group band so the UI can
  // show a compressed summary row with expand/collapse.
  const mergedBands: MatchBand[] = [];
  const buyGroupSeen = new Map<string, number>(); // key → index in mergedBands

  for (const band of bands) {
    // Only merge PF-only bands (no DP match) or all-unmatched BUY groups
    if (band.pfRows.length !== 1) { mergedBands.push(band); continue; }
    const row = band.pfRows[0];
    const rawT = (row.pf_txn_type ?? '').toUpperCase();
    // Only merge BUY-side rows (not SELL, not CA bonus)
    if (rawT === 'SELL') { mergedBands.push(band); continue; }
    const code = row.pf_particulars?.trim() ?? '';
    const date = band.sortDate;
    const colour = band.colour;
    const isin = row.canonical_isin;
    const mergeKey = `${isin}|${code}|${date}|${colour}`;

    if (code && buyGroupSeen.has(mergeKey)) {
      // Merge into existing group band
      const idx = buyGroupSeen.get(mergeKey)!;
      const existing = mergedBands[idx];
      mergedBands[idx] = {
        ...existing,
        pfRows: [...existing.pfRows, row],
        isGroup: true,
        // Keep dpRow from whichever band had one (green match)
        dpRow: existing.dpRow ?? band.dpRow,
        dpRows: [...existing.dpRows, ...band.dpRows].filter((d, i, a) => a.findIndex(x => x.id === d.id) === i),
        intradayIds: new Set([...existing.intradayIds, ...band.intradayIds]),
      };
    } else {
      if (code) buyGroupSeen.set(mergeKey, mergedBands.length);
      mergedBands.push({ ...band });
    }
  }
  bands.length = 0;
  bands.push(...mergedBands);

  // ── Group ALL dividend bands into one collapsible group at the BOTTOM ──────
  // Every dividend entry (regardless of date, position, colour, or DP state) is
  // pulled out and merged into a single group row with an expand button, placed
  // AFTER all other entries. Non-dividend bands keep their order. Detection is
  // by TYPE only (any band whose PF rows are all dividend-type) so it can't be
  // defeated by a band's colour or dpRow being unexpected.
  const isDivBand = (b: MatchBand) =>
    b.pfRows.length > 0 &&
    b.pfRows.every(r => {
      const t = (r.pf_txn_type ?? '').toUpperCase().trim();
      return DIVIDEND_TYPES.has(t) || t.includes('DIVIDEND');
    });

  const nonDivBands: MatchBand[] = [];
  const divRows: ReconTxnPair[] = [];
  for (const band of bands) {
    if (isDivBand(band)) {
      divRows.push(...band.pfRows);      // collect every dividend row
    } else {
      nonDivBands.push(band);
    }
  }
  // Sort collected dividends by date so the expanded list reads chronologically.
  divRows.sort((a, b) => (a.pf_txn_date ?? '').localeCompare(b.pf_txn_date ?? ''));

  bands.length = 0;
  bands.push(...nonDivBands);
  if (divRows.length > 0) {
    // One dividend group at the very bottom. sortDate uses a far-future key so
    // the later chronological sort (if any) keeps it last; but since we push it
    // after nonDivBands and skip re-sorting below, order is preserved.
    bands.push({
      pfRows: divRows,
      dpRow: null,
      dpRows: [],
      colour: 'green',
      isGroup: divRows.length > 1,
      sortDate: '9999-12-31',   // anchor at the bottom
      intradayIds: new Set(),
    });
  }

  // ── Rule 2: Intraday detection ────────────────────────────────────────────
  // For each ISIN+date, pair BUY and SELL rows with equal qty.
  const intradayIds = new Set<string>();
  const byIsinsDate = new Map<string, { buys: ReconTxnPair[]; sells: ReconTxnPair[] }>();
  for (const band of bands) {
    for (const row of band.pfRows) {
      if (!row.pf_txn_date || !row.pf_txn_type) continue;
      const rawT = (row.pf_txn_type ?? '').toUpperCase();
      const isBuy  = rawT !== 'SELL';
      const isSell = rawT === 'SELL';
      if (!isBuy && !isSell) continue;
      const k = `${row.canonical_isin}|${row.pf_txn_date}`;
      if (!byIsinsDate.has(k)) byIsinsDate.set(k, { buys: [], sells: [] });
      if (isBuy)  byIsinsDate.get(k)!.buys.push(row);
      if (isSell) byIsinsDate.get(k)!.sells.push(row);
    }
  }
  for (const { buys, sells } of byIsinsDate.values()) {
    // Greedy match by equal quantity
    const usedSell = new Set<string>();
    for (const buy of buys) {
      const buyQty = Math.abs(Number(buy.pf_qty) ?? 0);
      const matchSell = sells.find(s => !usedSell.has(s.id) && Math.abs(Math.abs(Number(s.pf_qty) ?? 0) - buyQty) < 0.001);
      if (matchSell) {
        intradayIds.add(buy.id);
        intradayIds.add(matchSell.id);
        usedSell.add(matchSell.id);
      }
    }
  }
  // Tag each band's pfRows
  // ── Pair the partial-intraday BUY with its offsetting SELL(s) ──────────────
  // For each partialIntradayBuy, find the DP-matched SELL band on the same day
  // and flag the earliest sell(s) that sum to the buy qty as the partial-intraday
  // counterpart, so both the buy and its pairing sell(s) show the same partial-
  // intraday colour/tag. The sell(s) still settle to the DP (they are only
  // visually marked, not removed).
  for (const buyBand of bands.filter(b => b.partialIntradayBuy)) {
    const buyRow = buyBand.pfRows[0];
    if (!buyRow) continue;
    const buyQty = Math.abs(Number(buyRow.pf_qty) ?? 0);
    const sellBand = bands.find(b =>
      b.colour === 'green' && b.dpRows.length > 0 && b.pfRows.length > 0 &&
      b.pfRows[0]?.pf_txn_date === buyRow.pf_txn_date &&
      b.pfRows[0]?.canonical_isin === buyRow.canonical_isin &&
      b.pfRows.every(r => { const t = (r.pf_txn_type ?? '').toUpperCase().trim(); return t === 'SELL' || t === 'SELLCR'; }));
    if (!sellBand) continue;
    // Earliest sells (by qty ascending, then order) summing up to the buy qty.
    const sorted = [...sellBand.pfRows].sort((a, b) => Math.abs(Number(a.pf_qty) ?? 0) - Math.abs(Number(b.pf_qty) ?? 0));
    const pairIds = new Set<string>();
    let acc = 0;
    for (const s of sorted) {
      if (acc >= buyQty - 0.001) break;
      pairIds.add(s.id);
      acc += Math.abs(Number(s.pf_qty) ?? 0);
    }
    if (pairIds.size > 0) sellBand.partialIntradayPairIds = pairIds;
  }

  return { bands };
}

/* ─── Side-by-side split tables ─────────────────────────────────────── */

// Fixed column widths shared by every PF table and every DP table.
// Using colgroup ensures Qty/Credit/Debit are always the same width
// regardless of content, so both sides stay perfectly aligned.
function PfColGroup() {
  return (
    <colgroup>
      <col style={{ width: '100px' }} /> {/* Date — wide enough for YYYY-MM-DD */}
      <col />                             {/* Type — flex */}
      <col style={{ width: '56px' }} />  {/* Qty */}
      <col style={{ width: '140px' }} /> {/* Price / Value */}
    </colgroup>
  );
}
function DpColGroup() {
  return (
    <colgroup>
      <col style={{ width: '100px' }} /> {/* Date — wide enough for YYYY-MM-DD */}
      <col />                             {/* Description — flex */}
      <col style={{ width: '64px' }} />  {/* Credit */}
      <col style={{ width: '64px' }} />  {/* Debit */}
    </colgroup>
  );
}

/** Compute PF+DP totals from the bands that are actually rendered. */
function buildVisibleBreakdown(bands: MatchBand[]) {
  const b = {
    portfolio: { buys_qty: 0, buys_count: 0, sells_qty: 0, sells_count: 0,
      dividends_count: 0, dividends_amount: 0, corporate_action_count: 0, other_count: 0 },
    dp: { credits_qty: 0, credits_count: 0, debits_qty: 0, debits_count: 0 },
  };

  // Pre-compute: for each orange (intraday SELL, no DP debit) band, find its
  // matching intraday BUY band. Rule: if the orange SELL has no DP debit but
  // the paired BUY has a DP credit — the round-trip net-settled at zero in DP
  // (depository only sees net delivery). We must:
  //   (a) Omit the orange SELL qty from sells_qty (PF side)
  //   (b) Omit the BUY's DP credit from credits_qty (DP side)
  //   (c) Omit the BUY qty from buys_qty (PF side)
  // so that the entire intraday round-trip contributes 0 to both PF net and DP net.
  //
  // Identification of the orange band:
  //   - colour === 'red' (unmatched)
  //   - intradayIds.size > 0 (has intraday tag)
  //   - dpRows is empty (no DP debit — the SELL didn't get a debit entry)
  //   - pfRows are SELL-type
  //
  // Identification of the paired BUY:
  //   - colour === 'green' (matched)
  //   - intradayIds overlaps with the SELL's intradayIds
  //   - dpRows.length > 0 (has DP credit)
  //   - pfRows are BUY-type
  //
  // Build a set of DP row IDs that belong to orange-paired BUY bands → exclude from credits_qty.
  // Also build a set of PF row IDs (both SELL and BUY of the pair) → exclude from buys/sells_qty.
  const dpIdsToExclude = new Set<string>();
  const pfIdsToExclude = new Set<string>();

  const orangeSellBands = bands.filter(band =>
    band.colour === 'red' &&
    band.intradayIds.size > 0 &&
    band.dpRows.length === 0 &&
    band.pfRows.length > 0 &&
    band.pfRows.every(r => (r.pf_txn_type ?? '').trim().toUpperCase() === 'SELL' ||
                            (r.pf_txn_type ?? '').trim().toUpperCase() === 'SELLCR')
  );

  for (const sellBand of orangeSellBands) {
    // Find the BUY band whose intradayIds overlaps the sell band's intradayIds
    const matchingBuyBand = bands.find(b2 =>
      b2.colour === 'green' &&
      b2.dpRows.length > 0 &&
      b2.pfRows.length > 0 &&
      b2.pfRows.some(r => {
        const tt = (r.pf_txn_type ?? '').trim().toUpperCase();
        return tt !== 'SELL' && tt !== 'SELLCR'; // buy-side
      }) &&
      b2.pfRows.some(r => sellBand.intradayIds.has(r.id) || b2.intradayIds.has(r.id)) &&
      // Same date and ISIN as the sell band
      b2.sortDate === sellBand.sortDate &&
      b2.pfRows[0]?.canonical_isin === sellBand.pfRows[0]?.canonical_isin
    );

    if (matchingBuyBand) {
      // Exclude this BUY's DP credit(s) from the DP net
      matchingBuyBand.dpRows.forEach(dp => dpIdsToExclude.add(dp.id));
      // Exclude both the BUY pfRows and SELL pfRows from PF net
      matchingBuyBand.pfRows.forEach(r => pfIdsToExclude.add(r.id));
      sellBand.pfRows.forEach(r => pfIdsToExclude.add(r.id));
    }
  }

  // Dedupe DP rows across bands: a pooled bonus credit can be attached to
  // multiple split bands (green + red), and must be counted at most once.
  const countedDpIds = new Set<string>();

  // Partial intraday BUYs net to zero (their offsetting sell was consumed by the
  // DP delivery) — exclude them from the PF net.
  for (const band of bands) {
    if (band.partialIntradayBuy) band.pfRows.forEach(r => pfIdsToExclude.add(r.id));
  }

  for (const band of bands) {
    // For splitIntraday bands, the BUY qty counted is remainder only (buyTotal - sellQty)
    // The sellQty (53) is already counted by the separate SELL band — don't double-count
    const splitAdjust = band.splitIntraday ? band.splitIntraday.sellQty : 0;
    for (const p of band.pfRows) {
      const tt = (p.pf_txn_type ?? '').trim().toUpperCase();
      const origTT = (p.pf_txn_type ?? '').trim(); // preserve case for mixed-case CA labels
      // Bonus-pool bands group the FULL PF rows in one band; each row's full
      // pf_qty is counted normally (covered + shortfall = full qty). No override.
      const rawQty = Math.abs(Number(p.pf_qty ?? 0));

      // Determine the signed contribution using the SAME rules as BUY_LIKE_TYPES /
      // SELL_LIKE_TYPES / signedQty. Previously only BUY/BUYCR went into buys_qty,
      // leaving BONUS, SPLIT BUY, DEMERGERIN, DEMERGERBUY, Bonus Buy, Rights Buy etc.
      // uncounted on the PF side while their matched DP credits were counted on the DP
      // side — producing a systematic mismatch equal to the total CA qty.
      const isBuyLike = BUY_LIKE_TYPES.has(tt) || BUY_LIKE_TYPES.has(origTT) ||
                        origTT.endsWith('Buy') || origTT.endsWith('BUY') ||
                        tt === 'TRANSFERIN' || tt === 'INTRANSFER' || tt === 'MERGER BUY';
      const isSellLike = SELL_LIKE_TYPES.has(tt) ||
                         tt === 'SELLCR' || tt === 'SPLIT SELL' || tt === 'DEMERGERSELL' ||
                         tt === 'OUTTRANSFER' || tt === 'MERGER SELL';
      const isDividend = DIVIDEND_TYPES.has(tt) || tt.includes('DIVIDEND') ||
                         tt === 'DEMERGERAMT' || tt === 'BONUSAMT';
      const isCA = !isBuyLike && !isSellLike && !isDividend &&
                   ['BONUS','SPLIT BUY','SPLIT SELL','DEMERGERIN','DEMERGERBUY',
                    'DEMERGERSELL','DEMERGERAMT'].includes(tt);

      // Skip PF rows that are part of an intraday round-trip where the BUY
      // has a DP credit but the paired SELL has no DP debit — net = 0 in DP.
      if (pfIdsToExclude.has(p.id)) continue;

      if (isDividend) {
        b.portfolio.dividends_count++;
        b.portfolio.dividends_amount += Number(p.pf_qty ?? 0);
      } else if (isBuyLike) {
        // BUY-like: positive contribution to net. splitAdjust applies to any buy-like type.
        const qty = Math.max(0, rawQty - splitAdjust);
        b.portfolio.buys_qty += qty;
        b.portfolio.buys_count++;
        // Also tag as CA for display info if it's not a plain BUY
        if (isCA || (origTT !== 'BUY' && origTT !== 'BUYCR' && tt !== 'BUY' && tt !== 'BUYCR')) {
          b.portfolio.corporate_action_count++;
        }
      } else if (isSellLike) {
        // SELL-like: negative contribution to net.
        b.portfolio.sells_qty += -rawQty;
        b.portfolio.sells_count++;
      } else if (tt) {
        b.portfolio.other_count++;
      }
    }
    if (band.pfRows.length === 0 && band.dpRow) {
      // Unmatched DP-only band — use dpRow (dpRows is just [dpRow], avoid double-count)
      // SLB entries stay VISIBLE but are excluded from the DP net entirely.
      const isSlb = /\bSLB\b/i.test(band.dpRow.dp_description ?? '');
      const cr = Number(band.dpRow.dp_credit ?? 0); const dr = Number(band.dpRow.dp_debit ?? 0);
      if (!isSlb && cr > 0 && !dpIdsToExclude.has(band.dpRow.id) && !countedDpIds.has(band.dpRow.id)) { b.dp.credits_qty += cr; b.dp.credits_count++; countedDpIds.add(band.dpRow.id); }
      if (!isSlb && dr > 0 && !dpIdsToExclude.has(band.dpRow.id) && !countedDpIds.has(band.dpRow.id)) { b.dp.debits_qty += dr; b.dp.debits_count++; countedDpIds.add(band.dpRow.id); }
    } else {
      // Matched band — use dpRows (may have multiple DP entries for one PF group).
      // Dedupe by ID: a pooled bonus credit can appear in several split bands
      // (e.g. the 65-band and the 108-band share the same "By Bonus Issue" row);
      // count each DP credit at most once.
      for (const d of band.dpRows) {
        if (dpIdsToExclude.has(d.id)) continue; // skip intraday round-trip DP entries
        if (countedDpIds.has(d.id)) continue;   // already counted in another band
        if (/\bSLB\b/i.test(d.dp_description ?? '')) continue; // SLB excluded from DP net
        countedDpIds.add(d.id);
        const cr = Number(d.dp_credit ?? 0); const dr = Number(d.dp_debit ?? 0);
        if (cr > 0) { b.dp.credits_qty += cr; b.dp.credits_count++; }
        if (dr > 0) { b.dp.debits_qty += dr; b.dp.debits_count++; }
      }
    }
  }
  return b;
}

/* ─── AI Insight generator ────────────────────────────────────────────────
 * Analyzes the UNMATCHED (red) bands and surfaces *possible* matches the
 * matching engine deliberately did NOT make — near-misses. This is advisory
 * only: it never changes the reconciliation, it just explains why something
 * that looks like it should match wasn't matched, or where a grouped
 * (merger/aggregate) match could exist.
 */
export interface ReconInsight {
  kind: 'date-gap' | 'aggregate' | 'sign-flip' | 'info';
  title: string;
  detail: string;
}

function daysBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  if (!a || !b) return null;
  const da = new Date(a).getTime(), db = new Date(b).getTime();
  if (isNaN(da) || isNaN(db)) return null;
  return Math.abs(Math.round((da - db) / 86400000));
}

function generateInsights(bands: MatchBand[]): ReconInsight[] {
  const insights: ReconInsight[] = [];

  // Collect the unmatched PF rows and unmatched DP entries from red bands.
  const unmatchedPf: { row: ReconTxnPair; qty: number; isBuy: boolean; isSell: boolean }[] = [];
  const unmatchedDp: { dp: ReconTxnPair; qty: number; isCredit: boolean }[] = [];

  for (const band of bands) {
    if (band.colour !== 'red') continue;
    // Red band with PF rows → unmatched PF entries
    for (const p of band.pfRows) {
      const tt = (p.pf_txn_type ?? '').trim().toUpperCase();
      const qty = Math.abs(Number(p.pf_qty ?? 0));
      if (qty <= 0) continue;
      const isBuy  = tt.includes('BUY')  || tt === 'BONUS' || tt.includes('MERGER BUY');
      const isSell = tt.includes('SELL') || tt.includes('TRANSFER') || tt.includes('MERGER SELL');
      unmatchedPf.push({ row: p, qty, isBuy, isSell });
    }
    // Red DP-only band → unmatched DP entry
    if (band.pfRows.length === 0 && band.dpRow) {
      const cr = Number(band.dpRow.dp_credit ?? 0);
      const dr = Number(band.dpRow.dp_debit ?? 0);
      const qty = cr > 0 ? cr : dr;
      if (qty > 0) unmatchedDp.push({ dp: band.dpRow, qty, isCredit: cr > 0 });
    }
  }

  // ── Insight 1: date-gap near-miss ──
  // A PF entry and a DP entry with the SAME quantity and the SAME direction
  // (PF buy ↔ DP credit, PF sell ↔ DP debit) that were NOT matched because the
  // dates are far apart (outside the settlement window).
  const usedDp = new Set<string>();
  for (const pf of unmatchedPf) {
    for (const dp of unmatchedDp) {
      if (usedDp.has(dp.dp.id)) continue;
      if (Math.abs(pf.qty - dp.qty) >= 0.001) continue;
      const sameDir = (pf.isBuy && dp.isCredit) || (pf.isSell && !dp.isCredit);
      if (!sameDir) continue;
      const gap = daysBetween(pf.row.pf_txn_date, dp.dp.dp_txn_date);
      if (gap != null && gap > 8) {
        usedDp.add(dp.dp.id);
        const pfLabel = (pf.row.pf_txn_type ?? 'entry').trim();
        const dpDesc  = (dp.dp.dp_description ?? 'DP entry').trim();
        insights.push({
          kind: 'date-gap',
          title: `Quantity matches (${fmtQty(pf.qty)}) but dates are ${gap} days apart`,
          detail: `PF ${pfLabel} of ${fmtQty(pf.qty)} on ${fmtDate(pf.row.pf_txn_date)} and DP "${dpDesc}" of ${fmtQty(dp.qty)} on ${fmtDate(dp.dp.dp_txn_date)} have the same quantity and direction. They were NOT matched because the ${gap}-day gap is outside the settlement window. In real terms these may be the same movement, but the rule does not match them.`,
        });
      }
    }
  }

  // ── Insight 2: aggregate / merger grouping ──
  // Where several unmatched PF buys (or sells) sum to an unmatched DP entry —
  // common with merger buy/sell where the DP posts one aggregated credit/debit.
  const tryAggregate = (side: 'buy' | 'sell') => {
    const pfSide = unmatchedPf.filter(p => side === 'buy' ? p.isBuy : p.isSell);
    const dpSide = unmatchedDp.filter(d => side === 'buy' ? d.isCredit : !d.isCredit && !usedDp.has(d.dp.id));
    for (const dp of dpSide) {
      if (usedDp.has(dp.dp.id)) continue;
      // find the smallest subset of pfSide that sums to dp.qty (cap at 12 rows)
      const pool = pfSide.filter(p => !p.row.id || true).slice(0, 12);
      const n = pool.length;
      let best: typeof pool | null = null;
      for (let mask = 1; mask < (1 << n) && n <= 12; mask++) {
        let sum = 0; const subset: typeof pool = [];
        for (let bit = 0; bit < n; bit++) {
          if (mask & (1 << bit)) { subset.push(pool[bit]); sum += pool[bit].qty; }
          if (sum > dp.qty + 0.001) break;
        }
        if (Math.abs(sum - dp.qty) < 0.001 && subset.length >= 2) {
          if (!best || subset.length < best.length) best = subset;
        }
      }
      if (best) {
        usedDp.add(dp.dp.id);
        const parts = best.map(p => `${fmtQty(p.qty)} (${(p.row.pf_txn_type ?? '').trim()}, ${fmtDate(p.row.pf_txn_date)})`).join(' + ');
        const dpDesc = (dp.dp.dp_description ?? 'DP entry').trim();
        insights.push({
          kind: 'aggregate',
          title: `${best.length} PF ${side}s sum to a DP entry of ${fmtQty(dp.qty)}`,
          detail: `Grouping these PF ${side} entries — ${parts} — sums to ${fmtQty(dp.qty)}, which equals the unmatched DP "${dpDesc}" of ${fmtQty(dp.qty)} on ${fmtDate(dp.dp.dp_txn_date)}. This could be an aggregated (e.g. merger) movement, but the rule matches per-row and did not group them.`,
        });
      }
    }
  };
  tryAggregate('buy');
  tryAggregate('sell');

  // ── Insight 3: reverse aggregate — several DP entries sum to ONE PF entry ──
  // The mirror of Insight 2. A single PF buy/sell can correspond to multiple DP
  // credits/debits that together sum to the PF quantity (e.g. shares delivered
  // across two brokers/settlements). Screenshot case: PF BUY 5,200 vs DP
  // credits 3,000 + 2,200.
  const tryAggregateDp = (side: 'buy' | 'sell') => {
    const pfSide = unmatchedPf.filter(p => side === 'buy' ? p.isBuy : p.isSell);
    const dpPoolAll = unmatchedDp.filter(d =>
      (side === 'buy' ? d.isCredit : !d.isCredit) && !usedDp.has(d.dp.id));
    for (const pf of pfSide) {
      if (usedDp.has(`pf:${pf.row.id}`)) continue;
      const pool = dpPoolAll.filter(d => !usedDp.has(d.dp.id)).slice(0, 12);
      const n = pool.length;
      let best: typeof pool | null = null;
      for (let mask = 1; mask < (1 << n) && n <= 12; mask++) {
        let sum = 0; const subset: typeof pool = [];
        for (let bit = 0; bit < n; bit++) {
          if (mask & (1 << bit)) { subset.push(pool[bit]); sum += pool[bit].qty; }
          if (sum > pf.qty + 0.001) break;
        }
        if (Math.abs(sum - pf.qty) < 0.001 && subset.length >= 2) {
          if (!best || subset.length < best.length) best = subset;
        }
      }
      if (best) {
        best.forEach(d => usedDp.add(d.dp.id));
        usedDp.add(`pf:${pf.row.id}`);
        const parts = best.map(d => `${fmtQty(d.qty)} ("${(d.dp.dp_description ?? 'DP').trim()}", ${fmtDate(d.dp.dp_txn_date)})`).join(' + ');
        const pfLabel = (pf.row.pf_txn_type ?? 'entry').trim();
        insights.push({
          kind: 'aggregate',
          title: `${best.length} DP entries sum to PF ${pfLabel} of ${fmtQty(pf.qty)}`,
          detail: `The unmatched PF ${pfLabel} of ${fmtQty(pf.qty)} on ${fmtDate(pf.row.pf_txn_date)} equals the sum of ${best.length} unmatched DP ${side === 'buy' ? 'credits' : 'debits'} — ${parts} = ${fmtQty(pf.qty)}. These DP entries together could correspond to this single PF entry (e.g. delivery split across settlements/brokers), but the rule matches per-row and did not group them.`,
        });
      }
    }
  };
  tryAggregateDp('buy');
  tryAggregateDp('sell');

  return insights;
}

function SplitTxnTables({ bands, visibleBreakdown, row, onCounts }: {
  bands: MatchBand[];
  visibleBreakdown: ReturnType<typeof buildVisibleBreakdown>;
  row: ReconRow;
  onCounts?: (pf: number, dp: number) => void;
}) {
  // Red bands split into: DP-only rows still inside the PF-arrival window
  // ("waiting for entries") vs genuinely unmatched.
  const redBands = bands.filter(b => b.colour === 'red');
  const waitingCount = redBands.filter(b => b.pfRows.length === 0 && b.dpRow && isWithinDpWaitingWindow(b.dpRow.dp_txn_date)).length;
  const totalUnmatched = redBands.length - waitingCount;

  // Count only what's actually rendered — for matched bands use dpRows.length,
  // for unmatched DP-only bands use 1 (dpRow). Never count both to avoid double-count.
  const pfCount = bands.reduce((s, b) => s + b.pfRows.length, 0);
  const dpCount = bands.reduce((s, b) =>
    s + (b.pfRows.length === 0 && b.dpRow ? 1 : b.dpRows.length), 0);

  // Report visible counts to parent so the top header stays in sync
  useMemo(() => { onCounts?.(pfCount, dpCount); }, [pfCount, dpCount]);

  return (
    <div>
      {totalUnmatched > 0 && (
        <div
          className="px-3 py-1.5 text-[11px] flex items-center gap-2"
          style={{
            background: 'color-mix(in srgb, var(--status-warn) 14%, transparent)',
            color: 'var(--text-strong)',
            borderBottom: '1px solid var(--glass-border)',
          }}
        >
          <span
            className="px-1.5 py-0.5 rounded font-semibold text-white text-[10px] uppercase tracking-wider"
            style={{ background: 'var(--status-warn)' }}
          >
            {totalUnmatched} unmatched
          </span>
          <span>Unmatched entries highlighted in red — no counterpart found within the settlement window.</span>
        </div>
      )}

      {waitingCount > 0 && (
        <div
          className="px-3 py-1.5 text-[11px] flex items-center gap-2"
          style={{
            background: 'color-mix(in srgb, var(--status-warn) 10%, transparent)',
            color: 'var(--text-strong)',
            borderBottom: '1px solid var(--glass-border)',
          }}
        >
          <span
            className="px-1.5 py-0.5 rounded font-semibold text-white text-[10px] uppercase tracking-wider"
            style={{ background: 'var(--status-warn)' }}
          >
            {waitingCount} waiting
          </span>
          <span>DP entries awaiting their PF counterpart — still within the 2-day arrival window (4 days across a weekend).</span>
        </div>
      )}

      {/* Section labels + column headers — single unified row */}
      <div className="flex" style={{ borderBottom: '1px solid var(--glass-border)', background: 'var(--bg-elevated)' }}>
        {/* PF header */}
        <div className="flex-1 min-w-0" style={{ borderRight: '2px solid var(--glass-border-h)' }}>
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold"
               style={{ color: 'var(--status-info)', borderBottom: '1px solid var(--glass-border)' }}>
            Portfolio · {pfCount} txn
          </div>
          <table className="w-full text-xs table-fixed">
            <PfColGroup />
            <thead style={{ color: 'var(--text-strong)' }}>
              <tr>
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Date</th>
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Type</th>
                <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Qty</th>
                <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Price / Value</th>
              </tr>
            </thead>
          </table>
        </div>
        {/* DP header */}
        <div className="flex-1 min-w-0">
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold"
               style={{ color: 'var(--status-ok)', borderBottom: '1px solid var(--glass-border)' }}>
            Depository (DP) · {dpCount} txn
          </div>
          <table className="w-full text-xs table-fixed">
            <DpColGroup />
            <thead style={{ color: 'var(--text-strong)' }}>
              <tr>
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Date</th>
                <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold">Description</th>
                <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Credit</th>
                <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-semibold">Debit</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {/* Single chronologically-sorted list — matched bands AND
          unmatched-DP-only bands interleaved by date, never two
          separate render passes. */}
      {bands.map((band, i) => (
        band.slb
          ? <SlbGroupBandRow key={i} band={band} />
          : band.bonusPool
            ? <BonusPoolBandRow key={i} band={band} />
            : band.pfRows.length > 0
              ? <MatchedBandRow key={i} band={band} />
              : <UnmatchedDpBandRow key={band.dpRow!.id} dp={band.dpRow!} />
      ))}
      <AiInsightBar insights={generateInsights(bands)} />
      <BreakdownPanel row={row} visibleBreakdown={visibleBreakdown} hasUnmatched={totalUnmatched > 0} />
    </div>
  );
}

/**
 * Collapsible "AI Insight" bar. Sits under the transaction list and above the
 * calculation bar. Shows a count of possible matches (near-misses) the engine
 * did NOT make; clicking expands the explanations. Advisory only — nothing here
 * changes the reconciliation.
 */
function AiInsightBar({ insights }: { insights: ReconInsight[] }) {
  const [open, setOpen] = useState(false);
  const count = insights.length;

  const iconFor = (k: ReconInsight['kind']) =>
    k === 'date-gap' ? '📅' : k === 'aggregate' ? '➕' : k === 'sign-flip' ? '↔' : 'ℹ️';

  return (
    <div style={{ borderBottom: '2px solid var(--glass-border-h)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition"
        style={{
          background: 'linear-gradient(90deg, color-mix(in srgb, #8b5cf6 10%, transparent), transparent)',
          cursor: 'pointer',
        }}
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-md text-[11px] font-bold"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff' }}>
          AI
        </span>
        <span className="text-sm font-semibold" style={{ color: '#5b21b6' }}>
          AI Insight
        </span>
        {count > 0 ? (
          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
            style={{ background: 'color-mix(in srgb, #8b5cf6 18%, transparent)', color: '#5b21b6' }}>
            {count} possible {count === 1 ? 'match' : 'matches'} to review
          </span>
        ) : (
          <span className="text-[11px]" style={{ color: 'var(--text-400)' }}>
            no near-miss patterns detected
          </span>
        )}
        <span className="ml-auto text-[11px]" style={{ color: '#8b5cf6' }}>
          {count > 0 ? (open ? '▲ hide' : '▼ show') : ''}
        </span>
      </button>

      {open && count > 0 && (
        <div className="px-4 pb-3 pt-1 space-y-2"
          style={{ background: 'color-mix(in srgb, #8b5cf6 5%, transparent)' }}>
          {insights.map((ins, i) => (
            <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg"
              style={{ background: 'var(--bg-elevated)', border: '1px solid color-mix(in srgb, #8b5cf6 25%, transparent)' }}>
              <span className="text-base leading-none mt-0.5">{iconFor(ins.kind)}</span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold" style={{ color: 'var(--text-strong)' }}>{ins.title}</div>
                <div className="text-[12px] mt-0.5 leading-relaxed" style={{ color: 'var(--text-300)' }}>{ins.detail}</div>
              </div>
            </div>
          ))}
          <div className="text-[10px] px-1" style={{ color: 'var(--text-400)', fontStyle: 'italic' }}>
            These are advisory near-misses only — the reconciliation is unchanged. Review manually before acting.
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Band row renderers ─────────────────────────────────────────────── */

/**
 * Renders one match band as two side-by-side cells.
 * Green bands: PF rows on the left, DP row on the right, both tinted green.
 * The DP cell is vertically centred against the PF group (border spans all rows).
 * Red bands: PF rows on the left tinted red, DP cell empty.
 * Dividend bands: PF rows tinted green, DP cell shows "—" with green tint.
 */
/**
 * Colour indicator approach:
 * The 3px coloured bar lives on the LEFT edge of the outer PF div — NOT on
 * the <tr>. This means every row type (matched green, matched red, unmatched
 * DP) all use the same structural position for the colour bar, so they always
 * align in one straight vertical line.
 *
 * Centre divider = PF div's right border (always 2px grey). No left border on
 * the DP div so there's no double-border gap.
 */
function MatchedBandRow({ band }: { band: MatchBand }) {
  const isGreen = band.colour === 'green';
  // A dividend band is green, has no DP row, and its PF rows are dividend-type.
  // (partialIntradayBuy is also green + no DP row but is NOT a dividend.)
  const isDividend = band.dpRow == null && isGreen && !band.partialIntradayBuy &&
    band.pfRows.length > 0 && band.pfRows.every(r => {
      const t = (r.pf_txn_type ?? '').toUpperCase().trim();
      return DIVIDEND_TYPES.has(t) || t.includes('DIVIDEND');
    });
  // Rule 3: track which PF row is expanded (click to see full details)
  const [expandedPfId, setExpandedPfId] = useState<string | null>(null);
  // Group collapse: multi-row groups are collapsed by default, showing only summary
  const [groupExpanded, setGroupExpanded] = useState(false);
  // DP side group collapse
  const [dpGroupExpanded, setDpGroupExpanded] = useState(false);
  const [expandedDpId, setExpandedDpId] = useState<string | null>(null);

  const accent   = isGreen ? 'var(--status-ok)' : 'var(--status-err)';
  const pfBg     = isGreen
    ? 'color-mix(in srgb, var(--status-ok) 12%, transparent)'
    : 'color-mix(in srgb, var(--status-err) 12%, transparent)';
  const dpBg     = isGreen
    ? 'color-mix(in srgb, var(--status-ok) 12%, transparent)'
    : 'transparent';
  // Rule 2: intraday colours
  // SELL side: orange (#f97316) — partial intraday sell highlight
  // BUY side:  light sky blue (#7dd3fc) — partial intraday buy highlight
  const INTRADAY_BG     = '#f97316'; // orange for intraday SELL
  const INTRADAY_BUY_BG = '#7dd3fc'; // light sky blue for intraday BUY
  const INTRADAY_TEXT   = '#000000'; // black text — readable on both yellow and light blue
  const PARTIAL_INTRADAY_BG = '#c4b5fd'; // light violet — distinct colour for PARTIAL intraday (buy + its pairing sell)

  return (
    <div className="flex" style={{ borderBottom: '2px solid var(--glass-border-h)', alignItems: 'stretch' }}>
      {/* PF side — colour bar on LEFT of this div, grey divider on RIGHT */}
      <div
        className="flex-1 min-w-0"
        style={{ borderLeft: `3px solid ${accent}`, borderRight: '2px solid var(--glass-border-h)' }}
      >
        <table className="w-full text-xs table-fixed">
          <PfColGroup />
          <tbody>
            {/* Group collapse: when multiple rows share same code, show summary by default */}
            {band.isGroup && band.pfRows.length > 1 && !groupExpanded && !band.bonusPool ? (
              // Collapsed summary row
              (() => {
                const totalQty = band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
                const firstRow = band.pfRows[0];
                const rawType  = (firstRow.pf_txn_type ?? '').toUpperCase().trim();
                const isIntraday = band.partialIntradaySell || band.pfRows.some(r => band.intradayIds.has(r.id));
                const isSellRow  = rawType === 'SELL';
                const summaryBg  = band.partialIntradaySell ? INTRADAY_BG : (isIntraday ? (isSellRow ? INTRADAY_BG : INTRADAY_BUY_BG) : pfBg);
                const summaryText = (band.partialIntradaySell || isIntraday) ? INTRADAY_TEXT : 'var(--text-strong)';
                const isDivGroup = DIVIDEND_TYPES.has(rawType) || rawType.includes('DIVIDEND');
                // Show every distinct PF type in the group (e.g. a mixed OMC
                // leftover group has both "BUY" and "Merger Sell").
                const groupTypeLabel = [...new Set(band.pfRows.map(r => (r.pf_txn_type ?? '').trim()).filter(Boolean))].join(' + ');
                return (
                  <tr
                    style={{ background: summaryBg, color: summaryText, cursor: 'pointer' }}
                    title="Click to expand all rows"
                    onClick={() => setGroupExpanded(true)}
                  >
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(firstRow.pf_txn_date)}</td>
                    <td className="px-3 py-2">
                      <span>{groupTypeLabel || firstRow.pf_txn_type}</span>
                      {band.sdoMatch && (
                        <span className="ml-1 rounded px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700">SDO</span>
                      )}
                      {band.offmarketMatch && (
                        <span
                          className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: 'color-mix(in srgb, #f97316 18%, transparent)', color: '#c2410c', border: '1px solid color-mix(in srgb, #f97316 40%, transparent)' }}
                          title="Offmarket / CMOTS match: matched by quantity (single or grouped) with the DP entry dated on/after the PF entry"
                        >
                          OFM
                        </span>
                      )}
                      {band.offmarketMergerCase && (
                        <span
                          className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: 'color-mix(in srgb, #f97316 18%, transparent)', color: '#c2410c', border: '1px solid color-mix(in srgb, #f97316 40%, transparent)' }}
                          title="Offmarket merger conversion: BUY + Merger Sell + Merger Buy of equal qty; the DP delivery matches the MERGER BUY (not the plain BUY)"
                        >
                          OMC
                        </span>
                      )}
                      {band.bmsmb && (
                        <span
                          className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: 'color-mix(in srgb, #0891b2 18%, transparent)', color: '#0e7490', border: '1px solid color-mix(in srgb, #0891b2 40%, transparent)' }}
                          title="BMSMB: Buy + Merger Sell + Merger Buy triple where the DP credit carries the plain BUY's settlement code — the MERGER BUY is matched to it (not the plain buy)"
                        >
                          BMSMB
                        </span>
                      )}
                      {band.sellIntradayOverlap != null && band.sellIntradayOverlap > 0 && (
                        <span
                          className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                          style={{ background: 'color-mix(in srgb, #7c3aed 15%, transparent)', color: '#6d28d9', border: '1px solid color-mix(in srgb, #7c3aed 35%, transparent)' }}
                          title={`Net reconciliation: ${fmtQty(band.sellIntradayOverlap)} intraday (paired with same-day buys), remainder settled to the DP debit`}
                        >
                          {fmtQty(band.sellIntradayOverlap)} intraday + {fmtQty(totalQty - band.sellIntradayOverlap)} → DP
                        </span>
                      )}
                      {firstRow.pf_particulars && (
                        <span
                          className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(0,0,0,0.06)', color: 'var(--text-200)', border: '1px solid var(--glass-border)' }}
                        >{firstRow.pf_particulars}</span>
                      )}
                      <span
                        className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                        style={{ background: `color-mix(in srgb, ${accent} 20%, transparent)`, color: accent }}
                      >
                        {isDivGroup
                          ? `${band.pfRows.length} dividends`
                          : `${band.pfRows.length} rows · total: ${fmtQty(totalQty)}`}
                      </span>
                      {!isGreen && (
                        <span className="ml-1.5 text-[10px] font-medium" style={{ color: 'var(--status-err)' }}>· unmatched</span>
                      )}
                      {isIntraday && (
                        <span className="ml-1.5 text-[10px] font-semibold" style={{ color: summaryText }}>· intraday trade</span>
                      )}
                      <span className="ml-1.5 text-[10px]" style={{ color: 'var(--text-400)' }}>▼ expand</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtQty(totalQty)}</td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>—</td>
                  </tr>
                );
              })()
            ) : (
              <>
                {/* Collapse button when expanded */}
                {band.isGroup && band.pfRows.length > 1 && groupExpanded && (
                  <tr style={{ background: pfBg, cursor: 'pointer' }} onClick={() => setGroupExpanded(false)}>
                    <td colSpan={4} className="px-3 py-1 text-[10px]" style={{ color: 'var(--text-400)' }}>
                      ▲ collapse {band.pfRows.length} rows
                    </td>
                  </tr>
                )}
                {band.pfRows.map((p, rowIdx) => {
              const isLastInGroup = rowIdx === band.pfRows.length - 1;
              const rawType    = (p.pf_txn_type ?? '').toUpperCase().trim();
              const isDivRow   = DIVIDEND_TYPES.has(rawType);
              const isPartialIntraday = band.partialIntradayBuy || (band.partialIntradayPairIds?.has(p.id) ?? false);
              const isIntraday = band.partialIntradaySell || band.intradayIds.has(p.id) || isPartialIntraday;
              const isExpanded  = expandedPfId === p.id;
              const isSellRow   = (p.pf_txn_type ?? '').toUpperCase() === 'SELL';
              const intradayBg  = isPartialIntraday ? PARTIAL_INTRADAY_BG : (isSellRow ? INTRADAY_BG : INTRADAY_BUY_BG);

              // ── Double-matching partial intraday split display ─────────────
              if (band.splitIntraday && !isDivRow && rowIdx === 0) {
                const buyTotal   = band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0);
                const sellQty    = band.splitIntraday.sellQty;
                const remainder  = buyTotal - sellQty;
                const firstRow   = band.pfRows[0];
                const [expanded, setExpanded] = [expandedPfId === (firstRow.id + '_split'), (v: boolean) => setExpandedPfId(v ? firstRow.id + '_split' : null)];
                return (
                  <>
                    {/* Summary row — shows remainder (154) matched to DP credit, with expand to see split */}
                    <tr key={p.id + '_summary'}
                      style={{ background: pfBg, color: 'var(--text-strong)', cursor: 'pointer' }}
                      onClick={() => setExpanded(!expanded)}
                    >
                      <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(firstRow.pf_txn_date)}</td>
                      <td className="px-3 py-2">
                        <span>{firstRow.pf_txn_type}</span>
                        {firstRow.pf_particulars && (
                          <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(0,0,0,0.06)', color: 'var(--text-200)', border: '1px solid var(--glass-border)' }}>
                            {firstRow.pf_particulars}
                          </span>
                        )}
                        <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                          style={{ background: `color-mix(in srgb, ${accent} 20%, transparent)`, color: accent }}>
                          partial intraday · {expanded ? '▲ collapse' : '▼ expand'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtQty(remainder)}</td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>
                        {firstRow.pf_trade_price != null ? `₹${Number(firstRow.pf_trade_price).toFixed(2)} tot ₹${(remainder * Number(firstRow.pf_trade_price)).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                    {expanded && <>
                      {/* Orange row: BUY 53 (intraday portion) — DP side shows debit 53 */}
                      <tr key={p.id + '_intraday'}
                        style={{ background: INTRADAY_BG, color: INTRADAY_TEXT }}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap pl-6">↳ {fmtDate(firstRow.pf_txn_date)}</td>
                        <td className="px-3 py-2">
                          <span>{firstRow.pf_txn_type}</span>
                          <span className="ml-1.5 text-[10px] font-semibold" style={{ color: INTRADAY_TEXT }}>· intraday</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtQty(sellQty)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'rgba(0,0,0,0.5)' }}>—</td>
                      </tr>
                      {/* Plain row: BUY 154 (net delivery) */}
                      <tr key={p.id + '_remainder'}
                        style={{ background: pfBg, color: 'var(--text-strong)' }}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap pl-6">↳ {fmtDate(firstRow.pf_txn_date)}</td>
                        <td className="px-3 py-2">
                          <span>{firstRow.pf_txn_type}</span>
                          <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                            style={{ background: `color-mix(in srgb, var(--status-ok) 20%, transparent)`, color: 'var(--status-ok)' }}>
                            net delivery
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtQty(remainder)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>
                          {firstRow.pf_trade_price != null ? `₹${Number(firstRow.pf_trade_price).toFixed(2)} tot ₹${(remainder * Number(firstRow.pf_trade_price)).toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    </>}
                  </>
                );
              }
              if (band.splitIntraday && rowIdx > 0) return null;

              const rowBg       = isIntraday ? intradayBg : pfBg;
              const rowText     = isIntraday ? INTRADAY_TEXT : 'var(--text-strong)';
              return (
                <tr key={p.id}
                  style={{ background: rowBg, color: rowText, cursor: 'pointer' }}
                  title="Click to see full details"
                  onClick={() => setExpandedPfId(isExpanded ? null : p.id)}
                >
                  <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(p.pf_txn_date)}</td>
                  <td className="px-3 py-2">
                    {isExpanded ? (
                      <div className="text-[10px] leading-relaxed py-0.5" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div><strong>ISIN:</strong> {p._displayIsin ?? p.canonical_isin}</div>
                        <div><strong>Date:</strong> {fmtDate(p.pf_txn_date)}</div>
                        <div><strong>Source:</strong> {p.pf_source ?? '—'}</div>
                        <div><strong>Type:</strong> {p.pf_txn_type}</div>
                        <div><strong>Qty:</strong> {fmtQty(p.pf_qty)}</div>
                        <div><strong>Price:</strong> {p.pf_trade_price != null ? `₹${Number(p.pf_trade_price).toFixed(4)}` : '—'}</div>
                        <div><strong>Charges:</strong> {p.pf_charges != null ? `₹${Number(p.pf_charges).toFixed(2)}` : '0'}</div>
                        <div><strong>Value:</strong> {p.pf_trade_value != null ? `₹${Number(p.pf_trade_value).toFixed(2)}` : '—'}</div>
                        <div><strong>Particulars:</strong> {p.pf_particulars ?? '—'}</div>
                        <div className="mt-0.5 text-[9px]" style={{ color: 'var(--text-400)', fontStyle: 'italic' }}>Click to collapse ↑</div>
                      </div>
                    ) : (
                      <>
                        <span>{p.pf_txn_type}</span>
                        {band.bbcMatch && (
                        <span className="ml-1 rounded px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-purple-100 text-purple-700">BBC</span>
                      )}
                      {band.sellBuyBack && (
                          <span
                            className="ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'color-mix(in srgb, #16a34a 15%, transparent)', color: '#15803d', border: '1px solid color-mix(in srgb, #16a34a 35%, transparent)' }}
                            title="Sell buy-back: matched against the DP buy-back debit and tender-offer-cancellation credit (buyback = sell + cancellation)"
                          >
                            $
                          </span>
                        )}
                        {band.offmarketMatch && (
                          <span
                            className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'color-mix(in srgb, #f97316 18%, transparent)', color: '#c2410c', border: '1px solid color-mix(in srgb, #f97316 40%, transparent)' }}
                            title="Offmarket / CMOTS match: matched by quantity (single or grouped) with the DP entry dated on/after the PF entry"
                          >
                            OFM
                          </span>
                        )}
                        {band.offmarketMergerCase && (
                          <span
                            className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'color-mix(in srgb, #f97316 18%, transparent)', color: '#c2410c', border: '1px solid color-mix(in srgb, #f97316 40%, transparent)' }}
                            title="Offmarket merger conversion: same-day BUY + Merger Sell + Merger Buy of equal qty; the DP delivery matches the MERGER BUY (not the plain BUY)"
                          >
                            OMC
                          </span>
                        )}
                        {band.bmsmb && (
                          <span
                            className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'color-mix(in srgb, #0891b2 18%, transparent)', color: '#0e7490', border: '1px solid color-mix(in srgb, #0891b2 40%, transparent)' }}
                            title="BMSMB: Buy + Merger Sell + Merger Buy triple where the DP credit carries the plain BUY's settlement code — the MERGER BUY is matched to it (not the plain buy)"
                          >
                            BMSMB
                          </span>
                        )}
                        {p.pf_particulars && (
                          <span
                            className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--color-background-secondary, rgba(0,0,0,0.06))', color: 'var(--text-200)', border: '1px solid var(--glass-border)' }}
                            title="Settlement code (Particulars)"
                          >
                            {p.pf_particulars}
                          </span>
                        )}
                        {isIntraday && (
                          <span
                            className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                            style={{ background: isSellRow ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.15)', color: rowText }}
                          >
                            {isPartialIntraday ? '· partial intraday' : '· intraday trade'}
                          </span>
                        )}
                        {band.isGroup && isLastInGroup && !band.bonusPool && (
                          <span
                            className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                            style={{ background: `color-mix(in srgb, ${accent} 20%, transparent)`, color: accent }}
                          >
                            group total: {fmtQty(band.pfRows.reduce((s, r) => s + Math.abs(Number(r.pf_qty) ?? 0), 0))}
                          </span>
                        )}
                        {!isGreen && !isDivRow && (
                          <span className="ml-1.5 text-[10px] font-medium" style={{ color: 'var(--status-err)' }}>· unmatched</span>
                        )}
                        {isDivRow && (
                          <span className="ml-1.5 text-[10px] font-medium" style={{ color: 'var(--status-ok)' }}>· auto-matched</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtQty(p.pf_qty)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.pf_trade_price != null ? `₹${Number(p.pf_trade_price).toFixed(2)}` : '—'}
                    {p.pf_trade_value != null && (
                      <span className="ml-1 text-[10px]" style={{ color: 'var(--text-200)' }}>
                        tot ₹{Number(p.pf_trade_value).toFixed(2)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* DP side — NO left border (centre divider comes from PF's right border only) */}
      <div
        className="flex-1 min-w-0"
        style={{ background: dpBg }}
      >
        {band.dpRow ? (
          <table className="w-full text-xs table-fixed">
            <DpColGroup />
            <tbody>
              {(() => {
                const dpEntries = band.dpRows.length > 0 ? band.dpRows : [band.dpRow];
                const totalCredit = dpEntries.reduce((s, d) => s + (d.dp_credit ?? 0), 0);
                const totalDebit  = dpEntries.reduce((s, d) => s + (d.dp_debit ?? 0), 0);
                const showCollapsed = dpEntries.length > 1 && !dpGroupExpanded;
                return (
                  <>
                    {showCollapsed ? (
                      <tr
                        style={{ color: 'var(--text-strong)', cursor: 'pointer' }}
                        title="Click to expand all DP entries"
                        onClick={() => setDpGroupExpanded(true)}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(dpEntries[0].dp_txn_date)}</td>
                        <td className="px-3 py-2 truncate max-w-0">
                          <span className="truncate"><DpDescriptionCell desc={dpEntries[0].dp_description} /></span>
                          {band.splitSubDivMatch && (
                            <span
                              className="ml-1.5 inline-block align-middle rounded-sm"
                              style={{ width: '11px', height: '11px', background: '#8b5cf6', border: '1px solid #6d28d9' }}
                              title="Special match: Split Buy/Sell ↔ Sub Division / Corporate Actions (quantity-only, no date window)"
                            />
                          )}
                          <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                            style={{ background: `color-mix(in srgb, var(--status-ok) 20%, transparent)`, color: 'var(--status-ok)' }}>
                            {dpEntries.length} entries
                          </span>
                          <span className="ml-1 text-[10px]" style={{ color: 'var(--text-400)' }}>▼ expand</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{totalCredit > 0 ? fmtQty(totalCredit) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{totalDebit  > 0 ? fmtQty(totalDebit)  : '—'}</td>
                      </tr>
                    ) : (
                      <>
                        {dpEntries.length > 1 && (
                          <tr style={{ cursor: 'pointer' }} onClick={() => setDpGroupExpanded(false)}>
                            <td colSpan={4} className="px-3 py-1 text-[10px]" style={{ color: 'var(--text-400)' }}>
                              ▲ collapse {dpEntries.length} entries
                            </td>
                          </tr>
                        )}
                        {dpEntries.map((dpEntry) => {
                          const dpExpanded = expandedDpId === dpEntry.id;
                          const crNum = Number(dpEntry.dp_credit ?? 0);
                          const drNum = Number(dpEntry.dp_debit ?? 0);
                          const isCredit = crNum !== 0;
                          const dpQty = isCredit ? crNum : drNum;
                          const dpDir = isCredit ? 'Credit' : 'Debit';
                          return (
                          <tr key={dpEntry.id} style={{ color: 'var(--text-strong)' }}>
                            <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(dpEntry.dp_txn_date)}</td>
                            <td className="px-3 py-2 truncate max-w-0 cursor-pointer select-text"
                              title="Click to see details"
                              onClick={() => setExpandedDpId(dpExpanded ? null : dpEntry.id)}>
                              {dpExpanded ? (
                                <div className="text-[10px] leading-relaxed py-0.5" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  <div><strong>ISIN:</strong> {dpEntry._displayIsin ?? dpEntry.canonical_isin}</div>
                                  <div><strong>Date:</strong> {fmtDate(dpEntry.dp_txn_date)}</div>
                                  <div><strong>{dpDir}:</strong> {fmtQty(dpQty)}</div>
                                  <div><strong>Description:</strong> <DpDescriptionCell desc={dpEntry.dp_description} /></div>
                                  {dpEntry.is_tier3_match && (
                                    <div className="mt-0.5 text-[10px] font-semibold" style={{ color: '#92400e' }}>⚑ Tier-3 pledge-recovery</div>
                                  )}
                                  <div className="mt-0.5 text-[9px]" style={{ color: 'var(--text-400)', fontStyle: 'italic' }}>Click to collapse ↑</div>
                                </div>
                              ) : (
                                <>
                                  <DpDescriptionCell desc={dpEntry.dp_description} />
                                  {band.splitSubDivMatch && (
                                    <span
                                      className="ml-1.5 inline-block align-middle rounded-sm"
                                      style={{ width: '11px', height: '11px', background: '#8b5cf6', border: '1px solid #6d28d9' }}
                                      title="Special match: Split Buy/Sell ↔ Sub Division / Corporate Actions (quantity-only, no date window)"
                                    />
                                  )}
                                  {dpEntry.is_tier3_match && (
                                    <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ background: 'color-mix(in srgb, #f59e0b 18%, transparent)', color: '#92400e', whiteSpace: 'nowrap' }}
                                      title="Tier-3 pledge-recovery: NUT2PU+47 hidden row admitted via exception list">
                                      ⚑ Tier-3 pledge-recovery
                                    </span>
                                  )}
                                  {band.bonusNarrationMatch && (
                                    <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ background: 'color-mix(in srgb, #8b5cf6 18%, transparent)', color: '#5b21b6', whiteSpace: 'nowrap' }}
                                      title="Bonus narration match (T+8): PF bonus matched to aggregated By Corporate Action / By Bonus credits within 8 days">
                                      ★ bonus narration match (T+8)
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {dpEntry.dp_credit != null && dpEntry.dp_credit !== 0 ? fmtQty(dpEntry.dp_credit) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {dpEntry.dp_debit != null && dpEntry.dp_debit !== 0 ? fmtQty(dpEntry.dp_debit) : '—'}
                            </td>
                          </tr>
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })()}
            </tbody>
          </table>
        ) : (
          <div className="px-3 py-2 text-[11px]" style={{ color: isDividend ? 'var(--status-ok)' : 'var(--text-400)' }}>
            {isDividend ? 'Auto-matched (dividend)' : '—'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Bonus-Issue pool drawdown renderer. Breaks the pooled DP credit(s) and the
 * PF bonus rows into aligned SUB-ENTRIES so each matched line pairs a PF
 * portion with an equal DP portion, and each shortfall line is a red PF-only
 * sub-entry. Every sub-entry names its parent (e.g. "sub-entry of 373").
 */
function BonusPoolBandRow({ band }: { band: MatchBand }) {
  const pool = band.bonusPool!;
  const allocs = pool.allocations;
  const anyMatched = allocs.some(a => a.matched);
  const accent = anyMatched ? 'var(--status-ok)' : 'var(--status-err)';
  const [expanded, setExpanded] = useState(false);

  const greenBg = 'color-mix(in srgb, var(--status-ok) 12%, transparent)';
  const redBg   = 'color-mix(in srgb, var(--status-err) 12%, transparent)';
  const intraBg = 'color-mix(in srgb, #f97316 18%, transparent)';
  const overlapBg = 'color-mix(in srgb, #a855f7 22%, transparent)'; // violet: PARTIAL intraday (matched to DP AND offsets the buy)

  // Summary stats for the collapsed view.
  const fmtNum = (n: number) => fmtQty(n);
  const pfAllocs    = allocs.filter(a => a.pfRow); // exclude DP-only shortfall
  const matchedQty  = allocs.filter(a => a.matched && !a.intraday && !a.dpOnly).reduce((s, a) => s + a.qty, 0);
  const intradayQty = allocs.filter(a => a.intraday).reduce((s, a) => s + a.qty, 0);
  const shortQty    = allocs.filter(a => !a.matched && !a.intraday && !a.dpOnly).reduce((s, a) => s + a.qty, 0);
  const dpShortQty  = allocs.filter(a => a.dpOnly).reduce((s, a) => s + a.qty, 0);
  const pfParents   = Array.from(new Set(pfAllocs.map(a => a.pfRow!.id)));
  const pfTotal     = pfAllocs.reduce((s, a) => s + a.qty, 0);
  const firstPf     = pfAllocs[0]?.pfRow;
  const dpParents   = Array.from(new Set(allocs.filter(a => a.dpRow).map(a => a.dpRow!.id)));
  const dpTotal     = pool.poolTotal;
  const firstDp     = allocs.find(a => a.dpRow)?.dpRow;

  const parentTag = (label: string) => (
    <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
      style={{ background: 'var(--bg-surface)', color: 'var(--text-400)', border: '1px solid var(--glass-border)' }}>
      {label}
    </span>
  );

  const summaryBadge = (bg: string, color: string, text: string) => (
    <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded" style={{ background: bg, color }}>{text}</span>
  );

  return (
    <div className="flex" style={{ borderBottom: '2px solid var(--glass-border-h)', alignItems: 'stretch' }}>
      {/* PF side */}
      <div className="flex-1 min-w-0"
        style={{ borderLeft: `3px solid ${accent}`, borderRight: '2px solid var(--glass-border-h)' }}>
        <table className="w-full text-xs table-fixed">
          <PfColGroup />
          <tbody>
            {!expanded ? (
              <tr style={{ background: anyMatched ? greenBg : redBg, color: 'var(--text-strong)', cursor: 'pointer' }} title="Click to expand sub-entries" onClick={() => setExpanded(true)}>
                <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(firstPf?.pf_txn_date)}</td>
                <td className="px-3 py-2">
                  <span>{firstPf?.pf_txn_type}</span>
                  {matchedQty  > 0 && summaryBadge('color-mix(in srgb, var(--status-ok) 18%, transparent)', 'var(--status-ok)', `matched ${fmtNum(matchedQty)}`)}
                  {intradayQty > 0 && summaryBadge('color-mix(in srgb, #f97316 25%, transparent)', '#c2410c', `intraday ${fmtNum(intradayQty)}`)}
                  {shortQty    > 0 && summaryBadge('color-mix(in srgb, var(--status-err) 18%, transparent)', 'var(--status-err)', `unmatched ${fmtNum(shortQty)}`)}
                  <span className="ml-1.5 text-[10px]" style={{ color: 'var(--text-400)' }}>· {pfParents.length} rows ▼ expand</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtNum(pfTotal)}</td>
                <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>—</td>
              </tr>
            ) : (
            <>
            {allocs.map((a, idx) => {
              const matched = a.matched;
              const isSub = a.pfSplit; // parent PF row was broken up
              if (a.dpOnly || !a.pfRow) {
                // DP-only shortfall: blank PF row (aligns with the red DP entry).
                return (
                  <tr key={`pf-${idx}`} style={{ background: 'transparent', color: 'var(--text-400)' }}>
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">—</td>
                    <td className="px-3 py-2 text-[11px]" style={{ fontStyle: 'italic' }}>no PF counterpart</td>
                    <td className="px-3 py-2 text-right tabular-nums">—</td>
                    <td className="px-3 py-2 text-right tabular-nums">—</td>
                  </tr>
                );
              }
              const rowBg = a.intraday ? intraBg : (a.intradayOverlap ? overlapBg : (matched ? greenBg : redBg));
              return (
                <tr key={`pf-${idx}`} style={{ background: rowBg, color: 'var(--text-strong)' }}>
                  <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
                    {isSub ? '↳ ' : ''}{fmtDate(a.pfRow.pf_txn_date)}
                  </td>
                  <td className="px-3 py-2">
                    <span>{a.pfRow.pf_txn_type}</span>
                    {isSub && parentTag(`sub-entry of ${fmtQty(a.pfParentQty)}`)}
                    {a.intraday ? (
                      <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                        style={{ background: 'color-mix(in srgb, #f97316 25%, transparent)', color: '#c2410c' }}>
                        · intraday {fmtQty(a.qty)}
                      </span>
                    ) : matched ? (
                      <>
                      <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                        style={{ background: 'color-mix(in srgb, var(--status-ok) 18%, transparent)', color: 'var(--status-ok)' }}>
                        matched {fmtQty(a.qty)}
                      </span>
                      {a.intradayOverlap && (
                        <span className="ml-1.5 text-[10px] font-bold px-1 py-0.5 rounded"
                          style={{ background: 'color-mix(in srgb, #a855f7 25%, transparent)', color: '#7c3aed' }}>
                          + partial intraday {fmtQty(a.qty)}
                        </span>
                      )}
                      </>
                    ) : (
                      <span className="ml-1.5 text-[10px] font-medium px-1 py-0.5 rounded"
                        style={{ background: 'color-mix(in srgb, var(--status-err) 18%, transparent)', color: 'var(--status-err)' }}>
                        · unmatched {fmtQty(a.qty)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtQty(a.qty)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>
                    {a.pfRow.pf_trade_price != null ? `₹${Number(a.pfRow.pf_trade_price).toFixed(2)}` : '—'}
                  </td>
                </tr>
              );
            })}
            <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(false)}>
              <td className="px-3 py-1.5 text-[10px]" colSpan={4} style={{ color: 'var(--text-400)' }}>▲ collapse</td>
            </tr>
            </>
            )}
          </tbody>
        </table>
      </div>

      {/* DP side */}
      <div className="flex-1 min-w-0" style={{ background: anyMatched ? greenBg : 'transparent' }}>
        <table className="w-full text-xs table-fixed">
          <DpColGroup />
          <tbody>
            {!expanded ? (
              <tr style={{ background: anyMatched ? greenBg : 'transparent', color: 'var(--text-strong)' }}>
                <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(firstDp?.dp_txn_date)}</td>
                <td className="px-3 py-2">
                  <span className="block truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={firstDp?.dp_description ?? ''}>
                    <DpDescriptionCell desc={firstDp?.dp_description} />
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--text-400)' }}>{dpParents.length} entries · matched {fmtNum(matchedQty)}{dpShortQty > 0 ? ` · unmatched ${fmtNum(dpShortQty)}` : ''}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{firstDp && Number(firstDp.dp_credit ?? 0) > 0 ? fmtNum(dpTotal) : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{firstDp && Number(firstDp.dp_debit ?? 0) > 0 ? fmtNum(dpTotal) : '—'}</td>
              </tr>
            ) : (
            <>
            {allocs.map((a, idx) => {
              if (a.dpOnly && a.dpRow) {
                // DP shortfall: a DP entry (portion) with no PF counterpart → red.
                const isDebit = Number(a.dpRow.dp_debit ?? 0) > 0;
                return (
                  <tr key={`dp-${idx}`} style={{ background: redBg, color: 'var(--text-strong)' }}>
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{a.dpSplit ? '↳ ' : ''}{fmtDate(a.dpRow.dp_txn_date)}</td>
                    <td className="px-3 py-2">
                      <span className="block truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.dpRow.dp_description ?? ''}>
                        {a.dpRow.dp_description ?? '—'}
                      </span>
                      <span className="text-[10px] font-medium" style={{ color: 'var(--status-err)' }}>· unmatched {fmtQty(a.qty)}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{isDebit ? '—' : fmtQty(a.qty)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{isDebit ? fmtQty(a.qty) : '—'}</td>
                  </tr>
                );
              }
              if (!a.matched || !a.dpRow) {
                // No DP counterpart: intraday offset (orange) or genuine shortfall (grey).
                return (
                  <tr key={`dp-${idx}`} style={{ background: a.intraday ? intraBg : 'transparent', color: 'var(--text-400)' }}>
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">—</td>
                    <td className="px-3 py-2 text-[11px]" style={{ fontStyle: 'italic' }}>{a.intraday ? 'intraday offset (same-day buy)' : 'no credit left'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">—</td>
                    <td className="px-3 py-2 text-right tabular-nums">—</td>
                  </tr>
                );
              }
              const isSub = a.dpSplit;
              return (
                <tr key={`dp-${idx}`} style={{ background: greenBg, color: 'var(--text-strong)' }}>
                  <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
                    {isSub ? '↳ ' : ''}{fmtDate(a.dpRow.dp_txn_date)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="block truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.dpRow.dp_description ?? ''}>
                      {a.dpRow.dp_description ?? '—'}{isSub ? ` (sub-entry of ${fmtQty(a.dpParentQty)})` : ''}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtQty(a.qty)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">—</td>
                </tr>
              );
            })}
            <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(false)}>
              <td className="px-3 py-1.5 text-[10px]" colSpan={4} style={{ color: 'var(--text-400)' }}>▲ collapse</td>
            </tr>
            </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Unmatched DP row — same structure as MatchedBandRow so borders align exactly. */
function UnmatchedDpBandRow({ dp }: { dp: ReconTxnPair }) {
  const waiting = isWithinDpWaitingWindow(dp.dp_txn_date);
  return (
    <div className="flex" style={{ borderBottom: '2px solid var(--glass-border-h)' }}>
      {/* PF side — empty. When the DP entry is still within the PF-arrival
          window, the PF cell shows an amber "waiting for entries" note (the PF
          counterpart hasn't arrived yet). */}
      <div
        className="flex-1 min-w-0"
        style={{
          borderRight: '2px solid var(--glass-border-h)',
          background: waiting ? 'color-mix(in srgb, var(--status-warn) 10%, transparent)' : undefined,
        }}
      >
        <table className="w-full text-xs table-fixed">
          <tbody>
            <tr style={{ borderLeft: waiting ? '3px solid var(--status-warn)' : '3px solid transparent', color: 'var(--text-400)', minHeight: '36px' }}>
              <td className="px-3 py-2 text-[11px]" colSpan={4}>
                {waiting
                  ? <span className="font-medium" style={{ color: 'var(--status-warn)' }}>waiting for entries</span>
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* DP side — the DP entry is present. Red = genuinely unmatched;
          when the PF side is still awaited we mark it amber "· awaiting PF". */}
      <div
        className="flex-1 min-w-0"
        style={{ background: `color-mix(in srgb, ${waiting ? 'var(--status-warn)' : 'var(--status-err)'} 12%, transparent)` }}
      >
        <table className="w-full text-xs table-fixed">
          <DpColGroup />
          <tbody>
            <tr style={{ borderLeft: `3px solid ${waiting ? 'var(--status-warn)' : 'var(--status-err)'}`, color: 'var(--text-strong)' }}>
              <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(dp.dp_txn_date)}</td>
              <td className="px-3 py-2 truncate max-w-0 cursor-pointer select-text"
                title={dp.dp_description ?? ''}
                onClick={e => {
                  const el = e.currentTarget;
                  if (el.style.whiteSpace === 'normal') { el.style.whiteSpace = ''; el.style.overflow = ''; el.classList.add('truncate'); }
                  else { el.style.whiteSpace = 'normal'; el.style.overflow = 'visible'; el.classList.remove('truncate'); }
                }}>
                {dp.dp_description ?? '—'}
                <span className="ml-1.5 text-[10px] font-medium" style={{ color: waiting ? 'var(--status-warn)' : 'var(--status-err)' }}>
                  {waiting ? '· awaiting PF' : '· unmatched'}
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {dp.dp_credit != null && dp.dp_credit !== 0 ? fmtQty(dp.dp_credit) : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {dp.dp_debit != null && dp.dp_debit !== 0 ? fmtQty(dp.dp_debit) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** SLB group — all SLB DP entries clubbed together at the bottom (like the
 *  dividend group), in a distinct SLATE colour (not red) because SLB is
 *  informational only: never matched, excluded from the DP net and mismatch. */
function SlbGroupBandRow({ band }: { band: MatchBand }) {
  const [expanded, setExpanded] = useState(false);
  const slbRows = band.dpRows;
  const slate   = '#64748b';
  const slateBg = 'color-mix(in srgb, #64748b 14%, transparent)';
  const totalCr = slbRows.reduce((s, d) => s + Number(d.dp_credit ?? 0), 0);
  const totalDr = slbRows.reduce((s, d) => s + Number(d.dp_debit ?? 0), 0);
  const first   = slbRows[0];

  return (
    <div className="flex" style={{ borderBottom: '2px solid var(--glass-border-h)' }}>
      {/* PF side — empty */}
      <div className="flex-1 min-w-0" style={{ borderRight: '2px solid var(--glass-border-h)' }}>
        <table className="w-full text-xs table-fixed">
          <tbody>
            <tr style={{ borderLeft: `3px solid ${slate}`, color: 'var(--text-400)' }}>
              <td className="px-3 py-2 text-[11px]" colSpan={4}>—</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* DP side — slate, collapsible list of SLB entries */}
      <div className="flex-1 min-w-0" style={{ background: slateBg }}>
        <table className="w-full text-xs table-fixed">
          <DpColGroup />
          <tbody>
            {!expanded ? (
              <tr style={{ borderLeft: `3px solid ${slate}`, color: 'var(--text-strong)', cursor: 'pointer' }} onClick={() => setExpanded(true)} title="Click to expand SLB entries">
                <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(first?.dp_txn_date)}</td>
                <td className="px-3 py-2">
                  <span className="font-medium" style={{ color: slate }}>SLB</span>
                  <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'color-mix(in srgb, #64748b 22%, transparent)', color: slate }}>
                    {slbRows.length} {slbRows.length === 1 ? 'entry' : 'entries'} · not counted
                  </span>
                  <span className="ml-1.5 text-[10px]" style={{ color: 'var(--text-400)' }}>▼ expand</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{totalCr > 0 ? fmtQty(totalCr) : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{totalDr > 0 ? fmtQty(totalDr) : '—'}</td>
              </tr>
            ) : (
              <>
                {slbRows.map((dp, idx) => (
                  <tr key={dp.id} style={{ borderLeft: `3px solid ${slate}`, color: 'var(--text-strong)' }}>
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{fmtDate(dp.dp_txn_date)}</td>
                    <td className="px-3 py-2">
                      <span className="block truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={dp.dp_description ?? ''}>
                        {dp.dp_description ?? '—'}
                      </span>
                      {idx === 0 && <span className="text-[10px]" style={{ color: slate }}>SLB · not counted in net/mismatch</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{dp.dp_credit != null && dp.dp_credit !== 0 ? fmtQty(dp.dp_credit) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dp.dp_debit != null && dp.dp_debit !== 0 ? fmtQty(dp.dp_debit) : '—'}</td>
                  </tr>
                ))}
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(false)}>
                  <td className="px-3 py-1.5 text-[10px]" colSpan={4} style={{ color: 'var(--text-400)' }}>▲ collapse</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * Visual design: PF rows tinted info-blue, DP rows tinted ok-green, both
 * via color-mix on theme tokens so the contrast works in light AND dark
 * mode. The previous static `bg-blue-50/40` + `text-axis-ash` combo was
 * unreadable in dark mode (light tint over dark surface + low-contrast
 * mid-gray text). A 4px left border of the side's accent color gives a
 * clear visual lane without needing strong fill.
 *
 * All readable text uses `var(--text-strong)` (main body color); only
 * truly secondary subtext (e.g. "tot ₹..." after the price) uses the
 * dimmer `var(--text-200)`.
 */
function AuditTxnRow({ pair }: { pair: ReconTxnPair }) {
  const isPf = pair.pf_txn_date != null;
  const isUnmatched = pair.status === 'UNMATCHED_PORTFOLIO' || pair.status === 'UNMATCHED_DP';
  const accent = isPf ? 'var(--status-info)' : 'var(--status-ok)';
  const bg = isUnmatched
    ? 'color-mix(in srgb, var(--status-warn) 25%, transparent)'
    : `color-mix(in srgb, ${accent} 8%, transparent)`;
  const borderLeft = isUnmatched ? '4px solid var(--status-warn)' : `3px solid ${accent}`;
  if (isPf) {
    return (
      <tr
        style={{
          background: bg,
          borderLeft,
          color: 'var(--text-strong)',
        }}
      >
        <td className="px-3 py-2"><span className="font-semibold" style={{ color: 'var(--status-info)' }}>PF</span>{isUnmatched && <span className="ml-1 text-[10px] font-medium" style={{ color: 'var(--status-warn)' }}>unpaired</span>}</td>
        <td className="px-3 py-2 font-mono text-[11px]">{fmtDate(pair.pf_txn_date)}</td>
        <td className="px-3 py-2">{pair.pf_txn_type}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtQty(pair.pf_qty)}</td>
        <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>—</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {pair.pf_trade_price != null ? `₹${Number(pair.pf_trade_price).toFixed(2)}` : '—'}
          {pair.pf_trade_value != null && (
            <span className="ml-2 text-[11px]" style={{ color: 'var(--text-200)' }}>
              tot ₹{Number(pair.pf_trade_value).toFixed(2)}
            </span>
          )}
        </td>
      </tr>
    );
  }
  // DP side
  return (
    <tr
      style={{
        background: bg,
        borderLeft,
        color: 'var(--text-strong)',
      }}
    >
      <td className="px-3 py-2"><span className="font-semibold" style={{ color: 'var(--status-ok)' }}>DP</span>{isUnmatched && <span className="ml-1 text-[10px] font-medium" style={{ color: 'var(--status-warn)' }}>unpaired</span>}</td>
      <td className="px-3 py-2 font-mono text-[11px]">{fmtDate(pair.dp_txn_date)}</td>
      <td className="px-3 py-2 truncate max-w-[280px]" title={pair.dp_description ?? ''}><DpDescriptionCell desc={pair.dp_description} /></td>
      <td className="px-3 py-2 text-right tabular-nums">{pair.dp_credit != null && pair.dp_credit !== 0 ? fmtQty(pair.dp_credit) : '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums">{pair.dp_debit != null && pair.dp_debit !== 0 ? fmtQty(pair.dp_debit) : '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>—</td>
    </tr>
  );
}

/* ─── Breakdown panel (compact summary of how the row's numbers were derived) ───── */

function BreakdownPanel({ row, visibleBreakdown, hasUnmatched }: {
  row: ReconRow;
  visibleBreakdown?: {
    portfolio: { buys_qty: number; buys_count: number; sells_qty: number; sells_count: number;
      dividends_count: number; dividends_amount: number; corporate_action_count: number; other_count: number };
    dp: { credits_qty: number; credits_count: number; debits_qty: number; debits_count: number };
  };
  hasUnmatched?: boolean;
}) {
  // Always use visibleBreakdown (computed from rendered bands).
  const b = visibleBreakdown ?? row.breakdown;
  const isMatch = row.status === 'MATCH';

  // Net values always from visible bands
  const visiblePfNet = visibleBreakdown
    ? visibleBreakdown.portfolio.buys_qty + visibleBreakdown.portfolio.sells_qty
    : Number(row.portfolio_qty);
  const visibleDpNet = visibleBreakdown
    ? visibleBreakdown.dp.credits_qty - visibleBreakdown.dp.debits_qty
    : Number(row.dp_qty);
  const visibleDiff = visiblePfNet - visibleDpNet;

  // No breakdown (older holdings_v1 row) — just show totals.
  if (!b) {
    return (
      <div className={`rounded-md border px-3 py-2 text-xs flex items-center justify-between flex-wrap gap-2 ${
        isMatch ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
      }`}>
        <div className="text-axis-ink">
          <span className="text-axis-ash">portfolio_net</span> <span className="tabular-nums font-medium">{fmtSigned(visiblePfNet)}</span>
          <span className="text-axis-ash mx-2">·</span>
          <span className="text-axis-ash">dp_net</span> <span className="tabular-nums font-medium">{fmtSigned(visibleDpNet)}</span>
          <span className="text-axis-ash mx-2">·</span>
          <span className="text-axis-ash">difference</span>{' '}
          <span className={`tabular-nums font-semibold ${isMatch ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtSigned(visibleDiff)}</span>
        </div>
        <StatusPill status={row.status} hasUnmatched={hasUnmatched} />
      </div>
    );
  }

  // Compact one-line composition strings per side.
  const pfParts: string[] = [];
  if (b.portfolio.buys_count > 0)              pfParts.push(`Buys ${fmtSigned(b.portfolio.buys_qty)}`);
  if (b.portfolio.sells_count > 0)             pfParts.push(`Sells ${fmtSigned(b.portfolio.sells_qty)}`);
  if (b.portfolio.corporate_action_count > 0)  pfParts.push(`CA ${b.portfolio.corporate_action_count}`);
  if (b.portfolio.dividends_count > 0)         pfParts.push(`Div ${b.portfolio.dividends_count}`);
  if (b.portfolio.other_count > 0)             pfParts.push(`Other ${b.portfolio.other_count}`);

  const dpParts: string[] = [];
  if (b.dp.credits_count > 0) dpParts.push(`Cr +${fmtQty(b.dp.credits_qty)}`);
  if (b.dp.debits_count > 0)  dpParts.push(`Dr −${fmtQty(b.dp.debits_qty)}`);

  return (
    <div className={`rounded-md border overflow-hidden ${isMatch ? 'border-emerald-200' : 'border-rose-200'}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-axis-cloud-200">
        <div className="px-3 py-2 text-xs">
          <span className="text-[10px] uppercase tracking-wider text-blue-700 font-medium mr-2">PF</span>
          {pfParts.length > 0
            ? <span className="text-axis-ink">{pfParts.join(' · ')}</span>
            : <span className="italic text-axis-ash">no portfolio txns</span>}
          <span className="text-axis-ash"> → net</span>{' '}
          <span className="tabular-nums font-semibold text-axis-ink">{fmtSigned(visiblePfNet)}</span>
        </div>
        <div className="px-3 py-2 text-xs">
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium mr-2">DP</span>
          {dpParts.length > 0
            ? <span className="text-axis-ink">{dpParts.join(' · ')}</span>
            : <span className="italic text-axis-ash">no DP txns</span>}
          <span className="text-axis-ash"> → net</span>{' '}
          <span className="tabular-nums font-semibold text-axis-ink">{fmtSigned(visibleDpNet)}</span>
        </div>
      </div>
      <div className={`px-3 py-2 text-xs flex items-center justify-between flex-wrap gap-2 border-t ${
        isMatch ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
      }`}>
        <div>
          <span className="text-axis-ash">difference = </span>
          <span className="tabular-nums">{fmtSigned(visiblePfNet)}</span>
          <span className="text-axis-ash"> − </span>
          <span className="tabular-nums">{fmtSigned(visibleDpNet)}</span>
          <span className="text-axis-ash"> = </span>
          <span className={`tabular-nums font-semibold ${isMatch ? 'text-emerald-700' : 'text-rose-700'}`}>
            {fmtSigned(visibleDiff)}
          </span>
        </div>
        <StatusPill status={row.status} hasUnmatched={hasUnmatched} />
      </div>
    </div>
  );
}

/** Format a Postgres date column for display. Strips the time component
 *  (`2019-07-08T00:00:00.000Z` → `2019-07-08`) so the audit trail shows
 *  just the date the way the source statements present it. Uses string
 *  slicing rather than the Date constructor to avoid timezone shifts. */
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const s = String(d);
  // Already YYYY-MM-DD form
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO with time → strip at T
  const ti = s.indexOf('T');
  if (ti > 0) return s.slice(0, ti);
  // Anything else (DD/MM/YYYY etc.) — display as-is
  return s;
}

/** Same as fmtQty but always shows a sign prefix for non-zero values. */
function fmtSigned(v: number | null | undefined): string {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) < 0.0005) return '0';
  const formatted = fmtQty(n);
  return n > 0 ? `+${formatted}` : formatted;
}

/* ─── Atoms ─────────────────────────────────────────────────────────── */

function Tile({ label, value, tone }: {
  label: string;
  value: string | number;
  tone: 'match' | 'warn' | 'mismatch' | 'neutral' | 'info';
}) {
  const toneCls = {
    match:    'border-emerald-400/30',
    warn:     'border-amber-400/30',
    mismatch: 'border-rose-400/30',
    info:     'border-blue-400/30',
    neutral:  'border-white/[0.08]',
  }[tone];
  const accent = {
    match:    'text-emerald-300',
    warn:     'text-amber-300',
    mismatch: 'text-rose-300',
    info:     'text-blue-300',
    neutral:  'text-ink-50',
  }[tone];
  // Shrink font for long values so they stay inside the tile
  const strLen = String(value).length;
  const sizeCls = strLen > 12 ? 'text-lg' : strLen > 9 ? 'text-xl' : strLen > 6 ? 'text-2xl' : 'text-2xl';
  return (
    <div className={`card h-full p-3 ${toneCls} min-w-0`}>
      <div className="text-[10px] text-ink-400 uppercase tracking-[0.18em] truncate">{label}</div>
      <div className={`${sizeCls} font-bold mt-1.5 tabular-nums tracking-tight break-words leading-tight ${accent}`} title={String(value)}>{value}</div>
    </div>
  );
}

function NetTile({ label, value, tone }: {
  label: string;
  value: number;
  tone: 'match' | 'mismatch' | 'neutral';
}) {
  const toneCls = {
    match:    'border-emerald-400/30',
    mismatch: 'border-rose-400/30',
    neutral:  'border-white/[0.08]',
  }[tone];
  const accent = {
    match:    'text-emerald-300',
    mismatch: 'text-rose-300',
    neutral:  'text-ink-50',
  }[tone];
  return (
    <div className={`card p-4 ${toneCls}`}>
      <div className="text-[10px] text-ink-400 uppercase tracking-[0.18em]">{label}</div>
      <div className={`text-3xl font-bold mt-1.5 tabular-nums tracking-tight ${accent}`}>
        {value > 0 ? '+' : ''}{fmtQty(value)}
      </div>
    </div>
  );
}

function Chip({ active, onClick, label, count }: {
  active: boolean; onClick: () => void; label: string; count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
        active
          ? 'bg-axis-maroon text-white'
          : 'bg-axis-cloud-100 text-axis-ash hover:bg-axis-cloud-200'
      }`}
    >
      {label}
      <span className={`tabular-nums ${active ? 'text-white' : 'text-axis-ink font-medium'}`}>
        {count}
      </span>
    </button>
  );
}

/** Compact qty formatter. Integer-looking values display without
 *  decimals; fractional MF units show 3-decimal precision. */
function fmtQty(v: number | null | undefined): string {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const isWhole = Math.abs(n - Math.round(n)) < 0.0005;
  return isWhole
    ? Math.round(n).toLocaleString()
    : n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
